// ── CONSTANTS ──
import { PRIMARY_ADMIN } from './config.js';
import { Auth, koppelApp } from './auth.js';
import { round2, billedOf, balanceOf, isWrittenOff, isSettled, deriveStatus, deriveStatusOf } from './lib/money.js';
import { esc, fmtDate, fmtDatetime, fmtMoney, relativeTime, getCountryFlag } from './lib/format.js';
import { toast } from './ui/toast.js';
import { showConfirm, closeConfirm } from './ui/confirm.js';
import { tabelTeken, tabelZoek, tabelSorteer, tabelWisAlles, tabelFilter, tabelFilterToepassen, tabelFilterWissen, tabelRijKlik } from './ui/tabel.js';
import { state } from './state.js';
import { logActivity } from './data/log.js';
import * as router from './router.js';
import { vertaalDom, koppelTaalkeuze } from './lib/i18n.js';
import { renderLeden } from './pages/leden.js';
import * as activiteiten from './pages/activiteiten.js';
import * as deelnemersMod from './pages/deelnemers.js';
import * as tweestap from './pages/tweestap.js';
import * as goedkeuringen from './pages/goedkeuringen.js';
import * as aansluitingen from './pages/aansluitingen.js';
import * as clubregister from './pages/clubregister.js';
import * as verzoeken from './pages/verzoeken.js';
import { dispatchFout } from './pages/goedkeuringen.js';
import { t as t_ } from './lib/i18n.js';
import * as context from './lib/context.js';
import * as eigendom from './pages/eigendom.js';
import * as exportMod from './pages/export.js';
import * as toernooienMod from './pages/toernooien.js';
import * as organisatieMod from './pages/organisatie.js';
import * as tweestapOverzicht from './pages/tweestap-overzicht.js';
import { vraagReden } from './ui/reden.js';
import { refLabel, koppelRef } from './ui/ref.js';

// ── APP MODULE ──
const App = (() => {
  let sb = null;
  let currentTeamId = null;
  let activityFilter = 'all';
  let kpiFilter = 'all';
  let stripeTeamRef = null;
  let pendingDiffs = [];
  let selectedIds = new Set();
  let pendingDeleteIds = [];
  let archivedTeams = [];
  let archiveTab = 'archived';

  const LS = {
    get: k => { try { return JSON.parse(localStorage.getItem(k)); } catch { return null; } },
    set: (k, v) => localStorage.setItem(k, JSON.stringify(v)),
    remove: k => localStorage.removeItem(k)
  };

  async function init(supabaseClient, userEmail, lidmaatschap) {
    sb = supabaseClient;
    state.currentUserEmail = userEmail || '';
    state.currentOrgId = lidmaatschap?.organisation_id || null;
    await bepaalPlatformrol();
    // Bekijk als (alleen platformbeheer): de weergave van een andere rol. De
    // database geeft nog steeds je eigen rechten; dit verbergt alleen.
    const bekijkAls = isPlatformAdmin ? context.simulatie()?.rol : null;
    state.currentRole = bekijkAls || lidmaatschap?.role || 'owner';
    // Verbergt wat deze rol toch niet mag. De database weigert het hoe dan ook;
    // dit voorkomt alleen knoppen die in een foutmelding eindigen.
    document.body.classList.remove('rol-owner','rol-admin','rol-editor','rol-viewer');
    document.body.classList.add('rol-' + state.currentRole);
    koppelGeschiedenis();
    koppelRef();
    // Platformbeheer is een eigen ingang. Dan niets van een organisatie laden of
    // tekenen: geen modulekiezer, geen toernooi, geen editie. Zo is er ook
    // niets dat kort in beeld komt voordat platformbeheer er staat.
    const adresBijStart = router.leesAdres();
    if (isPlatformAdmin && (context.gekozen()?.soort === 'platform' || adresBijStart?.soort === 'platform')) {
      await openPlatform(adresBijStart?.soort === 'platform' ? adresBijStart.naam : 'dash');
      return;
    }
    await router.laadPermissies(sb, state.currentOrgId, state.currentRole, { bekijkAls: !!bekijkAls });
    await router.laadEntitlements(sb, state.currentOrgId);
    kiesModule();
    tekenMenu();
    tekenModuleKiezer();
    document.addEventListener('taalgewijzigd', tekenModuleKiezer);
    toonViaPlatformtoegang(lidmaatschap);
    goedkeuringen.vernieuwTeller();
    verzoeken.vernieuwTeller();
    await laadOrganisatie();
    // Eén keer opvragen of Stripe server-side bereikbaar is; de instellingenpagina
    // ververst dit zelf wanneer je hem opent.
    refreshStripeStatus();
    renderSettingsMailchimp();
    // Het toernooideel alleen als de organisatie die module heeft.
    const toernooien = router.module() === 'toernooien' && router.heeftModule('toernooien') && !!state.currentOrgId;
    document.getElementById('orgOnly1').style.display = toernooien ? '' : 'none';
    if (toernooien) { toernooiGeladen = true; await loadEvents(); leidToernooiAf(); }
    herstelPagina();
    await verwerkStripeTerugkeer();
  }

  // Op welke pagina je stond staat in de adresbalk, achter een hekje. Zonder
  // dat belandde je na elke verversing op het dashboard, en dat is precies op
  // het moment dat je ergens middenin zit. Welke namen geldig zijn en wie
  // erheen mag, staat in router.js.
  function herstelPagina() {
    const adres = router.leesAdres();
    if (adres?.soort === 'org' && router.moduleVan(adres.naam) === router.module()) { nav(adres.naam, { vervang: true }); return; }
    nav(router.eerstePagina(), { vervang: true });
  }

  // Terugknop en vooruitknop binnen de app. Andere context (organisatie,
  // platformbeheer, startscherm) dan nu: opnieuw laden in die context.
  let geschiedenisGekoppeld = false;
  function koppelGeschiedenis() {
    if (geschiedenisGekoppeld) return;
    geschiedenisGekoppeld = true;
    window.addEventListener('popstate', e => {
      const st = e.state;
      if (st?.tornema && 'ctx' in st && JSON.stringify(st.ctx ?? null) !== JSON.stringify(context.gekozen() ?? null)) {
        context.volgGeschiedenis();
        location.reload();
        return;
      }
      const adres = router.leesAdres();
      if (!adres) return;
      const inPlatform = document.body.classList.contains('platform-modus');
      if (adres.soort === 'platform') {
        if (inPlatform) navPlatform(adres.naam, { vervang: true });
        else location.reload();
        return;
      }
      if (inPlatform) { location.reload(); return; }
      nav(adres.naam, { vervang: true });
    });
  }

  // In welke module je werkt volgt uit de pagina in de adresbalk; zonder
  // pagina de eerste module die deze organisatie en rol heeft.
  function kiesModule() {
    const adres = router.leesAdres();
    const gevraagd = adres?.soort === 'org' ? router.moduleVan(adres.naam) : null;
    const beschikbaar = router.beschikbareModules();
    router.zetModule(beschikbaar.includes(gevraagd) ? gevraagd : beschikbaar[0] || null);
  }

  // Wisselen tussen modules gaat via de kiezer bovenin de zijbalk, of via het
  // startscherm. Elke module heeft zijn eigen menu.
  function tekenModuleKiezer() {
    const wrap = document.getElementById('moduleKiezerWrap');
    const sel = document.getElementById('moduleKiezer');
    const sub = document.getElementById('brandSub');
    const huidig = router.module();
    if (sub && huidig) sub.textContent = t_('start.module.' + huidig);
    if (!wrap || !sel) return;
    const lijst = router.beschikbareModules();
    wrap.hidden = lijst.length < 2;
    sel.innerHTML = lijst.map(m => `<option value="${m}" ${m === huidig ? 'selected' : ''}>${esc(t_('start.module.' + m))}</option>`).join('')
      + `<option value="__start">${esc(t_('start.naarstart'))}</option>`;
    sel.onchange = () => {
      if (sel.value === '__start') { context.naarStart(); return; }
      wisselModule(sel.value);
    };
  }

  // Naar een andere module binnen dezelfde organisatie, zonder te herladen:
  // ander menu, andere kiezer, en het toernooi pas laden als het nodig is.
  let toernooiGeladen = false;
  async function wisselModule(m, page, opties = {}) {
    router.zetModule(m);
    tekenMenu();
    tekenModuleKiezer();
    const toernooien = m === 'toernooien' && router.heeftModule('toernooien') && !!state.currentOrgId;
    document.getElementById('orgOnly1').style.display = toernooien ? '' : 'none';
    // Meldingen over betaaltermijnen horen bij het toernooi.
    if (!toernooien) document.querySelectorAll('#bannerContainer [data-bron="toernooi"]').forEach(b => b.remove());
    if (toernooien && !toernooiGeladen) { toernooiGeladen = true; await loadEvents(); leidToernooiAf(page); }
    else if (toernooien) checkDeadlines();
    nav(page && router.moduleVan(page) === m ? page : router.eerstePagina(m), opties);
  }

  // Binnen via platformtoegang, niet als lid: dat moet zichtbaar zijn.
  function toonViaPlatformtoegang(lidmaatschap) {
    if (lidmaatschap?.via !== 'platform') return;
    const b = document.getElementById('bannerContainer');
    if (b) b.insertAdjacentHTML('afterbegin', `<div class="banner banner-amber"><i class="ti ti-key"></i><span>${esc(t_('start.viaplatform'))}</span></div>`);
  }

  // De naam van de organisatie komt uit de database, niet uit de code. Zonder
  // dit staat er bij elke klant de naam van de eerste klant in de zijbalk en in elke e-mail.
  async function laadOrganisatie() {
    if (!state.currentOrgId) return;
    const { data } = await sb.from('organisations').select('*').eq('id', state.currentOrgId).maybeSingle();
    state.currentOrg = data || null;
    const naam = document.querySelector('.brand-name');
    if (naam && state.currentOrg?.name) naam.textContent = state.currentOrg.name;
    if (state.currentOrg?.name) {
      document.title = `${state.currentOrg.name} · ${window.__merknaam || 'CRM'}`;
    }
  }

  // ── TOERNOOI KIEZEN ──
  // Op het overzicht van alle toernooien is er nog geen toernooi gekozen: dan
  // alleen dat overzicht in het menu, geen editiekeuze en geen melding over
  // betaaltermijnen. Na het kiezen van een toernooi verschijnt het menu van dat
  // toernooi, met alleen zijn edities in de keuzelijst.
  let huidigePagina = null;
  const opOverzicht = () => router.module() === 'toernooien' && huidigePagina === 'toernooien' && !state.currentTournamentId;

  function tekenMenu() {
    router.bouwMenu();
    const overzicht = opOverzicht();
    if (router.module() === 'toernooien') {
      document.querySelectorAll('#navOrg [data-module="toernooien"]').forEach(el => {
        if (el.dataset.page !== 'toernooien' && overzicht) el.hidden = true;
      });
      const sel = document.getElementById('orgOnly1');
      if (sel && router.heeftModule('toernooien') && state.currentOrgId) sel.style.display = overzicht ? 'none' : '';
    }
    if (overzicht) document.querySelectorAll('#bannerContainer [data-bron="toernooi"]').forEach(b => b.remove());
  }

  // Bij binnenkomen op de pagina van een editie: het toernooi van die editie.
  function leidToernooiAf(pagina) {
    const naam = pagina || router.leesAdres()?.naam;
    const ev = state.events.find(e => e.id === state.currentEventId);
    state.currentTournamentId = naam && naam !== 'toernooien' ? (ev?.tournament_id || null) : null;
    renderEventSelect();
  }

  async function kiesToernooi(id) {
    state.currentTournamentId = id || null;
    renderEventSelect();
    if (id) {
      const eigen = state.events.filter(e => e.tournament_id === id)
        .sort((a, b) => String(b.tournament_date || '').localeCompare(String(a.tournament_date || '')));
      if (eigen.length && !eigen.some(e => e.id === state.currentEventId)) {
        document.getElementById('eventSelect').value = eigen[0].id;
        await switchEvent(eigen[0].id);
      }
    }
    tekenMenu();
    if (!opOverzicht()) checkDeadlines();
  }

  // Menu-item Alle toernooien: terug naar het overzicht, zonder gekozen toernooi.
  async function alleToernooien() {
    toernooienMod.zetOpen(null);
    state.currentTournamentId = null;
    nav('toernooien');
  }

  // ── EVENTS ──
  async function loadEvents() {
    // Alleen de toernooien van deze organisatie. Wie bij meer organisaties
    // hoort of platformtoegang heeft, zag anders ook die van anderen.
    const [{ data }] = await Promise.all([
      sb.from('events').select('*').eq('organisation_id', state.currentOrgId).order('created_at', { ascending: false }),
      toernooienMod.laad(),
    ]);
    state.events = data || [];
    renderEventSelect();
    if (state.events.length > 0) {
      const saved = LS.get('current_event');
      const found = state.events.find(e => e.id === saved);
      state.currentEventId = found ? found.id : state.events[0].id;
      document.getElementById('eventSelect').value = state.currentEventId;
      await loadTeams();
    } else {
      document.getElementById('noEventMsg').style.display = 'flex';
      document.getElementById('teamsContent').style.display = 'none';
    }
  }

  function renderEventSelect() {
    const sel = document.getElementById('eventSelect');
    // Edities per toernooi. Met één toernooi een platte lijst, anders gegroepeerd.
    const toernooien = state.tournaments || [];
    const optie = e => `<option value="${esc(e.id)}">${esc(e.name)}</option>`;
    if (state.currentTournamentId) {
      sel.innerHTML = state.events.filter(e => e.tournament_id === state.currentTournamentId).map(optie).join('');
    } else if (toernooien.length > 1) {
      const zonder = state.events.filter(e => !toernooien.some(x => x.id === e.tournament_id));
      sel.innerHTML = toernooien.map(x => {
        const eigen = state.events.filter(e => e.tournament_id === x.id);
        return eigen.length ? `<optgroup label="${esc(x.naam)}">${eigen.map(optie).join('')}</optgroup>` : '';
      }).join('') + zonder.map(optie).join('');
    } else {
      sel.innerHTML = state.events.map(optie).join('');
    }
    if (state.currentEventId) sel.value = state.currentEventId;
  }

  async function switchEvent(id) {
    state.currentEventId = id;
    LS.set('current_event', id);
    state.allClubs = [];
    await loadTeams();
  }

  function openEventModal(toernooiId) {
    document.getElementById('eventModalTitle').textContent = 'New event';
    document.getElementById('eventName').value = '';
    // Geen voorinvulling meer: die was van één specifiek toernooi.
    document.getElementById('eventDate').value = '';
    document.getElementById('eventDeadline').value = '';
    document.getElementById('eventLocation').value = '';
    // Onder welk toernooi deze editie valt; standaard dat van de huidige editie.
    const tsel = document.getElementById('eventTournament');
    const toernooien = state.tournaments || [];
    if (tsel) {
      const huidig = (typeof toernooiId === 'string' && toernooiId) || state.events.find(e => e.id === state.currentEventId)?.tournament_id;
      tsel.innerHTML = toernooien.map(x => `<option value="${esc(x.id)}" ${x.id === huidig ? 'selected' : ''}>${esc(x.naam)}</option>`).join('');
      document.getElementById('eventTournamentGroup').hidden = toernooien.length < 2;
    }
    document.getElementById('eventModal').classList.add('open');
  }
  function closeEventModal() { document.getElementById('eventModal').classList.remove('open'); }

  async function saveEvent() {
    const name = document.getElementById('eventName').value.trim();
    if (!name) { toast('Event name is required', 'error'); return; }
    const { data, error } = await sb.from('events').insert([{
      name,
      tournament_date: document.getElementById('eventDate').value,
      payment_deadline: document.getElementById('eventDeadline').value,
      location: document.getElementById('eventLocation').value.trim(),
      ...(document.getElementById('eventTournament')?.value ? { tournament_id: document.getElementById('eventTournament').value } : {}),
    }]).select();
    if (error) { toast('Error: ' + error.message, 'error'); return; }
    closeEventModal();
    state.events.unshift(data[0]);
    state.currentEventId = data[0].id;
    LS.set('current_event', state.currentEventId);
    renderEventSelect();
    document.getElementById('eventSelect').value = state.currentEventId;
    await loadTeams();
    if (document.getElementById('pageToernooien')?.classList.contains('active')) await toernooienMod.vernieuw();
    toast('Event created', 'success');
  }

  // ── TEAMS ──
  async function loadTeams() {
    if (!state.currentEventId) return;
    document.getElementById('noEventMsg').style.display = 'none';
    document.getElementById('teamsContent').style.display = 'block';
    const { data } = await sb.from('teams').select('*')
      .eq('event_id', state.currentEventId)
      .is('archived_at', null)
      .order('created_at', { ascending: false });
    state.teams = data || [];
    selectedIds.clear();
    updateSelectionToolbar();
    renderTable(); renderKPIs(); renderCountryFilter(); checkDeadlines(); updateBadges(); renderCheckAllBtn();
    if (document.getElementById('pageDashboard')?.classList.contains('active')) renderDashboard();
    applyColVisibility();
    // Load archived count for badge
    const { count } = await sb.from('teams').select('id', { count: 'exact', head: true })
      .eq('event_id', state.currentEventId).not('archived_at', 'is', null);
    const el = document.getElementById('badgeArchived');
    if (count > 0) { el.textContent = count; el.style.display = 'inline'; }
    else el.style.display = 'none';
  }

  // ── COLUMN FILTERS state ──
  // colFilters: { fieldKey: { type: 'empty'|'contains'|'preset', value: string } }
  let colFilters = {};

  const COL_FIELD_MAP = {
    team_name: t => t.team_name||'',
    country: t => (t.country||'')+' '+(t.country||'').toUpperCase(), // match on full name and code
    balance_eur: t => String(t.balance_eur??''),
    entry_fee_eur: t => String(t.entry_fee_eur??''),
    amount_paid_eur: t => String(t.amount_paid_eur??''),
    stripe_payment_link: t => t.stripe_payment_link||'',
    contact_email: t => t.contact_email||t.user_email||t.club_email||'',
    contact_name: t => t.contact_name||[t.user_firstname,t.user_lastname].filter(Boolean).join(' ')||'',
    contact_phone: t => t.contact_phone||t.user_mobile||t.user_phone||t.club_phone||''
  };

  // A written off team is shown as "Written off" rather than as its derived
  // status, so the payment-status filters must not return it. Otherwise a
  // no-show turns up under "Paid" and the two counts contradict the table.
  function matchStatusFilter(t, f) {
    if (f === 'written_off')  return isWrittenOff(t);
    if (f === 'fee_adjusted') return t.settlement === 'fee_adjusted';
    if (isWrittenOff(t)) return false;
    return t.payment_status === f;
  }

  const EMPTY_VALUES = new Set(['','—','-','0','null','undefined','n/a']);

  function isEmptyVal(v) {
    return EMPTY_VALUES.has(String(v??'').trim().toLowerCase());
  }

  function colFilterMatches(t, field, filter) {
    const val = (COL_FIELD_MAP[field]?.(t) ?? '').trim();
    if (filter.type === 'empty') return isEmptyVal(val);
    if (filter.type === 'notempty') return !isEmptyVal(val);
    if (filter.type === 'contains') return val.toLowerCase().includes(filter.value.toLowerCase());
    if (filter.type === 'equals') return val.toLowerCase() === filter.value.toLowerCase();
    return true;
  }

  function renderTable() {
    const tbody = document.getElementById('teamsTableBody');
    const empty = document.getElementById('teamsEmptyState');
    const search = document.getElementById('searchInput').value.toLowerCase();
    const statusF = document.getElementById('filterStatus').value;
    const stageF = document.getElementById('filterStage').value;
    const countryF = document.getElementById('filterCountry').value;
    const event = state.events.find(e => e.id === state.currentEventId);
    const deadline = event?.payment_deadline ? new Date(event.payment_deadline) : null;
    const today = new Date();
    const hasColFilters = Object.keys(colFilters).length > 0;

    // Update "Clear column filters" button visibility
    const clearBtn = document.getElementById('btnClearColFilters');
    if (clearBtn) clearBtn.style.display = hasColFilters ? 'flex' : 'none';

    // Update col filter button active states and tags
    Object.keys(COL_FIELD_MAP).forEach(field => {
      const btn = document.querySelector(`.col-filter-btn[data-col="${field}"]`);
      const tag = document.getElementById(`cft_${field}`);
      const f = colFilters[field];
      if (btn) btn.classList.toggle('active', !!f);
      if (tag) {
        if (f) {
          const label = f.type === 'empty' ? 'is empty'
            : f.type === 'notempty' ? 'is filled'
            : f.type === 'contains' ? `contains "${f.value}"`
            : `= "${f.value}"`;
          tag.textContent = label;
        } else {
          tag.textContent = '';
        }
      }
    });

    const filtered = state.teams.filter(t => {
      if (t.archived_at) return false;
      if (search && !t.team_name.toLowerCase().includes(search) && !(t.contact_email||'').toLowerCase().includes(search)) return false;
      // The status dropdown and the KPI cards mix two fields: the derived
      // payment status and the manual settlement marking. matchStatusFilter
      // resolves which one a given value belongs to.
      if (statusF && !matchStatusFilter(t, statusF)) return false;
      if (stageF && t.reminder_stage !== stageF) return false;
      if (countryF && t.country !== countryF) return false;
      if (kpiFilter !== 'all' && !matchStatusFilter(t, kpiFilter)) return false;
      for (const [field, filter] of Object.entries(colFilters)) {
        if (!colFilterMatches(t, field, filter)) return false;
      }
      return true;
    });

    // Result banner
    const resultBanner = document.getElementById('filterResultBanner');
    if (resultBanner) {
      if (hasColFilters && filtered.length >= 0) {
        const parts = Object.entries(colFilters).map(([f,v]) => {
          const label = f.replace(/_/g,' ');
          return v.type === 'empty' ? `${label} is empty` : v.type === 'notempty' ? `${label} is filled` : v.type === 'contains' ? `${label} contains "${v.value}"` : `${label} = "${v.value}"`;
        });
        resultBanner.style.display = 'flex';
        resultBanner.innerHTML = `<i class="ti ti-filter" style="font-size:14px;flex-shrink:0"></i> <span><strong>${filtered.length} team${filtered.length!==1?'s':''}</strong> match: ${parts.join(' · ')} — select all with the header checkbox, then archive or delete.</span>`;
      } else {
        resultBanner.style.display = 'none';
      }
    }

    if (!filtered.length) {
      tbody.innerHTML = '';
      empty.style.display = 'block';
      const selectAll2 = document.getElementById('selectAllCb');
      if (selectAll2) { selectAll2.checked = false; selectAll2.indeterminate = false; }
      return;
    }
    empty.style.display = 'none';
    tbody.innerHTML = filtered.map(t => {
      const overdue = deadline && today > deadline && !isSettled(t);
      const sel = selectedIds.has(t.id);
      // Highlight cells that have an active column filter on them
      const hl = (field) => {
        const f = colFilters[field];
        if (!f) return '';
        const val = COL_FIELD_MAP[field]?.(t) ?? '';
        const matches = colFilterMatches(t, field, f);
        return matches && f.type === 'empty' ? 'style="background:#fff7e8"' : '';
      };
      const emailVal = t.contact_email||t.user_email||t.club_email||'';
      const nameVal = t.contact_name||[t.user_firstname,t.user_lastname].filter(Boolean).join(' ')||'';
      const phoneVal = t.contact_phone||t.user_mobile||t.user_phone||t.club_phone||'';
      const countryFlag = getCountryFlag(t.country||'');
      const colVis = (col) => hiddenCols.has(col) ? 'style="display:none"' : '';
      return `<tr class="${overdue?'deadline-passed':''} ${sel?'selected':''}" data-id="${t.id}">
        <td class="cb-cell" onclick="event.stopPropagation()">
          <input type="checkbox" ${sel?'checked':''} onchange="App.toggleRow('${t.id}',this.checked)">
        </td>
        <td onclick="App.openPanel('${t.id}')" ${colVis('team_name')}><div class="td-name">${esc(t.team_name)}</div><div class="td-abbr">${esc(t.team_abbreviation||'')} · ${countryFlag.code}</div></td>
        <td onclick="App.openPanel('${t.id}')" ${hl('country')} ${colVis('country')}>
          ${t.country
            ? `<div class="country-cell"><span class="flag-emoji" title="${esc(t.country)}">${countryFlag.flag}</span><span style="font-size:11px;font-family:var(--mono);color:var(--text-2)">${esc(countryFlag.iso||t.country)}</span></div>`
            : '<span style="color:var(--text-3)">—</span>'}
        </td>
        <td data-col="athlete_count" onclick="App.openPanel('${t.id}')" ${colVis('athlete_count')} style="text-align:center;font-family:var(--mono)">${t.athlete_count||0}</td>
        <td data-col="entry_fee_eur" onclick="App.openPanel('${t.id}')" ${hl('entry_fee_eur')} ${colVis('entry_fee_eur')} style="text-align:right;font-family:var(--mono);font-size:12px;color:var(--text-2)">€${fmtMoney(t.entry_fee_eur)}</td>
        <td data-col="amount_paid_eur" onclick="App.openPanel('${t.id}')" ${hl('amount_paid_eur')} ${colVis('amount_paid_eur')} style="text-align:right;font-family:var(--mono);font-size:12px;color:${Number(t.amount_paid_eur||0)>0?'var(--green)':'var(--text-3)'}">€${fmtMoney(t.amount_paid_eur)}</td>
        <td data-col="balance_eur" onclick="App.openPanel('${t.id}')" class="td-balance ${t.balance_eur>0?'amber':'green'}" ${hl('balance_eur')} ${colVis('balance_eur')}>€${fmtMoney(t.balance_eur)}</td>
        <td onclick="App.openPanel('${t.id}')" ${colVis('payment_status')}>${combinedStatusBadge(t)}</td>
        <td onclick="App.openPanel('${t.id}')" ${colVis('reminder_stage')}>${stageBadge(t.reminder_stage)}</td>
        <td style="text-align:center" onclick="event.stopPropagation()" ${hl('stripe_payment_link')} ${colVis('stripe_payment_link')}>
          ${t.stripe_payment_link
            ? (stripeLinkIsStale(t)
                ? `<a href="${t.stripe_payment_link}" target="_blank" title="Link is for €${fmtMoney(t.stripe_link_amount)} but €${fmtMoney(t.balance_eur)} is outstanding"><i class="ti ti-link-off stripe-link-icon" style="color:var(--amber)"></i></a>`
                : `<a href="${t.stripe_payment_link}" target="_blank"><i class="ti ti-link stripe-link-icon has-link"></i></a>`)
            : `<i class="ti ti-unlink stripe-link-icon"></i>`}
        </td>
        <td onclick="App.openPanel('${t.id}')" ${colVis('updated_at')} style="font-size:12px;color:var(--text-3)">${relativeTime(t.updated_at||t.created_at)}</td>
        <td onclick="App.openPanel('${t.id}')" ${hl('contact_email')} ${colVis('contact_email')} style="font-size:12px">${emailVal?esc(emailVal):'<span style="color:var(--text-3)">—</span>'}</td>
        <td onclick="App.openPanel('${t.id}')" ${hl('contact_name')} ${colVis('contact_name')} style="font-size:12px">${nameVal?esc(nameVal):'<span style="color:var(--text-3)">—</span>'}</td>
        <td onclick="App.openPanel('${t.id}')" ${hl('contact_phone')} ${colVis('contact_phone')} style="font-size:12px;font-family:var(--mono)">${phoneVal?esc(phoneVal):'<span style="color:var(--text-3)">—</span>'}</td>
        <td onclick="event.stopPropagation()" ${colVis('actions')} style="position:relative">
          <button class="actions-btn" onclick="App.showActions('${t.id}',this)"><i class="ti ti-dots"></i></button>
        </td>
      </tr>`;
    }).join('');
  }

  // ── STANDAARDTABEL ── (in ui/tabel.js)

  // ── COLUMN FILTER POPOVER ──
  function openColFilter(field, btn) {
    // Close any open popover first
    document.querySelectorAll('.col-filter-pop').forEach(p=>p.remove());

    const current = colFilters[field] || {};
    const rect = btn.getBoundingClientRect();
    const labels = {
      team_name:'Team name', country:'Country', balance_eur:'Balance',
      stripe_payment_link:'Stripe link', contact_email:'Email',
      contact_name:'Name', contact_phone:'Phone'
    };

    const pop = document.createElement('div');
    pop.className = 'col-filter-pop';
    pop.id = 'colFilterPop';
    // Position below the button, left-aligned
    const left = Math.min(rect.left, window.innerWidth - 256);
    pop.style.cssText = `top:${rect.bottom+6}px;left:${left}px`;
    pop.innerHTML = `
      <div class="col-filter-pop-title">Filter: ${labels[field]||field}</div>
      <div class="col-filter-presets">
        <button class="col-filter-preset ${current.type==='empty'?'active':''}" onclick="App.applyColFilter('${field}','empty','')">Is empty / —</button>
        <button class="col-filter-preset ${current.type==='notempty'?'active':''}" onclick="App.applyColFilter('${field}','notempty','')">Is filled</button>
      </div>
      <input id="colFilterInput" type="text" placeholder="Contains…" value="${current.type==='contains'?esc(current.value||''):''}"
        oninput="App.previewColFilter('${field}',this.value)"
        onkeydown="if(event.key==='Enter')App.applyColFilter('${field}','contains',this.value)">
      <div class="col-filter-pop-footer">
        <button class="btn btn-ghost btn-sm" onclick="App.clearColFilter('${field}')"><i class="ti ti-x"></i> Clear</button>
        <button class="btn btn-primary btn-sm" onclick="App.applyColFilter('${field}','contains',document.getElementById('colFilterInput').value)"><i class="ti ti-check"></i> Apply</button>
      </div>`;
    document.getElementById('dropdownContainer').appendChild(pop);
    // Focus the input
    setTimeout(() => { const inp = document.getElementById('colFilterInput'); if(inp) inp.focus(); }, 30);
    // Close on outside click
    setTimeout(() => {
      document.addEventListener('click', function closePop(e) {
        if (!pop.contains(e.target) && !btn.contains(e.target)) {
          pop.remove();
          document.removeEventListener('click', closePop);
        }
      });
    }, 10);
  }

  function previewColFilter(field, value) {
    // Live preview: apply contains filter while typing
    if (value.trim()) {
      colFilters[field] = { type: 'contains', value };
    } else {
      delete colFilters[field];
    }
    renderTable();
  }

  function applyColFilter(field, type, value) {
    document.querySelectorAll('.col-filter-pop').forEach(p=>p.remove());
    if (type === 'empty' || type === 'notempty') {
      colFilters[field] = { type };
    } else if (value && value.trim()) {
      colFilters[field] = { type, value: value.trim() };
    } else {
      delete colFilters[field];
    }
    renderTable();
  }

  function clearColFilter(field) {
    document.querySelectorAll('.col-filter-pop').forEach(p=>p.remove());
    delete colFilters[field];
    renderTable();
  }

  function clearColumnFilters() {
    colFilters = {};
    renderTable();
  }

  // ── COLUMN VISIBILITY ──
  const ALL_COLS = [
    {key:'team_name',label:'Team',required:true},
    {key:'country',label:'Country'},
    {key:'athlete_count',label:'Entries'},
    {key:'entry_fee_eur',label:'Entry fee'},
    {key:'amount_paid_eur',label:'Paid'},
    {key:'balance_eur',label:'Balance'},
    {key:'payment_status',label:'Status'},
    {key:'reminder_stage',label:'Stage'},
    {key:'stripe_payment_link',label:'Stripe link'},
    {key:'updated_at',label:'Last activity'},
    {key:'contact_email',label:'Email'},
    {key:'contact_name',label:'Name'},
    {key:'contact_phone',label:'Phone'},
    {key:'actions',label:'Actions'}
  ];

  // Fifteen columns do not fit on a laptop screen. Contact details live in the
  // detail panel anyway, so they start hidden. A stored preference always wins,
  // so this only affects people who never touched the Columns menu.
  const DEFAULT_HIDDEN_COLS = ['contact_email','contact_name','contact_phone','updated_at'];

  // Eenmalige overzetting van de sleutels van voor de naam Tornema. Zonder dit
  // verliest iedereen bij de eerste keer laden zijn kolomkeuze. Kan weg zodra
  // iedere gebruiker na deze release een keer heeft ingelogd.
  for (const [oud, nieuw] of [
    ['dragonopen_hidden_cols', 'tornema_hidden_cols'],
    ['dragonopen_club_hidden_cols', 'tornema_club_hidden_cols'],
  ]) {
    try {
      const waarde = localStorage.getItem(oud);
      if (waarde !== null && localStorage.getItem(nieuw) === null) localStorage.setItem(nieuw, waarde);
      localStorage.removeItem(oud);
    } catch { /* privémodus of geblokkeerde opslag: dan is er ook niets over te zetten */ }
  }
  let hiddenCols = new Set(
    localStorage.getItem('tornema_hidden_cols') !== null
      ? JSON.parse(localStorage.getItem('tornema_hidden_cols'))
      : DEFAULT_HIDDEN_COLS
  );

  function saveColVisibility() {
    localStorage.setItem('tornema_hidden_cols', JSON.stringify([...hiddenCols]));
  }

  function applyColVisibility() {
    ALL_COLS.forEach(c => {
      const hidden = hiddenCols.has(c.key);
      document.querySelectorAll(`thead th[data-col="${c.key}"]`).forEach(th => {
        th.style.display = hidden ? 'none' : '';
      });
    });
  }

  function openColToggle(btn) {
    document.querySelectorAll('.col-toggle-pop').forEach(p=>p.remove());
    const rect = btn.getBoundingClientRect();
    const pop = document.createElement('div');
    pop.className = 'col-toggle-pop';
    pop.style.cssText = `top:${rect.bottom+6}px;right:${window.innerWidth-rect.right}px`;
    pop.innerHTML = `
      <div class="col-toggle-pop-title">Show / hide columns</div>
      ${ALL_COLS.map(c=>`
        <label class="col-toggle-item">
          <input type="checkbox" ${!hiddenCols.has(c.key)?'checked':''} ${c.required?'disabled':''} onchange="App.toggleColVisibility('${c.key}',this.checked)">
          ${c.label}${c.required?' <span style="font-size:10px;color:var(--text-3)">(always visible)</span>':''}
        </label>`).join('')}
      <div class="col-toggle-pop-footer">
        <button class="btn btn-ghost btn-sm" onclick="App.resetColVisibility()"><i class="ti ti-refresh"></i> Reset</button>
      </div>`;
    document.getElementById('dropdownContainer').appendChild(pop);
    setTimeout(() => {
      document.addEventListener('click', function closePop(e) {
        if (!pop.contains(e.target) && !btn.contains(e.target)) {
          pop.remove(); document.removeEventListener('click', closePop);
        }
      });
    }, 10);
  }

  function toggleColVisibility(col, visible) {
    if (visible) hiddenCols.delete(col); else hiddenCols.add(col);
    saveColVisibility();
    applyColVisibility();
    renderTable();
  }

  function resetColVisibility() {
    hiddenCols.clear();
    saveColVisibility();
    applyColVisibility();
    renderTable();
    document.querySelectorAll('.col-toggle-pop').forEach(p=>p.remove());
  }

  // ── COUNTRY FLAGS ── (tabellen en opzoeken in lib/format.js)

  function renderCountryFilter() {
    const countries = [...new Set(state.teams.map(t=>t.country).filter(Boolean))].sort();
    const sel = document.getElementById('filterCountry');
    const cur = sel.value;
    sel.innerHTML = '<option value="">All countries</option>' + countries.map(c=>{
      const {flag} = getCountryFlag(c);
      return `<option value="${esc(c)}">${flag} ${esc(c)}</option>`;
    }).join('');
    if (cur) sel.value = cur;
  }

  function renderKPIs() {
    document.getElementById('kpiTotal').textContent = state.teams.length;
    const count = f => state.teams.filter(t=>matchStatusFilter(t,f)).length;
    document.getElementById('kpiPaid').textContent = count('paid');
    document.getElementById('kpiUnpaid').textContent = count('unpaid');
    document.getElementById('kpiPartial').textContent = count('partial');
    document.getElementById('kpiFlagged').textContent = count('flagged');
    document.getElementById('kpiExpired').textContent = count('expired');
    document.getElementById('kpiWrittenOff').textContent = count('written_off');
    document.querySelectorAll('.kpi-card').forEach(c=>c.classList.remove('active'));
    const a = document.querySelector(`.kpi-card[data-filter="${kpiFilter}"]`);
    if (a) a.classList.add('active');
  }

  function filterByKpi(f) { kpiFilter = kpiFilter===f?'all':f; renderKPIs(); renderTable(); }

  function updateBadges() {
    document.getElementById('badgeTeams').textContent = state.teams.length;
    const f = state.teams.filter(t=>t.payment_status==='flagged').length;
    const el = document.getElementById('badgeFlagged');
    el.textContent = f; el.style.display = f>0?'inline':'none';
  }

  function renderCheckAllBtn() {
    document.getElementById('btnCheckAll').style.display = state.teams.some(t=>t.stripe_payment_link_id)?'flex':'none';
  }

  function checkDeadlines() {
    const container0 = document.getElementById('bannerContainer');
    // Platformbeheer gaat niet over één evenement. Een waarschuwing over
    // betaaltermijnen van teams hoort daar niet te blijven staan.
    // Alleen de eigen melding vervangen; andere meldingen (tweestap,
    // platformtoegang) blijven staan.
    container0?.querySelectorAll('[data-bron="toernooi"]').forEach(b => b.remove());
    if (document.body.classList.contains('platform-modus') || router.module() !== 'toernooien' || opOverzicht()) return;
    const ev = state.events.find(e=>e.id===state.currentEventId);
    if (!ev?.payment_deadline) return;
    const deadline = new Date(ev.payment_deadline);
    const overdue = state.teams.filter(t=>new Date()>deadline&&!isSettled(t));
    const container = document.getElementById('bannerContainer');
    if (overdue.length) container.insertAdjacentHTML('beforeend', `
      <div class="banner banner-amber" data-bron="toernooi">
        <i class="ti ti-alert-triangle"></i>
        <span><strong>${overdue.length} team${overdue.length>1?'s':''}</strong> past payment deadline (${fmtDate(ev.payment_deadline)}) — review required.</span>
        <button class="banner-close" onclick="this.parentElement.remove()"><i class="ti ti-x"></i></button>
      </div>`);
  }

  // ── PANEL ──
  async function openPanel(teamId) {
    currentTeamId = teamId;
    const team = state.teams.find(t=>t.id===teamId);
    if (!team) return;
    document.getElementById('panelTeamName').textContent = team.team_name;
    document.getElementById('panelTeamMeta').textContent = `${team.country||'—'} · ID: ${team.sportdata_id||'—'} · ${team.athlete_count||0} entries`;
    await renderPanelBody(team);
    document.getElementById('panelOverlay').classList.add('open');
  }

  async function renderPanelBody(team) {
    const { data: logs } = await sb.from('activity_log').select('*').eq('team_id', team.id).order('created_at',{ascending:false}).limit(30);
    const voorstellen = await goedkeuringen.openVoorObject(team.id);
    const event = state.events.find(e=>e.id===state.currentEventId);
    const stages = ['none','email_1','reminder_1','reminder_2','final_warning'];
    const stageLabels = ['No contact','Email 1','Reminder 1','Reminder 2','Final warning'];
    const curIdx = stages.indexOf(team.reminder_stage||'none');
    const rd = team.reminder_dates||{};
    const adj = round2(team.manual_adjustment_eur);
    const derived = deriveStatusOf(team);
    // Money movements get their own history block, separate from the general
    // activity feed, so "what happened to this club's fee" is one glance.
    const feeLogs = (logs||[]).filter(l => FEE_ACTIONS.has(l.action_type));

    document.getElementById('panelBody').innerHTML = `
      <div class="panel-section">
        <div class="section-title"><i class="ti ti-info-circle"></i> Registration</div>
        <div class="field-grid">
          <div class="field-item"><div class="field-label">Sportdata ID</div><div class="field-value mono">${esc(team.sportdata_id||'—')}</div></div>
          <div class="field-item"><div class="field-label">Reg. date</div><div class="field-value">${fmtDate(team.registration_date)}</div></div>
          <div class="field-item"><div class="field-label">Country</div><div class="field-value">${esc(team.country||'—')}</div></div>
          <div class="field-item"><div class="field-label">Entries</div><div class="field-value">${team.athlete_count||0}</div></div>
          <div class="field-item full"><div class="field-label">Categories</div><div class="field-value">${(team.categories||[]).join(', ')||'—'}</div></div>
        </div>
      </div>
      <div class="panel-section">
        <div class="section-title"><i class="ti ti-user"></i> Contact</div>
        <div class="field-grid">
          <div class="field-item"><div class="field-label">Name</div><div class="editable-field"><input value="${esc(team.contact_name||team.user_firstname&&team.user_lastname?[team.user_firstname,team.user_lastname].filter(Boolean).join(' '):team.club_contact_name||'')}" placeholder="—" onblur="App.updateTeamField('contact_name',this.value)"></div></div>
          <div class="field-item"><div class="field-label">Phone</div><div class="editable-field"><input value="${esc(team.contact_phone||team.user_mobile||team.user_phone||team.club_phone||'')}" placeholder="—" onblur="App.updateTeamField('contact_phone',this.value)"></div></div>
          <div class="field-item full"><div class="field-label">Email</div><div class="editable-field"><input value="${esc(team.contact_email||team.user_email||team.club_email||'')}" type="email" placeholder="—" onblur="App.updateTeamField('contact_email',this.value)"></div></div>
        </div>
      </div>
      ${(team.user_firstname||team.user_lastname||team.user_email||team.user_address) ? `
      <div class="panel-section">
        <div class="section-title"><i class="ti ti-id-badge"></i> User data <span style="font-size:10px;font-weight:400;color:var(--text-3);margin-left:6px;text-transform:none;letter-spacing:0">from Club data CSV</span></div>
        <div class="field-grid">
          ${(team.user_firstname||team.user_lastname)?`<div class="field-item"><div class="field-label">User name</div><div class="field-value">${esc([team.user_firstname,team.user_lastname].filter(Boolean).join(' '))}</div></div>`:''}
          ${team.user_email?`<div class="field-item"><div class="field-label">User email</div><div class="field-value mono" style="font-size:12px"><a href="mailto:${esc(team.user_email)}" style="color:var(--accent)">${esc(team.user_email)}</a></div></div>`:''}
          ${team.user_mobile?`<div class="field-item"><div class="field-label">Mobile</div><div class="field-value mono">${esc(team.user_mobile)}</div></div>`:''}
          ${team.user_phone?`<div class="field-item"><div class="field-label">Phone</div><div class="field-value mono">${esc(team.user_phone)}</div></div>`:''}
          ${team.user_address?`<div class="field-item full"><div class="field-label">Address</div><div class="field-value">${esc(team.user_address)}${team.user_postal_code?' · '+esc(team.user_postal_code):''}${team.user_city?' · '+esc(team.user_city):''}${team.user_country?' · '+esc(team.user_country):''}</div></div>`:''}
          ${team.region?`<div class="field-item"><div class="field-label">Region</div><div class="field-value">${esc(team.region)}</div></div>`:''}
          ${team.is_referee!==undefined&&team.is_referee!==null?`<div class="field-item"><div class="field-label">E-Referee</div><div class="field-value">${team.is_referee?'<span class="badge badge-blue"><i class="ti ti-check" style="font-size:10px"></i> Yes</span>':'<span class="badge badge-grey">No</span>'}</div></div>`:''}
        </div>
        ${(team.club_contact_name||team.club_email||team.club_phone||team.club_address)?`
        <div style="margin-top:12px;padding-top:12px;border-top:1px solid var(--border)">
          <div style="font-size:10px;font-weight:600;text-transform:uppercase;letter-spacing:.07em;color:var(--text-3);margin-bottom:8px">Club contact</div>
          <div class="field-grid">
            ${team.club_contact_name?`<div class="field-item"><div class="field-label">Club contact</div><div class="field-value">${esc(team.club_contact_name)}</div></div>`:''}
            ${team.club_email?`<div class="field-item"><div class="field-label">Club email</div><div class="field-value mono" style="font-size:12px"><a href="mailto:${esc(team.club_email)}" style="color:var(--accent)">${esc(team.club_email)}</a></div></div>`:''}
            ${team.club_phone?`<div class="field-item"><div class="field-label">Club phone</div><div class="field-value mono">${esc(team.club_phone)}</div></div>`:''}
            ${team.club_address?`<div class="field-item full"><div class="field-label">Club address</div><div class="field-value">${esc(team.club_address)}</div></div>`:''}
          </div>
        </div>`:''}
      </div>` : ''}
      <div class="panel-section">
        <div class="section-title"><i class="ti ti-coin-euro"></i> Financial</div>
        <div class="field-grid">
          <div class="field-item"><div class="field-label">Entry fee</div><div class="field-value mono">€${fmtMoney(team.entry_fee_eur)}</div></div>
          <div class="field-item"><div class="field-label">Discount</div><div class="field-value mono">€${fmtMoney(team.discount_eur)}</div></div>
          ${adj !== 0 ? `
          <div class="field-item"><div class="field-label">Manual correction</div><div class="field-value mono" style="color:var(--purple,#7c3aed)">${adj>0?'−':'+'}€${fmtMoney(Math.abs(adj))}</div></div>
          <div class="field-item"><div class="field-label">Billed after correction</div><div class="field-value mono" style="font-weight:600">€${fmtMoney(billedOf(team))}</div></div>` : ''}
          <div class="field-item"><div class="field-label">Amount paid</div><div class="field-value mono">€${fmtMoney(team.amount_paid_eur)}</div></div>
          <div class="field-item"><div class="field-label">Balance due</div><div class="field-value mono" style="font-weight:600;color:${isWrittenOff(team)?'var(--text-3)':(team.balance_eur>0?'var(--amber)':'var(--green)')}">€${fmtMoney(team.balance_eur)}</div></div>
        </div>

        ${voorstellen.map(v => `<div class="banner banner-amber" style="margin-top:12px"><i class="ti ti-clock-hour-4"></i>
          <span>${esc(t_('vierogen.wacht', { handeling: t_('vierogen.handeling.' + v.action_type), door: v.submitted_by }))}</span></div>`).join('')}
        ${team.settlement && team.settlement !== 'none' ? `
        <div class="settle-box ${isWrittenOff(team)?'settle-box-off':'settle-box-adj'}">
          <div class="settle-head">
            ${settlementBadge(team)}
            <span class="settle-amt">${adj>0?'−':'+'}€${fmtMoney(Math.abs(adj))} on the billed amount</span>
          </div>
          <div class="settle-reason">${team.adjustment_reason?esc(team.adjustment_reason):'<em style="color:var(--text-3)">No reason recorded</em>'}</div>
          <div class="settle-meta">${team.settled_by?esc(team.settled_by):'unknown'} · ${team.settled_at?fmtDatetime(team.settled_at):'—'}</div>
          <div style="margin-top:10px;display:flex;gap:6px;flex-wrap:wrap">
            <button class="btn btn-sm btn-default" onclick="App.openAdjustModal('${team.id}')"><i class="ti ti-edit"></i> Change correction</button>
            <button class="btn btn-sm btn-ghost" onclick="App.confirmReverseSettlement('${team.id}')"><i class="ti ti-arrow-back-up"></i> Undo</button>
          </div>
        </div>` : `
        <div style="margin-top:12px;display:flex;gap:6px;flex-wrap:wrap">
          <button class="btn btn-sm btn-default" onclick="App.openAdjustModal('${team.id}')"><i class="ti ti-adjustments-dollar"></i> Adjust balance</button>
          <button class="btn btn-sm btn-ghost" onclick="App.openWriteOffModal('${team.id}')"><i class="ti ti-receipt-off"></i> No show · write off</button>
        </div>`}

        <div style="margin-top:14px">
          <div class="field-label" style="margin-bottom:4px">Payment status</div>
          <div class="editable-field" style="max-width:220px">
            <select onchange="App.updateTeamField('payment_status',this.value)">
              <option value="unpaid" ${team.payment_status==='unpaid'?'selected':''}>Unpaid</option>
              <option value="partial" ${team.payment_status==='partial'?'selected':''}>Partial</option>
              <option value="paid" ${team.payment_status==='paid'?'selected':''}>Paid</option>
              <option value="flagged" ${team.payment_status==='flagged'?'selected':''}>Flagged</option>
              <option value="expired" ${team.payment_status==='expired'?'selected':''}>Expired</option>
            </select>
          </div>
          ${team.payment_status !== derived ? `
          <div style="margin-top:6px;font-size:11px;color:var(--amber);display:flex;align-items:center;gap:5px">
            <i class="ti ti-alert-triangle" style="font-size:12px"></i>
            The amounts say <strong>${derived}</strong>. The next CSV import will reset the status to that.
          </div>` : ''}
        </div>
      </div>

      <div class="panel-section">
        <div class="section-title"><i class="ti ti-history"></i> Fee &amp; balance history
          <span style="font-size:10px;font-weight:400;color:var(--text-3);margin-left:6px;text-transform:none;letter-spacing:0">every change to what this club owes</span>
        </div>
        ${feeLogs.length ? `
        <div class="fee-hist">
          <table>
            <thead><tr><th>When</th><th>What changed</th><th>Movement</th><th>By</th></tr></thead>
            <tbody>${feeLogs.map(l => `
              <tr>
                <td class="fh-when">${fmtDatetime(l.created_at)}</td>
                <td>
                  <div style="display:flex;align-items:center;gap:6px;margin-bottom:3px">${actionIcon(l.action_type)}<strong style="color:var(--text-1);font-size:11px">${feeActionLabel(l.action_type)}</strong></div>
                  ${esc(l.description)}
                </td>
                <td class="fh-move">${l.old_value
                  ? `<span class="fh-from">${esc(l.old_value)}</span><br><span class="fh-to">${esc(l.new_value||'')}</span>`
                  : (l.amount_delta != null ? `<span class="fh-to">${Number(l.amount_delta)>0?'+':''}€${fmtMoney(l.amount_delta)}</span>` : '—')}</td>
                <td class="fh-who">${esc(l.actor_email||'system')}</td>
              </tr>`).join('')}</tbody>
          </table>
        </div>` : `
        <p style="font-size:13px;color:var(--text-3)">No fee or balance changes recorded. The entry fee is exactly as Sportdata delivered it.</p>`}
      </div>

      <div class="panel-section">
        <div class="section-title"><i class="ti ti-bolt"></i> Stripe</div>
        ${team.stripe_payment_link ? `
          <div style="display:flex;align-items:center;gap:8px;margin-bottom:10px;flex-wrap:wrap">
            <a href="${team.stripe_payment_link}" target="_blank" class="btn btn-default btn-sm"><i class="ti ti-external-link"></i> Open link</a>
            <button class="btn btn-ghost btn-sm" onclick="navigator.clipboard.writeText('${team.stripe_payment_link}');App.toast('Link copied','success')"><i class="ti ti-copy"></i> Copy</button>
            ${team.stripe_payment_link_id?`<button class="btn btn-ghost btn-sm" onclick="App.checkSingleStripeStatus('${team.id}')"><i class="ti ti-refresh"></i> Check status</button>`:''}
            ${stripeLinkIsStale(team)?`<button class="btn btn-default btn-sm" onclick="App.regenerateStripeLink('${team.id}')"><i class="ti ti-link-off"></i> Regenerate link</button>`:''}
          </div>
          <div style="font-size:11px;color:var(--text-3);font-family:var(--mono);word-break:break-all;margin-bottom:10px">${team.stripe_payment_link}</div>` :
          `<p style="font-size:13px;color:var(--text-3);margin-bottom:10px">No payment link generated yet.</p>`}
        <button class="btn btn-primary btn-sm" onclick="App.openStripeModal('${team.id}')">
          <i class="ti ti-bolt"></i> ${team.stripe_payment_link?'Regenerate':'Generate Stripe link'}
        </button>
        ${event?.payment_deadline && new Date()>new Date(event.payment_deadline) && !isSettled(team) ? `
          <button class="btn btn-sm" style="margin-left:8px;background:var(--red-bg);color:var(--red);border-color:#fecaca" onclick="App.confirmMarkExpired('${team.id}')">
            <i class="ti ti-user-x"></i> Mark expired
          </button>` : ''}
      </div>
      <div class="panel-section">
        <div class="section-title"><i class="ti ti-mail"></i> Reminder cycle</div>
        ${isWrittenOff(team) ? `
        <div class="banner banner-amber" style="margin-bottom:12px">
          <i class="ti ti-receipt-off"></i>
          <span>This balance was written off. The club is excluded from the deadline banner and Stripe checks. Only send a payment reminder if you intend to undo the write-off.</span>
        </div>` : ''}
        <div class="reminder-stepper">
          ${stages.slice(1).map((s,i)=>{
            const idx=i+1; const done=curIdx>=idx; const isLast=i===stages.length-2;
            return `<div class="reminder-step">
              <div class="step-line-wrap">
                <div class="step-dot ${done?'done':''}">${done?'<i class="ti ti-check" style="font-size:10px"></i>':idx}</div>
                ${!isLast?`<div class="step-connector ${done?'done':''}"></div>`:''}
              </div>
              <div class="step-content">
                <div class="step-label">${stageLabels[idx]}</div>
                <div class="step-date">${rd[s]?fmtDatetime(rd[s]):'Not sent'}</div>
              </div>
            </div>`;
          }).join('')}
        </div>
      </div>
      <div class="panel-section">
        <div class="section-title"><i class="ti ti-notes"></i> Notes</div>
        <textarea class="notes-field" onblur="App.updateTeamField('notes',this.value)">${esc(team.notes||'')}</textarea>
      </div>
      <div class="panel-section">
        <div class="section-title"><i class="ti ti-list-details"></i> Activity log</div>
        <div class="activity-list">
          ${logs?.length ? logs.map(l=>`
            <div class="activity-entry">
              <div class="activity-icon">${actionIcon(l.action_type)}</div>
              <div class="activity-body">
                <div class="activity-desc">${esc(l.description)}</div>
                ${l.old_value?`<div style="font-size:11px;color:var(--text-3);margin-top:2px">${esc(l.old_value)} → ${esc(l.new_value||'')}</div>`:''}
                <div class="activity-time">${fmtDatetime(l.created_at)}${l.actor_email?` · ${esc(l.actor_email)}`:''}</div>
              </div>
            </div>`).join('') : '<p style="font-size:13px;color:var(--text-3)">No activity yet.</p>'}
        </div>
      </div>`;
  }

  function closePanel(e) { if (e && e.target !== document.getElementById('panelOverlay')) return; closePanelDirect(); }
  function closePanelDirect() { document.getElementById('panelOverlay').classList.remove('open'); currentTeamId = null; }

  // ── UPDATE FIELD ──
  async function updateTeamField(field, value) {
    if (!currentTeamId) return;
    const team = state.teams.find(t=>t.id===currentTeamId);
    if (!team || team[field] === value) return;
    const old = team[field];
    await sb.from('teams').update({ [field]: value, updated_at: new Date().toISOString() }).eq('id', currentTeamId);
    await logActivity(currentTeamId, team.team_name, 'status_changed', `${field} updated`, String(old||''), String(value||''));
    state.teams.find(t=>t.id===currentTeamId)[field] = value;
    renderTable(); renderKPIs(); updateBadges();
    toast('Saved', 'success');
  }

  // ── IMPORT MENU ──
  function showImportMenu(btn) {
    closeDropdown();
    const rect = btn.getBoundingClientRect();
    const dd = document.createElement('div');
    dd.className = 'import-menu'; dd.id = 'activeDropdown';
    dd.style.cssText = `position:fixed;top:${rect.bottom+4}px;left:${rect.left}px;`;
    dd.innerHTML = `
      <button class="import-menu-item" onclick="document.getElementById('csvPaymentsInput').click();App.closeDropdown()">
        <i class="ti ti-credit-card"></i>
        <span><div class="im-label">Entry payments CSV</div><div class="im-sub">Clubs, balance, amount paid</div></span>
      </button>
      <button class="import-menu-item" onclick="document.getElementById('csvClubDataInput').click();App.closeDropdown()">
        <i class="ti ti-building"></i>
        <span><div class="im-label">Club / Team data CSV</div><div class="im-sub">Contact, address, user details</div></span>
      </button>
      <button class="import-menu-item" onclick="document.getElementById('csvDeelnemersInput').click();App.closeDropdown()">
        <i class="ti ti-users-group"></i>
        <span><div class="im-label">${esc(t_('dln.menu.titel'))}</div><div class="im-sub">${esc(t_('dln.menu.sub'))}</div></span>
      </button>`;
    document.getElementById('dropdownContainer').appendChild(dd);
    setTimeout(() => document.addEventListener('click', closeDropdown, {once:true}), 10);
  }

  // ── CSV IMPORT A: Entry Payments ──
  async function importPaymentsCSV(event) {
    const file = event.target.files[0];
    if (!file || !state.currentEventId) return;
    const text = await file.text();
    const lines = text.trim().split('\n').filter(l=>l.trim());
    const header = lines[0].split(';').map(h=>h.replace(/"/g,'').trim());
    const rows = lines.slice(1).filter(l=>!l.startsWith('"Sum:')&&!l.startsWith('Sum:'));
    let added=0, skipped=0, flaggedCount=0, updatedCount=0;
    const diffs = [];
    const appliedPayments = [];   // CSV was ahead: applied automatically
    const paymentReminders = [];  // CRM was ahead: kept, needs Sportdata update
    const staleLinks = [];        // balance moved while a payment link exists
    const adjustConflicts = [];   // Sportdata moved the fee under a manual correction

    for (const row of rows) {
      const cols = row.split(';').map(c=>c.replace(/"/g,'').trim());
      const obj = {}; header.forEach((h,i)=>obj[h]=cols[i]||'');
      const clubRaw = obj['Clubs']||'';
      const abbrMatch = clubRaw.match(/\(([^)]+)\)\s*$/);
      const abbr = abbrMatch?abbrMatch[1]:'';
      const fullName = clubRaw.replace(/\s*\([^)]*\)\s*$/,'').replace(/\[\d+\]/,'').trim();
      const sdId = obj['ID']||'';
      const country = (obj['Country']||'').toUpperCase();
      const entries = parseInt(obj['Entries']||'0')||0;
      const entryFee = parseFloat((obj['Entry fee  EUR']||obj['Entry fee EUR']||'0').replace('EUR','').trim())||0;
      const discount = parseFloat((obj['Discount EUR']||'0').replace('EUR','').trim())||0;
      const amountPaid = parseFloat((obj['Amount Paid EUR']||'0').replace('EUR','').trim())||0;
      const balance = parseFloat((obj['Balance EUR']||'0').replace('EUR','').trim())||0;
      const existing = sdId ? state.teams.find(t=>t.sportdata_id===sdId) : null;

      if (existing) {
        // Sportdata owns entries, fees and discounts. Those are applied without
        // asking. Only the paid amount can legitimately differ in both
        // directions, because Stripe payments are entered into Sportdata by
        // hand and the CSV can therefore lag behind the CRM.
        const upd = {};
        const prevEntries  = Number(existing.athlete_count || 0);
        const prevFee      = Number(existing.entry_fee_eur || 0);
        const prevDiscount = Number(existing.discount_eur || 0);
        const prevPaid     = Number(existing.amount_paid_eur || 0);
        const prevStatus   = existing.payment_status;
        const prevBalance  = Number(existing.balance_eur || 0);
        const prevAdjust   = Number(existing.manual_adjustment_eur || 0);

        if (prevEntries !== entries) {
          upd.athlete_count = entries;
          await logActivity(existing.id, existing.team_name, 'entries_changed',
            `Entries changed from ${prevEntries} to ${entries} via CSV re-import (${file.name})`,
            String(prevEntries), String(entries));
        }
        if (Math.abs(prevFee - entryFee) > 0.001) {
          upd.entry_fee_eur = entryFee;
          const perOld = prevEntries > 0 ? prevFee / prevEntries : 0;
          const perNew = entries > 0 ? entryFee / entries : 0;
          await logActivity(existing.id, existing.team_name, 'price_changed',
            `Entry fee changed from €${fmtMoney(prevFee)} to €${fmtMoney(entryFee)} via CSV re-import`
            + ` (€${fmtMoney(perOld)} to €${fmtMoney(perNew)} per entry)`,
            String(prevFee), String(entryFee));
        }
        if (Math.abs(prevDiscount - discount) > 0.001) {
          upd.discount_eur = discount;
          await logActivity(existing.id, existing.team_name, 'price_changed',
            `Discount changed from €${fmtMoney(prevDiscount)} to €${fmtMoney(discount)} via CSV re-import`,
            String(prevDiscount), String(discount));
        }

        // Reconcile the paid amount.
        const delta = Math.round((amountPaid - prevPaid) * 100) / 100;
        let effectivePaid = prevPaid;

        if (delta > 0.001) {
          // Sportdata is ahead: a payment was recorded there. Apply it.
          effectivePaid = amountPaid;
          upd.amount_paid_eur = amountPaid;
          if (!existing.paid_at) upd.paid_at = new Date().toISOString();
          appliedPayments.push({
            teamId: existing.id, teamName: existing.team_name,
            from: prevPaid, to: amountPaid, prevBalance, prevStatus
          });
        } else if (delta < -0.001) {
          // CRM is ahead: almost always a Stripe payment not yet entered into
          // Sportdata. Keep the CRM figure and remind the user instead.
          paymentReminders.push({
            teamId: existing.id, teamName: existing.team_name,
            crm: prevPaid, csv: amountPaid, fee: entryFee, discount
          });
        }

        // Fee, discount or paid may have moved, so balance and status are
        // always recomputed rather than taken from the CSV.
        // The manual adjustment is ours, not Sportdata's, so it is carried
        // through untouched. Same for `settlement`: the import never writes it.
        const effFee      = upd.entry_fee_eur !== undefined ? upd.entry_fee_eur : prevFee;
        const effDiscount = upd.discount_eur  !== undefined ? upd.discount_eur  : prevDiscount;
        const newBalance  = Math.max(0, Math.round((effFee - effDiscount - prevAdjust - effectivePaid) * 100) / 100);
        const newStatus   = deriveStatus(effFee, effDiscount, effectivePaid, prevAdjust);

        if (Math.abs(prevBalance - newBalance) > 0.001) upd.balance_eur = newBalance;
        if (prevStatus !== newStatus && prevStatus !== 'expired') upd.payment_status = newStatus;

        // Sportdata changed the fee while we hold a local correction against
        // the old fee. Silently recomputing would either resurrect a written
        // off balance or hide a real increase, so this is surfaced for review
        // instead of being decided automatically.
        if (prevAdjust !== 0 && Math.abs(prevFee - entryFee) > 0.001) {
          adjustConflicts.push({
            teamId: existing.id, teamName: existing.team_name,
            oldFee: prevFee, newFee: entryFee, adjustment: prevAdjust,
            settlement: existing.settlement || 'none'
          });
          await logActivity(existing.id, existing.team_name, 'adjustment_conflict',
            `Sportdata changed the entry fee from €${fmtMoney(prevFee)} to €${fmtMoney(entryFee)}`
            + ` while a manual correction of €${fmtMoney(prevAdjust)} is active. Review required.`,
            String(prevFee), String(entryFee));
        }

        if (Object.keys(upd).length) {
          upd.updated_at = new Date().toISOString();
          await sb.from('teams').update(upd).eq('id', existing.id);
          Object.assign(existing, upd);
          if (upd.amount_paid_eur !== undefined) {
            await logActivity(existing.id, existing.team_name, 'payment_received',
              `Payment of €${fmtMoney(amountPaid)} recorded from Sportdata import`
              + ` · balance now €${fmtMoney(newBalance)}`,
              `${prevStatus} / €${fmtMoney(prevPaid)}`,
              `${newStatus} / €${fmtMoney(amountPaid)}`);
          } else if (upd.payment_status !== undefined) {
            await logActivity(existing.id, existing.team_name, 'status_changed',
              `Status recalculated after import (fee €${fmtMoney(effFee)}, paid €${fmtMoney(effectivePaid)})`,
              prevStatus, newStatus);
          }
          updatedCount++;
        } else {
          skipped++;
        }

        // The balance moved but an old payment link is still live for the
        // previous amount. Flag it so the link can be replaced.
        const finalBalance = Number(existing.balance_eur || 0);
        if (existing.stripe_payment_link_id && finalBalance > 0
            && Math.abs(prevBalance - finalBalance) > 0.01) {
          staleLinks.push({
            teamId: existing.id, teamName: existing.team_name,
            oldAmount: existing.stripe_link_amount != null
              ? Number(existing.stripe_link_amount) : prevBalance,
            newAmount: finalBalance
          });
        }
      } else {
        const { data: ins, error } = await sb.from('teams').insert([{
          event_id:state.currentEventId,sportdata_id:sdId,team_name:fullName,team_abbreviation:abbr,
          country,athlete_count:entries,entry_fee_eur:entryFee,discount_eur:discount,
          amount_paid_eur:amountPaid,
          balance_eur:Math.max(0,Math.round((entryFee-discount-amountPaid)*100)/100),
          payment_status:deriveStatus(entryFee,discount,amountPaid,0),
          settlement:'none',manual_adjustment_eur:0,
          paid_at:amountPaid>0?new Date().toISOString():null,
          reminder_stage:'none',reminder_dates:{},
          updated_at:new Date().toISOString()
        }]).select();
        if (!error&&ins) {
          // Auto-enrich from clubs register if available
          if (sdId) {
            const { data: club } = await sb.from('clubs').select('*').eq('sportdata_id',sdId).single();
            if (club) {
              const enrich = {};
              const userName = [club.user_firstname,club.user_lastname].filter(Boolean).join(' ');
              if (!ins[0].contact_name && (userName||club.contact_name)) enrich.contact_name=userName||club.contact_name;
              if (!ins[0].contact_email && (club.user_email||club.email)) enrich.contact_email=club.user_email||club.email;
              if (!ins[0].contact_phone && (club.user_mobile||club.user_phone||club.phone)) enrich.contact_phone=club.user_mobile||club.user_phone||club.phone;
              ['user_firstname','user_lastname','user_email','user_mobile','user_phone','user_address','user_postal_code','user_city','user_country','region','is_referee','club_email','club_phone'].forEach(f=>{
                if(club[f]!==undefined&&club[f]!==null&&club[f]!=='') enrich[f]=club[f];
              });
              if(Object.keys(enrich).length) {
                await sb.from('teams').update({...enrich,updated_at:new Date().toISOString()}).eq('id',ins[0].id);
                Object.assign(ins[0], enrich);
              }
            }
          }
          state.teams.push(ins[0]); added++;
          if(ins[0].payment_status==='flagged') flaggedCount++;
          if(amountPaid>0) await logActivity(ins[0].id, fullName, 'payment_received',
            `Imported with €${fmtMoney(amountPaid)} already paid (${ins[0].payment_status})`,
            '', `${ins[0].payment_status} / €${fmtMoney(amountPaid)}`);
        }
      }
    }

    const paidSum = appliedPayments.reduce((s,p)=>s+(p.to-p.from),0);
    await logActivity(null,'System','csv_imported',
      `Entry payments CSV (${file.name}) — ${added} new, ${updatedCount} updated, ${appliedPayments.length} payments applied`
      + ` (€${fmtMoney(paidSum)}), ${paymentReminders.length} ahead of Sportdata,`
      + ` ${staleLinks.length} stale payment links, ${adjustConflicts.length} corrections to review,`
      + ` ${skipped} unchanged`,'','');

    document.getElementById('bannerContainer').innerHTML = `
      <div class="banner banner-blue"><i class="ti ti-upload"></i>
        <span>Entry payments import — <strong>${added} new</strong> · <strong>${updatedCount} updated</strong>
        ${appliedPayments.length?` · <strong>${appliedPayments.length} payment${appliedPayments.length>1?'s':''} applied</strong> (€${fmtMoney(paidSum)})`:''}
        ${paymentReminders.length?` · <strong>${paymentReminders.length} need Sportdata update</strong>`:''}
        ${staleLinks.length?` · <strong>${staleLinks.length} payment link${staleLinks.length>1?'s':''} out of date</strong>`:''}
        ${adjustConflicts.length?` · <strong>${adjustConflicts.length} manual correction${adjustConflicts.length>1?'s':''} need review</strong>`:''}
        · ${skipped} unchanged</span>
        <button class="banner-close" onclick="this.parentElement.remove()"><i class="ti ti-x"></i></button>
      </div>`;

    renderTable(); renderKPIs(); renderCountryFilter(); updateBadges();
    if (document.getElementById('pageDashboard')?.classList.contains('active')) renderDashboard();
    event.target.value='';

    if (appliedPayments.length || paymentReminders.length || staleLinks.length || adjustConflicts.length) {
      showPaymentReviewModal(appliedPayments, paymentReminders, staleLinks, adjustConflicts);
    } else {
      toast(`Payments CSV — ${added} added, ${updatedCount} updated`, 'success');
    }
  }

  // ── CSV IMPORT C: Deelnemers (competitor entries) ──
  async function importDeelnemersCSV(event) {
    const bestand = event.target.files[0];
    event.target.value = '';
    await deelnemersMod.importeer(bestand);
  }

  // ── CSV IMPORT B: Club / Team Data ──
  // Registered clubs (present in teams) → contact enrichment via diff-review
  // Non-registered clubs → global clubs register only, never touch teams
  async function importClubDataCSV(event) {
    const file = event.target.files[0];
    if (!file || !state.currentEventId) return;
    const text = await file.text();
    const lines = text.trim().split('\n').filter(l=>l.trim());
    const headerLine = lines.find(l=>l.includes('Club/Team ID'));
    if (!headerLine) { toast('Invalid Club Data CSV format', 'error'); return; }
    const headerIdx = lines.indexOf(headerLine);
    const header = headerLine.split(';').map(h=>h.replace(/"/g,'').trim());
    const rows = lines.slice(headerIdx+1).filter(l=>l.trim());

    let enriched=0, clubsAdded=0, clubsUpdated=0, flaggedCount=0;
    const diffs = [];

    for (const row of rows) {
      const cols = row.split(';').map(c=>c.replace(/"/g,'').trim());
      const obj = {}; header.forEach((h,i)=>obj[h]=cols[i]||'');
      const sdId = obj['Club/Team ID']||'';
      if (!sdId) continue;

      const clubName = obj['Name']||'';
      const abbr = obj['Abbrev.']||'';
      const countryCode = obj['Country-Code']||'';
      const countryName = (obj['Country Name']||'').toUpperCase();
      const region = obj['Region']||'';
      const clubPhone = obj['Phone']||'';
      const clubEmail = obj['Email']||'';
      const clubAddress = obj['Address']||'';
      const contactName = `${obj['Contact']||''}`.trim();
      const userFirstname = obj['User Firstname']||'';
      const userLastname = obj['User Lastname']||'';
      const userEmail = obj['User Email']||'';
      const userPhone = obj['User Phone']||'';
      const userMobile = obj['User Mobile']||'';
      const userAddress = obj['User Address']||'';
      const userPostal = obj['User Postal Code']||'';
      const userCity = obj['User City']||'';
      const userCountry = obj['User Country']||'';
      const isReferee = obj['User is E-Referee']==='1';
      const fullUserName = [userFirstname, userLastname].filter(Boolean).join(' ');

      const clubPayload = {
        sportdata_id: sdId,
        club_name: clubName,
        abbreviation: abbr,
        country_code: countryCode,
        country_name: countryName,
        region,
        phone: clubPhone,
        email: clubEmail,
        address: clubAddress,
        contact_name: contactName,
        user_firstname: userFirstname,
        user_lastname: userLastname,
        user_email: userEmail,
        user_phone: userPhone,
        user_mobile: userMobile,
        user_address: userAddress,
        user_postal_code: userPostal,
        user_city: userCity,
        user_country: userCountry,
        is_referee: isReferee,
        updated_at: new Date().toISOString()
      };

      // ── Always upsert into clubs register ──
      const { data: existingClub } = await sb.from('clubs')
        .select('id').eq('sportdata_id', sdId).single();
      if (existingClub) {
        await sb.from('clubs').update(clubPayload).eq('sportdata_id', sdId);
        clubsUpdated++;
      } else {
        await sb.from('clubs').insert([clubPayload]);
        clubsAdded++;
      }

      // ── If this club is also a registered team: offer contact enrichment ──
      const registeredTeam = state.teams.find(t=>t.sportdata_id===sdId);
      if (registeredTeam) {
        const contactFields = {
          contact_name: fullUserName||contactName||undefined,
          contact_email: userEmail||clubEmail||undefined,
          contact_phone: userMobile||userPhone||clubPhone||undefined,
          club_email: clubEmail||undefined,
          club_phone: clubPhone||undefined,
          club_address: clubAddress||undefined,
          user_email: userEmail||undefined,
          user_firstname: userFirstname||undefined,
          user_lastname: userLastname||undefined,
          user_mobile: userMobile||undefined,
          user_phone: userPhone||undefined,
          user_address: userAddress||undefined,
          user_postal_code: userPostal||undefined,
          user_city: userCity||undefined,
          user_country: userCountry||undefined,
          region: region||undefined,
          is_referee: isReferee
        };
        Object.keys(contactFields).forEach(k=>contactFields[k]===undefined&&delete contactFields[k]);

        const diffCheckFields = ['contact_name','contact_email','contact_phone','club_email','club_phone','club_address','user_email'];
        const changes = [];
        for (const f of diffCheckFields) {
          if (contactFields[f]!==undefined && contactFields[f]!=='' && String(registeredTeam[f]||'')!==String(contactFields[f]||'')) {
            changes.push({field:f, old:registeredTeam[f]||'', new:contactFields[f]});
          }
        }
        if (changes.length) {
          // Also note which contact fields are empty and can be auto-filled
          const autoFillEmpty = {};
          ['contact_name','contact_email','contact_phone'].forEach(f=>{
            if(!registeredTeam[f] && contactFields[f]) autoFillEmpty[f]=contactFields[f];
          });
          diffs.push({teamId:registeredTeam.id, teamName:registeredTeam.team_name, changes, allFields:contactFields, autoFillEmpty});
          // Immediately auto-fill empty fields (no diff needed for fields that are blank)
          if(Object.keys(autoFillEmpty).length) {
            await sb.from('teams').update({...autoFillEmpty,updated_at:new Date().toISOString()}).eq('id',registeredTeam.id);
            Object.assign(registeredTeam, autoFillEmpty);
          }
          flaggedCount++;
        } else {
          // Silent update for non-contact enrichment fields
          const silent = {};
          ['user_firstname','user_lastname','user_address','user_postal_code','user_city','user_country','user_phone','user_mobile','region','is_referee'].forEach(f=>{
            if(contactFields[f]!==undefined) silent[f]=contactFields[f];
          });
          // Auto-fill contact_name/email/phone if currently empty on the team record
          const autoFill = {
            contact_name: contactFields.contact_name,
            contact_email: contactFields.contact_email,
            contact_phone: contactFields.contact_phone
          };
          ['contact_name','contact_email','contact_phone'].forEach(f=>{
            if(!registeredTeam[f] && autoFill[f]) silent[f]=autoFill[f];
          });
          if(Object.keys(silent).length) {
            await sb.from('teams').update({...silent,updated_at:new Date().toISOString()}).eq('id',registeredTeam.id);
            Object.assign(registeredTeam, silent);
          }
          enriched++;
        }
      }
    }

    await logActivity(null,'System','csv_imported',
      `Club data CSV (${file.name}) — ${clubsAdded} clubs added, ${clubsUpdated} updated, ${enriched} teams enriched, ${flaggedCount} flagged`,'','');

    // Update clubs badge
    await loadClubsBadge();

    const totalClubs = clubsAdded + clubsUpdated;
    document.getElementById('bannerContainer').innerHTML = `
      <div class="banner banner-blue"><i class="ti ti-upload"></i>
        <span>Club data import — <strong>${totalClubs} clubs</strong> in register (${clubsAdded} new, ${clubsUpdated} updated) · <strong>${enriched} registered teams</strong> enriched${flaggedCount>0?` · <strong>${flaggedCount} flagged</strong> for contact review`:''}</span>
        <button class="banner-close" onclick="this.parentElement.remove()"><i class="ti ti-x"></i></button>
      </div>`;

    renderTable(); renderKPIs(); renderCountryFilter(); updateBadges();
    if (diffs.length) { pendingDiffs=diffs; showClubDiffModal(); }
    event.target.value='';
    toast(`Club data — ${clubsAdded} clubs added, ${enriched} teams enriched`, 'success');
  }

  async function loadClubsBadge() {
    const { count } = await sb.from('clubs').select('id',{count:'exact',head:true});
    const el = document.getElementById('badgeClubs');
    if (el) { el.textContent=count||0; el.style.display=count>0?'inline':'none'; }
  }

  let clubSort = { field: 'club_name', dir: 'asc' };
  let clubColFilters = {};
  let clubSelectedIds = new Set();
  const CLUB_HIDDEN_COLS_KEY = 'tornema_club_hidden_cols';
  let clubHiddenCols = new Set(JSON.parse(localStorage.getItem(CLUB_HIDDEN_COLS_KEY)||'[]'));

  const CLUB_COLS = [
    {key:'club_name',label:'Club',required:true},{key:'country',label:'Country'},
    {key:'contact',label:'Contact'},{key:'email',label:'Email'},{key:'phone',label:'Phone'},
    {key:'address',label:'Address'},{key:'region',label:'Region'},
    {key:'is_referee',label:'E-Referee'},{key:'registered',label:'Registered this event'}
  ];

  function saveClubColVis(){localStorage.setItem(CLUB_HIDDEN_COLS_KEY,JSON.stringify([...clubHiddenCols]));}

  function applyClubColVis(){
    CLUB_COLS.forEach(c=>{
      document.querySelectorAll(`#clubsTable th[data-col="${c.key}"], #clubsTable td[data-col="${c.key}"]`).forEach(el=>{
        el.style.display=clubHiddenCols.has(c.key)?'none':'';
      });
    });
  }

  function clubVal(c,field){
    switch(field){
      case 'club_name': return c.club_name||'';
      case 'country': return c.country_name||c.country_code||'';
      case 'contact': return [c.user_firstname,c.user_lastname].filter(Boolean).join(' ')||c.contact_name||'';
      case 'email': return c.user_email||c.email||'';
      case 'phone': return c.user_mobile||c.user_phone||c.phone||'';
      case 'address': return [c.user_address,c.user_city,c.user_country].filter(Boolean).join(', ')||'';
      case 'region': return c.region||'';
      case 'is_referee': return c.is_referee?'yes':'no';
      default: return '';
    }
  }

  async function renderClubsPage() {
    if (!state.allClubs.length) {
      const { data } = await sb.from('clubs').select('*').order('club_name',{ascending:true});
      state.allClubs = data||[];
    }
    const search = (document.getElementById('clubsSearch')?.value||'').toLowerCase();
    let filtered = state.allClubs.filter(c => {
      if (search && !['club_name','country_name','user_email','email','sportdata_id','contact_name','user_firstname','user_lastname'].some(f=>(c[f]||'').toLowerCase().includes(search))) return false;
      for (const [field, filter] of Object.entries(clubColFilters)) {
        const val = clubVal(c,field).trim();
        if (filter.type==='empty' && !isEmptyVal(val)) return false;
        if (filter.type==='notempty' && isEmptyVal(val)) return false;
        if (filter.type==='contains' && !val.toLowerCase().includes(filter.value.toLowerCase())) return false;
      }
      return true;
    });
    filtered.sort((a,b)=>{
      const av=clubVal(a,clubSort.field).toLowerCase(), bv=clubVal(b,clubSort.field).toLowerCase();
      return clubSort.dir==='asc'?av.localeCompare(bv):bv.localeCompare(av);
    });
    const countEl=document.getElementById('clubsCount');
    if(countEl) countEl.textContent=`${filtered.length} of ${state.allClubs.length} clubs`;
    const clearBtn=document.getElementById('btnClearClubFilters');
    if(clearBtn) clearBtn.style.display=Object.keys(clubColFilters).length>0?'flex':'none';
    const el=document.getElementById('clubsContent');
    if(!filtered.length){
      el.innerHTML=`<div class="empty-state" style="background:var(--surface);border:1px solid var(--border);border-radius:var(--radius-lg);padding:60px 20px;text-align:center">
        <i class="ti ti-building" style="font-size:40px;color:var(--text-3);display:block;margin-bottom:12px"></i>
        <strong style="color:var(--text-2);font-size:15px">${state.allClubs.length===0?'No clubs imported yet':'No clubs match'}</strong>
        <p style="color:var(--text-3);font-size:13px;margin-top:4px">${state.allClubs.length===0?'Import a Club / Team data CSV to populate the register.':''}</p>
      </div>`;
      return;
    }
    const sortIcon=(field)=>{
      if(clubSort.field!==field) return '<i class="ti ti-arrows-sort" style="font-size:10px;opacity:.4"></i>';
      return clubSort.dir==='asc'?'<i class="ti ti-sort-ascending" style="font-size:10px;color:var(--accent)"></i>':'<i class="ti ti-sort-descending" style="font-size:10px;color:var(--accent)"></i>';
    };
    const filterTag=(field)=>{const f=clubColFilters[field];if(!f)return'';const lbl=f.type==='empty'?'empty':f.type==='notempty'?'filled':`"${f.value}"`;return `<div style="font-size:9px;color:var(--accent);font-weight:600;margin-top:1px">${lbl}</div>`;};
    const thBtn=(field,label)=>`<div class="col-header">${label}<button class="col-filter-btn ${clubColFilters[field]?'active':''}" onclick="App.openClubColFilter('${field}',this)"><i class="ti ti-filter"></i></button><button style="background:none;border:none;cursor:pointer;padding:0 2px;font-size:10px" onclick="App.sortClubs('${field}')">${sortIcon(field)}</button></div>${filterTag(field)}`;
    el.innerHTML=`<div class="table-wrap"><table id="clubsTable">
      <thead><tr>
        <th class="cb-head"><input type="checkbox" id="clubSelectAllCb" onchange="App.toggleClubSelectAll(this.checked)" title="Select all"></th>
        <th data-col="club_name">${thBtn('club_name','Club')}</th>
        <th data-col="country">${thBtn('country','Country')}</th>
        <th data-col="contact">${thBtn('contact','Contact')}</th>
        <th data-col="email">${thBtn('email','Email')}</th>
        <th data-col="phone">${thBtn('phone','Phone')}</th>
        <th data-col="address">${thBtn('address','Address')}</th>
        <th data-col="region">${thBtn('region','Region')}</th>
        <th data-col="is_referee">${thBtn('is_referee','E-Referee')}</th>
        <th data-col="registered">Registered this event</th>
      </tr></thead>
      <tbody>${filtered.map(c=>{
        const {flag,iso}=getCountryFlag(c.country_name||c.country_code||'');
        const registeredTeam=state.teams.find(t=>t.sportdata_id===c.sportdata_id);
        const contactName=clubVal(c,'contact')||'—';
        const email=clubVal(c,'email');
        const phone=clubVal(c,'phone');
        const address=clubVal(c,'address');
        const sel=clubSelectedIds.has(c.id);
        const hlf=(field)=>{const f=clubColFilters[field];return f&&f.type==='empty'&&isEmptyVal(clubVal(c,field))?'style="background:#fff7e8"':'';};
        return `<tr class="${sel?'selected':''}" data-club-id="${c.id}">
          <td class="cb-cell" onclick="event.stopPropagation()">
            <input type="checkbox" ${sel?'checked':''} onchange="App.toggleClubRow('${c.id}',this.checked)">
          </td>
          <td data-col="club_name" onclick="${registeredTeam?`App.openPanel('${registeredTeam.id}')`:''}" style="${registeredTeam?'cursor:pointer':''}"><div class="td-name">${esc(c.club_name)}</div><div class="td-abbr">${esc(c.abbreviation||'')} · ID ${esc(c.sportdata_id||'')}</div></td>
          <td data-col="country" onclick="${registeredTeam?`App.openPanel('${registeredTeam.id}')`:''}" ${hlf('country')}><div class="country-cell"><span class="flag-emoji" title="${esc(c.country_name||'')}">${flag}</span><span style="font-size:11px;font-family:var(--mono)">${esc(iso)}</span></div></td>
          <td data-col="contact" onclick="${registeredTeam?`App.openPanel('${registeredTeam.id}')`:''}" style="font-size:13px" ${hlf('contact')}>${esc(contactName)}</td>
          <td data-col="email" style="font-size:12px" ${hlf('email')}>${email?`<a href="mailto:${esc(email)}" style="color:var(--accent)">${esc(email)}</a>`:'<span style="color:var(--text-3)">—</span>'}</td>
          <td data-col="phone" onclick="${registeredTeam?`App.openPanel('${registeredTeam.id}')`:''}" style="font-size:12px;font-family:var(--mono)" ${hlf('phone')}>${esc(phone)||'<span style="color:var(--text-3)">—</span>'}</td>
          <td data-col="address" onclick="${registeredTeam?`App.openPanel('${registeredTeam.id}')`:''}" style="font-size:12px;color:var(--text-2)" ${hlf('address')}>${esc(address)||'<span style="color:var(--text-3)">—</span>'}</td>
          <td data-col="region" onclick="${registeredTeam?`App.openPanel('${registeredTeam.id}')`:''}" style="font-size:12px" ${hlf('region')}>${esc(c.region||'')||'<span style="color:var(--text-3)">—</span>'}</td>
          <td data-col="is_referee" style="text-align:center">${c.is_referee?'<span class="badge badge-blue"><i class="ti ti-check" style="font-size:10px"></i> Yes</span>':'<span style="color:var(--text-3);font-size:12px">—</span>'}</td>
          <td data-col="registered" onclick="event.stopPropagation()">${registeredTeam?`<div style="display:flex;align-items:center;gap:6px;cursor:pointer" onclick="App.openPanel('${registeredTeam.id}')"><span class="badge badge-green"><i class="ti ti-check"></i> Yes</span>${combinedStatusBadge(registeredTeam)}</div>`:'<span class="badge badge-grey">Not registered</span>'}</td>
        </tr>`;
      }).join('')}</tbody>
    </table></div>`;
    applyClubColVis();
    // Sync header checkbox
    const allCb = document.getElementById('clubSelectAllCb');
    if (allCb) {
      const rows = [...document.querySelectorAll('tr[data-club-id]')];
      const allChecked = rows.length > 0 && rows.every(r => clubSelectedIds.has(r.dataset.clubId));
      const someChecked = rows.some(r => clubSelectedIds.has(r.dataset.clubId));
      allCb.checked = allChecked;
      allCb.indeterminate = !allChecked && someChecked;
    }
    updateClubSelectionToolbar();
  }

  function updateClubSelectionToolbar() {
    const tb = document.getElementById('clubSelToolbar');
    const cnt = document.getElementById('clubSelCount');
    if (!tb) return;
    const n = clubSelectedIds.size;
    if (n > 0) {
      tb.classList.add('visible');
      if (cnt) cnt.textContent = `${n} club${n>1?'s':''} selected`;
    } else {
      tb.classList.remove('visible');
    }
  }

  function toggleClubRow(id, checked) {
    if (checked) clubSelectedIds.add(id); else clubSelectedIds.delete(id);
    const row = document.querySelector(`tr[data-club-id="${id}"]`);
    if (row) row.classList.toggle('selected', checked);
    updateClubSelectionToolbar();
    const allCb = document.getElementById('clubSelectAllCb');
    if (allCb) {
      const rows = [...document.querySelectorAll('tr[data-club-id]')];
      const allChecked = rows.length > 0 && rows.every(r => clubSelectedIds.has(r.dataset.clubId));
      const someChecked = rows.some(r => clubSelectedIds.has(r.dataset.clubId));
      allCb.checked = allChecked;
      allCb.indeterminate = !allChecked && someChecked;
    }
  }

  function toggleClubSelectAll(checked) {
    document.querySelectorAll('tr[data-club-id]').forEach(row => {
      const id = row.dataset.clubId;
      if (checked) clubSelectedIds.add(id); else clubSelectedIds.delete(id);
    });
    renderClubsPage();
    updateClubSelectionToolbar();
    const allCb = document.getElementById('clubSelectAllCb');
    if (allCb) { allCb.checked = checked; allCb.indeterminate = false; }
  }

  function clearClubSelection() {
    clubSelectedIds.clear();
    document.querySelectorAll('tr[data-club-id]').forEach(r => r.classList.remove('selected'));
    const allCb = document.getElementById('clubSelectAllCb');
    if (allCb) { allCb.checked = false; allCb.indeterminate = false; }
    updateClubSelectionToolbar();
  }

  async function archiveClubsSelected() {
    if (!clubSelectedIds.size) return;
    const ids = [...clubSelectedIds];
    showConfirm(
      `Archive ${ids.length} club${ids.length>1?'s':''}`,
      `Archive ${ids.length} club record(s) from the register? They will be hidden but can be restored from the Archived page.`,
      async () => {
        const now = new Date().toISOString();
        await sb.from('clubs').update({archived_at: now, updated_at: now}).in('id', ids);
        state.allClubs = state.allClubs.filter(c => !ids.includes(c.id));
        clearClubSelection();
        await loadClubsBadge();
        renderClubsPage();
        toast(`${ids.length} club${ids.length>1?'s':''} archived`, 'success');
      }
    );
  }

  function deleteClubsSelectedModal() {
    if (!clubSelectedIds.size) return;
    pendingDeleteIds = [...clubSelectedIds];
    const n = pendingDeleteIds.length;
    document.getElementById('deleteCountLabel').textContent = n;
    document.getElementById('deleteCountHint').textContent = n;
    document.getElementById('deleteCountInput').value = '';
    document.getElementById('deleteCountError').style.display = 'none';
    document.getElementById('btnConfirmDelete').disabled = true;
    // Override execute to delete from clubs table
    document.getElementById('btnConfirmDelete').onclick = async () => {
      const val = parseInt(document.getElementById('deleteCountInput').value);
      if (val !== pendingDeleteIds.length) return;
      await sb.from('clubs').delete().in('id', pendingDeleteIds);
      state.allClubs = state.allClubs.filter(c => !pendingDeleteIds.includes(c.id));
      clearClubSelection();
      closeDeleteModal();
      await loadClubsBadge();
      renderClubsPage();
      toast(`${pendingDeleteIds.length} club${pendingDeleteIds.length>1?'s':''} permanently deleted`, 'success');
      pendingDeleteIds = [];
    };
    document.getElementById('deleteConfirmModal').classList.add('open');
  }

  function sortClubs(field){if(clubSort.field===field)clubSort.dir=clubSort.dir==='asc'?'desc':'asc';else{clubSort.field=field;clubSort.dir='asc';}renderClubsPage();}

  function openClubColFilter(field,btn){
    document.querySelectorAll('.col-filter-pop').forEach(p=>p.remove());
    const current=clubColFilters[field]||{};
    const rect=btn.getBoundingClientRect();
    const labels={club_name:'Club name',country:'Country',contact:'Contact',email:'Email',phone:'Phone',address:'Address',region:'Region',is_referee:'E-Referee'};
    const pop=document.createElement('div');
    pop.className='col-filter-pop';
    const left=Math.min(rect.left,window.innerWidth-256);
    pop.style.cssText=`top:${rect.bottom+6}px;left:${left}px`;
    pop.innerHTML=`
      <div class="col-filter-pop-title">Filter: ${labels[field]||field}</div>
      <div class="col-filter-presets">
        <button class="col-filter-preset ${current.type==='empty'?'active':''}" onclick="App.applyClubColFilter('${field}','empty','')">Is empty / —</button>
        <button class="col-filter-preset ${current.type==='notempty'?'active':''}" onclick="App.applyClubColFilter('${field}','notempty','')">Is filled</button>
      </div>
      <input id="clubColFilterInput" type="text" placeholder="Contains…" value="${current.type==='contains'?esc(current.value||''):''}"
        oninput="App.previewClubColFilter('${field}',this.value)"
        onkeydown="if(event.key==='Enter')App.applyClubColFilter('${field}','contains',this.value)">
      <div class="col-filter-pop-footer">
        <button class="btn btn-ghost btn-sm" onclick="App.clearClubColFilter('${field}')"><i class="ti ti-x"></i> Clear</button>
        <button class="btn btn-primary btn-sm" onclick="App.applyClubColFilter('${field}','contains',document.getElementById('clubColFilterInput').value)"><i class="ti ti-check"></i> Apply</button>
      </div>`;
    document.getElementById('dropdownContainer').appendChild(pop);
    setTimeout(()=>{const inp=document.getElementById('clubColFilterInput');if(inp)inp.focus();},30);
    setTimeout(()=>{document.addEventListener('click',function closePop(e){if(!pop.contains(e.target)&&!btn.contains(e.target)){pop.remove();document.removeEventListener('click',closePop);}});},10);
  }

  function previewClubColFilter(field,value){if(value.trim())clubColFilters[field]={type:'contains',value};else delete clubColFilters[field];renderClubsPage();}
  function applyClubColFilter(field,type,value){document.querySelectorAll('.col-filter-pop').forEach(p=>p.remove());if(type==='empty'||type==='notempty')clubColFilters[field]={type};else if(value&&value.trim())clubColFilters[field]={type,value:value.trim()};else delete clubColFilters[field];renderClubsPage();}
  function clearClubColFilter(field){document.querySelectorAll('.col-filter-pop').forEach(p=>p.remove());delete clubColFilters[field];renderClubsPage();}
  function clearAllClubFilters(){clubColFilters={};renderClubsPage();}

  function openClubColToggle(btn){
    document.querySelectorAll('.col-toggle-pop').forEach(p=>p.remove());
    const rect=btn.getBoundingClientRect();
    const pop=document.createElement('div');
    pop.className='col-toggle-pop';
    pop.style.cssText=`top:${rect.bottom+6}px;right:${window.innerWidth-rect.right}px`;
    pop.innerHTML=`<div class="col-toggle-pop-title">Show / hide columns</div>${CLUB_COLS.map(c=>`<label class="col-toggle-item"><input type="checkbox" ${!clubHiddenCols.has(c.key)?'checked':''} ${c.required?'disabled':''} onchange="App.toggleClubCol('${c.key}',this.checked)">${c.label}${c.required?' <span style="font-size:10px;color:var(--text-3)">(always visible)</span>':''}</label>`).join('')}<div class="col-toggle-pop-footer"><button class="btn btn-ghost btn-sm" onclick="App.resetClubCols()"><i class="ti ti-refresh"></i> Reset</button></div>`;
    document.getElementById('dropdownContainer').appendChild(pop);
    setTimeout(()=>{document.addEventListener('click',function closePop(e){if(!pop.contains(e.target)&&!btn.contains(e.target)){pop.remove();document.removeEventListener('click',closePop);}});},10);
  }

  function toggleClubCol(col,visible){if(visible)clubHiddenCols.delete(col);else clubHiddenCols.add(col);saveClubColVis();applyClubColVis();}
  function resetClubCols(){clubHiddenCols.clear();saveClubColVis();applyClubColVis();document.querySelectorAll('.col-toggle-pop').forEach(p=>p.remove());}

  function showImportBanner(added, flagged, skipped, label='Entry payments') {
    document.getElementById('bannerContainer').innerHTML = `
      <div class="banner banner-blue"><i class="ti ti-upload"></i>
        <span>${label} import — <strong>${added} new</strong>, <strong>${flagged} flagged</strong>, <strong>${skipped} skipped/updated</strong></span>
        <button class="banner-close" onclick="this.parentElement.remove()"><i class="ti ti-x"></i></button>
      </div>`;
  }

  // Club data diff modal (contact fields)
  function showClubDiffModal() {
    const totalChanges = pendingDiffs.reduce((n,d)=>n+d.changes.length,0);
    document.getElementById('diffContent').innerHTML = `
      <p style="font-size:13px;color:var(--text-2);margin-bottom:12px">The following contact fields differ from what's currently stored. Confirm each change individually, per team, or all at once.</p>
      <div style="display:flex;justify-content:flex-end;margin-bottom:16px">
        <button class="btn btn-primary btn-sm" id="btnApplyAllTeams" onclick="App.confirmApplyAllTeams()">
          <i class="ti ti-checks"></i> Apply all changes (${totalChanges} fields across ${pendingDiffs.length} teams)
        </button>
      </div>
      <div id="diffTeamsList">
      ${pendingDiffs.map((d,i)=>`
        <div id="diffBlock_${i}" style="margin-bottom:16px;padding:14px;background:var(--surface-2);border-radius:var(--radius);border:1px solid var(--border);transition:border-color .2s">
          <div style="display:flex;align-items:center;gap:10px;margin-bottom:10px">
            <div style="font-weight:600;font-size:14px;flex:1">${esc(d.teamName)}</div>
            <span id="diffTeamDone_${i}" style="display:none" class="badge badge-green"><i class="ti ti-check"></i> All applied</span>
          </div>
          ${d.changes.length?`
            <table class="diff-table" style="margin-bottom:10px">
              <thead><tr><th>Field</th><th>Current</th><th>From CSV</th><th style="width:110px"></th></tr></thead>
              <tbody id="diffRows_${i}">${d.changes.map((c,ci)=>`
                <tr id="diffRow_${i}_${ci}">
                  <td style="font-family:var(--mono);font-size:11px">${c.field.replace(/_/g,' ')}</td>
                  <td><span class="diff-old">${esc(String(c.old||'—'))}</span></td>
                  <td><span class="diff-new" id="diffNewVal_${i}_${ci}">${esc(String(c.new||''))}</span></td>
                  <td>
                    <button id="diffBtn_${i}_${ci}" class="btn btn-sm btn-primary" onclick="App.confirmClubDiffChange(${i},${ci})">
                      <i class="ti ti-check"></i> Apply
                    </button>
                  </td>
                </tr>`).join('')}
              </tbody>
            </table>
            <button id="diffAllBtn_${i}" class="btn btn-sm btn-default" onclick="App.confirmAllClubDiff(${i})">
              <i class="ti ti-checks"></i> Apply all for this team
            </button>
          `:'<p style="font-size:12px;color:var(--text-3)">No contact field changes.</p>'}
        </div>`).join('')}
      </div>`;
    document.getElementById('diffModal').classList.add('open');
  }

  function markDiffRowDone(dIdx, cIdx) {
    const btn = document.getElementById(`diffBtn_${dIdx}_${cIdx}`);
    if (btn) {
      btn.disabled = true;
      btn.style.background = 'var(--green)';
      btn.style.borderColor = 'var(--green)';
      btn.innerHTML = '<i class="ti ti-check"></i> Applied';
    }
    const row = document.getElementById(`diffRow_${dIdx}_${cIdx}`);
    if (row) row.style.opacity = '0.5';
  }

  function markDiffTeamDone(dIdx) {
    const block = document.getElementById(`diffBlock_${dIdx}`);
    if (block) {
      block.style.borderColor = 'var(--green)';
      block.style.background = '#f0fdf4';
    }
    const badge = document.getElementById(`diffTeamDone_${dIdx}`);
    if (badge) badge.style.display = 'inline-flex';
    const allBtn = document.getElementById(`diffAllBtn_${dIdx}`);
    if (allBtn) {
      allBtn.disabled = true;
      allBtn.style.opacity = '0.5';
    }
  }

  async function confirmClubDiffChange(dIdx, cIdx) {
    const d=pendingDiffs[dIdx]; const c=d.changes[cIdx];
    const btn = document.getElementById(`diffBtn_${dIdx}_${cIdx}`);
    if (btn) { btn.disabled=true; btn.innerHTML='<div class="spinner" style="width:12px;height:12px;border-width:1.5px"></div>'; }
    await sb.from('teams').update({[c.field]:c.new,updated_at:new Date().toISOString()}).eq('id',d.teamId);
    await logActivity(d.teamId,d.teamName,'status_changed',`${c.field} updated via Club data CSV`,String(c.old),String(c.new));
    const t=state.teams.find(t=>t.id===d.teamId); if(t) t[c.field]=c.new;
    c._applied = true;
    markDiffRowDone(dIdx, cIdx);
    // Check if all rows in this team are now applied
    if (d.changes.every(ch=>ch._applied)) markDiffTeamDone(dIdx);
    renderTable();
  }

  async function confirmAllClubDiff(dIdx) {
    const d=pendingDiffs[dIdx];
    const allBtn = document.getElementById(`diffAllBtn_${dIdx}`);
    if (allBtn) { allBtn.disabled=true; allBtn.innerHTML='<div class="spinner" style="width:12px;height:12px;border-width:1.5px;display:inline-block"></div> Applying…'; }
    const update = {};
    d.changes.forEach(c=>update[c.field]=c.new);
    update.updated_at=new Date().toISOString();
    await sb.from('teams').update(update).eq('id',d.teamId);
    await logActivity(d.teamId,d.teamName,'status_changed',`${d.changes.length} fields updated via Club data CSV`,'','');
    const t=state.teams.find(t=>t.id===d.teamId); if(t) Object.assign(t,update);
    d.changes.forEach((c,ci)=>{ c._applied=true; markDiffRowDone(dIdx,ci); });
    markDiffTeamDone(dIdx);
    renderTable();
  }

  function confirmApplyAllTeams() {
    const total = pendingDiffs.reduce((n,d)=>n+d.changes.length,0);
    showConfirm(
      'Apply all changes?',
      `This will apply ${total} field update${total!==1?'s':''} across ${pendingDiffs.length} team${pendingDiffs.length!==1?'s':''}. Are you sure you want to continue?`,
      async () => {
        const applyAllBtn = document.getElementById('btnApplyAllTeams');
        if (applyAllBtn) { applyAllBtn.disabled=true; applyAllBtn.innerHTML='<div class="spinner" style="width:14px;height:14px;border-width:1.5px;display:inline-block"></div> Applying all…'; }
        for (let i=0; i<pendingDiffs.length; i++) {
          await confirmAllClubDiff(i);
        }
        toast(`All changes applied — ${total} fields updated`, 'success');
        if (applyAllBtn) { applyAllBtn.innerHTML=`<i class="ti ti-checks"></i> All ${total} changes applied`; applyAllBtn.style.background='var(--green)'; applyAllBtn.style.borderColor='var(--green)'; }
      }
    );
  }

  // ── SELECTION ──
  function toggleRow(id, checked) {
    if (checked) selectedIds.add(id); else selectedIds.delete(id);
    const row = document.querySelector(`tr[data-id="${id}"]`);
    if (row) row.classList.toggle('selected', checked);
    updateSelectionToolbar();
    // sync header checkbox
    const allCb = document.getElementById('selectAllCb');
    if (allCb) {
      const rows = [...document.querySelectorAll('tbody tr[data-id]')];
      const allChecked = rows.length > 0 && rows.every(r => selectedIds.has(r.dataset.id));
      const someChecked = rows.some(r => selectedIds.has(r.dataset.id));
      allCb.checked = allChecked;
      allCb.indeterminate = !allChecked && someChecked;
    }
  }

  function toggleSelectAll(checked) {
    // Set state first, then re-render rows with correct checked state
    document.querySelectorAll('tbody tr[data-id]').forEach(row => {
      const id = row.dataset.id;
      if (checked) selectedIds.add(id); else selectedIds.delete(id);
    });
    // Re-render table so all checkboxes reflect state
    renderTable();
    updateSelectionToolbar();
    // After re-render, force header state
    const allCb = document.getElementById('selectAllCb');
    if (allCb) { allCb.checked = checked; allCb.indeterminate = false; }
  }

  function clearSelection() {
    selectedIds.clear();
    document.querySelectorAll('tbody tr[data-id]').forEach(r=>r.classList.remove('selected'));
    const allCb = document.getElementById('selectAllCb');
    if (allCb) allCb.checked = false;
    updateSelectionToolbar();
  }

  function updateSelectionToolbar() {
    const toolbar = document.getElementById('selToolbar');
    const count = document.getElementById('selCount');
    if (!toolbar) return;
    const n = selectedIds.size;
    if (n > 0) {
      toolbar.classList.add('visible');
      count.textContent = `${n} team${n>1?'s':''} selected`;
    } else {
      toolbar.classList.remove('visible');
    }
  }

  // ── ARCHIVE ──
  async function archiveSelected() {
    if (!selectedIds.size) return;
    const ids = [...selectedIds];
    const names = ids.map(id=>state.teams.find(t=>t.id===id)?.team_name).filter(Boolean).join(', ');
    showConfirm(
      `Archive ${ids.length} team${ids.length>1?'s':''}`,
      `Archive ${ids.length} team record(s)? They will be hidden from the main view but can be restored from the Archived page.`,
      async () => {
        const now = new Date().toISOString();
        await sb.from('teams').update({archived_at:now,updated_at:now}).in('id',ids);
        for (const id of ids) {
          const t = state.teams.find(t=>t.id===id);
          if (t) await logActivity(id,t.team_name,'status_changed','Team archived','active','archived');
        }
        state.teams = state.teams.filter(t=>!ids.includes(t.id));
        clearSelection();
        renderTable(); renderKPIs(); updateBadges();
        // Refresh archived badge
        await loadArchivedBadge();
        toast(`${ids.length} team${ids.length>1?'s':''} archived`, 'success');
      }
    );
  }

  async function loadArchivedBadge() {
    const { count } = await sb.from('teams').select('id',{count:'exact',head:true})
      .eq('event_id',state.currentEventId).not('archived_at','is',null);
    const el = document.getElementById('badgeArchived');
    if (el) { el.textContent=count||0; el.style.display=count>0?'inline':'none'; }
  }

  // ── DELETE ──
  function deleteSelectedModal() {
    if (!selectedIds.size) return;
    pendingDeleteIds = [...selectedIds];
    const n = pendingDeleteIds.length;
    document.getElementById('deleteCountLabel').textContent = n;
    document.getElementById('deleteCountHint').textContent = n;
    document.getElementById('deleteCountInput').value = '';
    document.getElementById('deleteCountError').style.display = 'none';
    document.getElementById('btnConfirmDelete').disabled = true;
    document.getElementById('deleteConfirmModal').classList.add('open');
  }

  function validateDeleteCount() {
    const val = parseInt(document.getElementById('deleteCountInput').value);
    const n = pendingDeleteIds.length;
    const match = val === n;
    document.getElementById('btnConfirmDelete').disabled = !match;
    document.getElementById('deleteCountError').style.display = (!match && document.getElementById('deleteCountInput').value) ? 'block' : 'none';
  }

  function closeDeleteModal() {
    document.getElementById('deleteConfirmModal').classList.remove('open');
    pendingDeleteIds = [];
  }

  async function executeDelete() {
    if (!pendingDeleteIds.length) return;
    const ids = [...pendingDeleteIds];
    // Delete activity logs first (FK), then teams
    await sb.from('activity_log').delete().in('team_id', ids);
    await sb.from('teams').delete().in('id', ids);
    state.teams = state.teams.filter(t=>!ids.includes(t.id));
    clearSelection();
    closeDeleteModal();
    renderTable(); renderKPIs(); updateBadges();
    await loadArchivedBadge();
    toast(`${ids.length} team${ids.length>1?'s':''} permanently deleted`, 'success');
  }

  // ── ARCHIVED PAGE ──
  async function renderArchivedPage() {
    if (!state.currentEventId) return;
    archiveTab = archiveTab || 'archived';
    document.querySelectorAll('[data-archivetab]').forEach(b=>b.classList.toggle('active',b.dataset.archivetab===archiveTab));

    let query = sb.from('teams').select('*').eq('event_id',state.currentEventId);
    if (archiveTab === 'archived') {
      query = query.not('archived_at','is',null).is('deleted_at',null);
    } else {
      // "Recently deleted" — we show soft-delete records; for permanently deleted we can't show them
      // So we repurpose this tab to show expired teams as a reference
      query = query.eq('payment_status','expired').is('archived_at',null);
    }
    const { data } = await query.order('updated_at',{ascending:false});
    archivedTeams = data || [];

    const el = document.getElementById('archivedContent');
    if (!archivedTeams.length) {
      el.innerHTML = `<div class="empty-state" style="background:var(--surface);border:1px solid var(--border);border-radius:var(--radius-lg);padding:60px 20px;text-align:center">
        <i class="ti ti-archive" style="font-size:40px;color:var(--text-3);display:block;margin-bottom:12px"></i>
        <strong style="color:var(--text-2);font-size:15px">${archiveTab==='archived'?'No archived teams':'No expired teams'}</strong>
        <p style="color:var(--text-3);font-size:13px;margin-top:4px">${archiveTab==='archived'?'Archived teams appear here.':'Teams marked as expired appear here.'}</p>
      </div>`;
      return;
    }

    const selIds = new Set();
    el.innerHTML = `
      <div class="sel-toolbar" id="archiveSelToolbar" style="display:none;margin-bottom:12px">
        <i class="ti ti-checkbox" style="color:var(--accent);font-size:16px"></i>
        <span class="sel-count" id="archiveSelCount">0 selected</span>
        <span class="sel-spacer"></span>
        ${archiveTab==='archived'?`<button class="btn btn-sm btn-default" onclick="App.restoreSelected()"><i class="ti ti-refresh"></i> Restore</button>`:''}
        <button class="btn btn-sm btn-delete" onclick="App.deleteArchivedModal()"><i class="ti ti-trash"></i> Delete permanently</button>
        <button class="btn btn-sm btn-ghost" onclick="App.clearArchiveSelection()"><i class="ti ti-x"></i></button>
      </div>
      <div class="table-wrap"><table>
        <thead><tr>
          <th class="cb-head"><input type="checkbox" id="archiveSelectAll" onchange="App.toggleArchiveSelectAll(this.checked)"></th>
          <th>Team</th><th>Country</th><th>Balance</th><th>Status</th>
          <th>${archiveTab==='archived'?'Archived on':'Expired'}</th><th></th>
        </tr></thead>
        <tbody>${archivedTeams.map(t=>`
          <tr data-archive-id="${t.id}" class="">
            <td class="cb-cell" onclick="event.stopPropagation()">
              <input type="checkbox" onchange="App.toggleArchiveRow('${t.id}',this.checked)">
            </td>
            <td><div class="td-name">${esc(t.team_name)}</div><div class="td-abbr">${esc(t.team_abbreviation||'')} · ${esc(t.country||'')}</div></td>
            <td>${esc(t.country||'—')}</td>
            <td class="td-balance ${t.balance_eur>0?'amber':'green'}">€${fmtMoney(t.balance_eur)}</td>
            <td>${combinedStatusBadge(t)}</td>
            <td style="font-size:12px;color:var(--text-3)">${fmtDate(archiveTab==='archived'?t.archived_at:t.updated_at)}</td>
            <td onclick="event.stopPropagation()" style="position:relative">
              ${archiveTab==='archived'?`
                <button class="actions-btn" onclick="App.restoreSingle('${t.id}')"><i class="ti ti-refresh"></i></button>
              `:``}
            </td>
          </tr>`).join('')}
        </tbody>
      </table></div>`;
  }

  function switchArchiveTab(tab) {
    archiveTab = tab;
    renderArchivedPage();
  }

  let archiveSelectedIds = new Set();

  function toggleArchiveRow(id, checked) {
    if (checked) archiveSelectedIds.add(id); else archiveSelectedIds.delete(id);
    updateArchiveToolbar();
  }

  function toggleArchiveSelectAll(checked) {
    document.querySelectorAll('tr[data-archive-id]').forEach(row=>{
      const id=row.dataset.archiveId;
      if(checked) archiveSelectedIds.add(id); else archiveSelectedIds.delete(id);
      row.querySelector('input[type=checkbox]').checked=checked;
    });
    updateArchiveToolbar();
  }

  function clearArchiveSelection() {
    archiveSelectedIds.clear();
    document.querySelectorAll('tr[data-archive-id] input[type=checkbox]').forEach(cb=>cb.checked=false);
    document.getElementById('archiveSelectAll').checked=false;
    updateArchiveToolbar();
  }

  function updateArchiveToolbar() {
    const tb=document.getElementById('archiveSelToolbar');
    const cnt=document.getElementById('archiveSelCount');
    if(!tb) return;
    const n=archiveSelectedIds.size;
    tb.style.display=n>0?'flex':'none';
    if(cnt) cnt.textContent=`${n} team${n>1?'s':''} selected`;
  }

  async function restoreSingle(id) {
    const t = archivedTeams.find(t=>t.id===id);
    await sb.from('teams').update({archived_at:null,updated_at:new Date().toISOString()}).eq('id',id);
    if(t) await logActivity(id,t.team_name,'status_changed','Team restored from archive','archived','active');
    toast('Team restored','success');
    await loadTeams();
    renderArchivedPage();
  }

  async function restoreSelected() {
    if(!archiveSelectedIds.size) return;
    const ids=[...archiveSelectedIds];
    await sb.from('teams').update({archived_at:null,updated_at:new Date().toISOString()}).in('id',ids);
    for(const id of ids){
      const t=archivedTeams.find(t=>t.id===id);
      if(t) await logActivity(id,t.team_name,'status_changed','Team restored from archive','archived','active');
    }
    clearArchiveSelection();
    toast(`${ids.length} team${ids.length>1?'s':''} restored`,'success');
    await loadTeams();
    renderArchivedPage();
  }

  function deleteArchivedModal() {
    if(!archiveSelectedIds.size) return;
    pendingDeleteIds=[...archiveSelectedIds];
    const n=pendingDeleteIds.length;
    document.getElementById('deleteCountLabel').textContent=n;
    document.getElementById('deleteCountHint').textContent=n;
    document.getElementById('deleteCountInput').value='';
    document.getElementById('deleteCountError').style.display='none';
    document.getElementById('btnConfirmDelete').disabled=true;
    document.getElementById('deleteConfirmModal').classList.add('open');
  }

  // ── PAYMENT RECONCILIATION REVIEW ──
  let payReviewApplied = [];
  let payReviewReminders = [];
  let payReviewStaleLinks = [];
  let payReviewAdjustConflicts = [];

  function showPaymentReviewModal(applied, reminders, stale, conflicts) {
    payReviewApplied = applied.slice();
    payReviewReminders = reminders.slice();
    payReviewStaleLinks = (stale||[]).slice();
    payReviewAdjustConflicts = (conflicts||[]).slice();
    renderPayReview();
    document.getElementById('payReviewModal').classList.add('open');
  }

  function closePayReviewModal() {
    document.getElementById('payReviewModal').classList.remove('open');
  }

  function renderPayReview() {
    const el = document.getElementById('payReviewContent');
    const applied = payReviewApplied.filter(p => !p.undone);
    const reminders = payReviewReminders.filter(r => !r.resolved);
    const parts = [];

    // Renders "old -> new" without implying the old value was deleted.
    const money = v => '\u20ac' + fmtMoney(v);
    const trans = (from, to, goodWhenZero) => {
      const cls = goodWhenZero ? (Number(to) <= 0 ? 'val-good' : 'val-open') : 'val-good';
      return `<span class="val-before">${money(from)}</span>`
           + `<span class="val-arrow">&rarr;</span>`
           + `<span class="val-after ${cls}">${money(to)}</span>`;
    };

    if (applied.length) {
      const sum = applied.reduce((s,p)=>s+(p.to-p.from),0);
      parts.push(`
        <div style="margin-bottom:20px">
          <div style="font-size:13px;font-weight:600;color:var(--text-1);margin-bottom:4px">
            <i class="ti ti-check" style="color:var(--green)"></i> Payments recorded from Sportdata (${applied.length})
          </div>
          <div style="font-size:12px;color:var(--text-3);margin-bottom:10px;line-height:1.6">
            Sportdata reported money received that the CRM did not have yet.
            These amounts have been added, so the outstanding balance goes down.
            Total received: <strong style="color:var(--green)">${money(sum)}</strong>.
          </div>
          <table class="diff-table">
            <thead><tr>
              <th>Team</th><th>Entry fee</th><th>Amount paid</th><th>Still to pay</th><th></th>
            </tr></thead>
            <tbody>${applied.map(p => {
              const i = payReviewApplied.indexOf(p);
              const t = state.teams.find(x => x.id === p.teamId);
              const fee = t ? Number(t.entry_fee_eur||0) - Number(t.discount_eur||0) : 0;
              const nowBal = t ? Number(t.balance_eur||0) : 0;
              return `<tr>
                <td>${esc(p.teamName)} ${t?combinedStatusBadge(t):''}</td>
                <td><span class="val-before">${money(fee)}</span></td>
                <td>${trans(p.from, p.to, false)}</td>
                <td>${trans(p.prevBalance, nowBal, true)}</td>
                <td><button class="btn btn-sm btn-ghost" onclick="App.undoAppliedPayment(${i})"><i class="ti ti-arrow-back-up"></i> Undo</button></td>
              </tr>`;
            }).join('')}</tbody>
          </table>
        </div>`);
    }

    if (reminders.length) {
      parts.push(`
        <div>
          <div style="font-size:13px;font-weight:600;color:var(--text-1);margin-bottom:4px">
            <i class="ti ti-alert-triangle" style="color:var(--amber)"></i> Sportdata is missing a payment (${reminders.length})
          </div>
          <div style="font-size:12px;color:var(--text-3);margin-bottom:10px;line-height:1.6">
            The CRM has recorded more money than Sportdata knows about, almost always a Stripe payment
            you have not entered yet. <strong>Nothing was changed.</strong>
            Enter these amounts in Sportdata under Amount Paid EUR, then mark them off here.
          </div>
          <table class="diff-table">
            <thead><tr>
              <th>Team</th><th>Recorded in CRM</th><th>Sportdata shows</th><th>To enter</th><th></th>
            </tr></thead>
            <tbody>${reminders.map(r => {
              const i = payReviewReminders.indexOf(r);
              const gap = Math.round((r.crm - r.csv) * 100) / 100;
              return `<tr>
                <td>${esc(r.teamName)}</td>
                <td><span class="val-after val-good">${money(r.crm)}</span></td>
                <td><span class="val-before">${money(r.csv)}</span></td>
                <td><span class="val-after val-open">${money(gap)}</span></td>
                <td style="white-space:nowrap">
                  <button class="btn btn-sm btn-default" onclick="App.dismissPaymentReminder(${i})" title="Remove from this list, change nothing"><i class="ti ti-check"></i> Entered</button>
                  <button class="btn btn-sm btn-ghost" onclick="App.applyCsvPaymentAnyway(${i})" title="Lower the CRM to the Sportdata amount"><i class="ti ti-arrow-down"></i> Use Sportdata</button>
                </td>
              </tr>`;
            }).join('')}</tbody>
          </table>
        </div>`);
    }

    const stale = payReviewStaleLinks.filter(s => !s.done);
    if (stale.length) {
      const hasKey = stripeConfigured;
      const ids = stale.map(s => s.teamId);
      parts.push(`
        <div style="margin-top:${parts.length?'20px':'0'}">
          <div style="font-size:13px;font-weight:600;color:var(--text-1);margin-bottom:4px">
            <i class="ti ti-link-off" style="color:var(--amber)"></i> Payment links out of date (${stale.length})
          </div>
          <div style="font-size:12px;color:var(--text-3);margin-bottom:10px;line-height:1.6">
            The outstanding amount changed while a Stripe link is still live for the old amount.
            Regenerating creates a link for the new amount and <strong>deactivates the old one</strong>,
            so nobody can pay the wrong total. The club will need the new link, so send them an email afterwards.
          </div>
          ${hasKey ? `<button class="btn btn-primary btn-sm" style="margin-bottom:10px"
              onclick='App.regenerateStaleLinks(${JSON.stringify(ids)})'>
              <i class="ti ti-refresh"></i> Regenerate all ${stale.length}
            </button>` : `<div class="banner banner-amber" style="margin-bottom:10px">
              <i class="ti ti-alert-triangle"></i> No Stripe key configured. Add it in Settings to regenerate links.
            </div>`}
          <table class="diff-table">
            <thead><tr><th>Team</th><th>Link is for</th><th>Should be</th><th></th></tr></thead>
            <tbody>${stale.map(s => `<tr>
              <td>${esc(s.teamName)}</td>
              <td><span class="val-before">${money(s.oldAmount)}</span></td>
              <td><span class="val-after val-open">${money(s.newAmount)}</span></td>
              <td>${hasKey ? `<button class="btn btn-sm btn-default" onclick='App.regenerateStaleLinks(["${s.teamId}"])'>
                    <i class="ti ti-refresh"></i> Regenerate</button>` : ''}</td>
            </tr>`).join('')}</tbody>
          </table>
        </div>`);
    }

    // Sportdata repriced a team that carries a manual correction. Nothing is
    // changed automatically here: only a human knows whether the correction
    // still applies to the new fee.
    const conflicts = payReviewAdjustConflicts.filter(c => !c.done);
    if (conflicts.length) {
      parts.push(`
        <div style="margin-top:${parts.length?'20px':'0'}">
          <div style="font-size:13px;font-weight:600;color:var(--text-1);margin-bottom:4px">
            <i class="ti ti-alert-triangle" style="color:var(--red)"></i> Manual corrections need review (${conflicts.length})
          </div>
          <div style="font-size:12px;color:var(--text-3);margin-bottom:10px;line-height:1.6">
            Sportdata changed the entry fee for these teams while a manual correction is active.
            The correction was <strong>kept as it is</strong> and nothing was recalculated automatically,
            because only you can tell whether the agreed amount still holds. Open each team and confirm or change the correction.
          </div>
          <table class="diff-table">
            <thead><tr><th>Team</th><th>Marking</th><th>Fee was</th><th>Fee now</th><th>Correction</th><th></th></tr></thead>
            <tbody>${conflicts.map(c => `<tr>
              <td>${esc(c.teamName)}</td>
              <td>${c.settlement==='written_off'?'<span class="badge badge-grey">Written off</span>':'<span class="badge badge-purple">Adjusted</span>'}</td>
              <td><span class="val-before">${money(c.oldFee)}</span></td>
              <td><span class="val-after val-open">${money(c.newFee)}</span></td>
              <td><span class="val-before">${money(c.adjustment)}</span></td>
              <td><button class="btn btn-sm btn-default" onclick="App.closePayReviewModal();App.openPanel('${c.teamId}')">
                <i class="ti ti-external-link"></i> Open</button></td>
            </tr>`).join('')}</tbody>
          </table>
        </div>`);
    }

    el.innerHTML = parts.length
      ? parts.join('')
      : '<p style="font-size:13px;color:var(--text-3);text-align:center;padding:24px 0">All payments reconciled.</p>';
  }

  async function undoAppliedPayment(i) {
    const p = payReviewApplied[i];
    if (!p || p.undone) return;
    const team = state.teams.find(t => t.id === p.teamId);
    if (!team) return;
    const fee = Number(team.entry_fee_eur||0), disc = Number(team.discount_eur||0);
    const adj = Number(team.manual_adjustment_eur||0);
    const upd = {
      amount_paid_eur: p.from,
      balance_eur: Math.max(0, Math.round((fee - disc - adj - p.from)*100)/100),
      payment_status: deriveStatus(fee, disc, p.from, adj),
      updated_at: new Date().toISOString()
    };
    const { error } = await sb.from('teams').update(upd).eq('id', p.teamId);
    if (error) { toast('Undo failed: '+error.message,'error'); return; }
    Object.assign(team, upd);
    await logActivity(p.teamId, p.teamName, 'payment_received',
      `Import payment reverted by user — back to €${fmtMoney(p.from)}`,
      `€${fmtMoney(p.to)}`, `€${fmtMoney(p.from)}`);
    p.undone = true;
    renderPayReview(); renderTable(); renderKPIs(); updateBadges();
    if (document.getElementById('pageDashboard')?.classList.contains('active')) renderDashboard();
    toast('Reverted','success');
  }

  async function dismissPaymentReminder(i) {
    const r = payReviewReminders[i];
    if (!r || r.resolved) return;
    await logActivity(r.teamId, r.teamName, 'note_added',
      `Marked as entered in Sportdata — CRM holds €${fmtMoney(r.crm)}, CSV showed €${fmtMoney(r.csv)}`, '', '');
    r.resolved = true;
    renderPayReview();
  }

  async function applyCsvPaymentAnyway(i) {
    const r = payReviewReminders[i];
    if (!r || r.resolved) return;
    showConfirm('Lower the recorded payment',
      `${r.teamName} currently shows €${fmtMoney(r.crm)} paid in the CRM.\n\n`
      + `Applying the CSV amount lowers this to €${fmtMoney(r.csv)}. Only do this if the CRM figure was wrong, `
      + `for example after a refund or a typing error in Sportdata.\n\nContinue?`,
      async () => {
        const team = state.teams.find(t => t.id === r.teamId);
        if (!team) return;
        const fee = Number(team.entry_fee_eur||0), disc = Number(team.discount_eur||0);
        const adj = Number(team.manual_adjustment_eur||0);
        const upd = {
          amount_paid_eur: r.csv,
          balance_eur: Math.max(0, Math.round((fee - disc - adj - r.csv)*100)/100),
          payment_status: deriveStatus(fee, disc, r.csv, adj),
          updated_at: new Date().toISOString()
        };
        const { error } = await sb.from('teams').update(upd).eq('id', r.teamId);
        if (error) { toast('Update failed: '+error.message,'error'); return; }
        Object.assign(team, upd);
        await logActivity(r.teamId, r.teamName, 'payment_received',
          `Paid amount lowered to the Sportdata figure by user`,
          `€${fmtMoney(r.crm)}`, `€${fmtMoney(r.csv)}`);
        r.resolved = true;
        renderPayReview(); renderTable(); renderKPIs(); updateBadges();
        if (document.getElementById('pageDashboard')?.classList.contains('active')) renderDashboard();
        toast('CRM lowered to CSV amount','success');
      });
  }

  function showDiffModal() {
    document.getElementById('diffContent').innerHTML = pendingDiffs.map((d,i)=>`
      <div style="margin-bottom:16px;padding:14px;background:var(--surface-2);border-radius:var(--radius);border:1px solid var(--border)">
        <div style="font-weight:600;font-size:14px;margin-bottom:8px">${esc(d.teamName)}</div>
        ${d.paymentDetected?`
          <div class="banner banner-amber" style="margin-bottom:8px"><i class="ti ti-currency-euro"></i>Payment of €${fmtMoney(d.paymentDetected)} detected. Verify and confirm.</div>
          <button class="btn btn-sm btn-primary" onclick="App.confirmDiffPayment(${i})"><i class="ti ti-check"></i> Mark as paid</button>
        `:''}
        ${d.changes.length?`
          <table class="diff-table" style="margin-bottom:10px">
            <thead><tr><th>Field</th><th>Current</th><th>From CSV</th><th></th></tr></thead>
            <tbody>${d.changes.map((c,ci)=>`
              <tr><td>${c.field.replace('_eur',' (€)').replace(/_/g,' ')}</td>
              <td><span class="diff-old">€${fmtMoney(c.old)}</span></td>
              <td><span class="diff-new">€${fmtMoney(c.new)}</span></td>
              <td><button class="btn btn-sm btn-primary" onclick="App.confirmDiffChange(${i},${ci})"><i class="ti ti-check"></i> Apply</button></td></tr>`).join('')}
            </tbody></table>`:''}
      </div>`).join('');
    document.getElementById('diffModal').classList.add('open');
  }

  async function confirmDiffChange(dIdx, cIdx) {
    const d=pendingDiffs[dIdx]; const c=d.changes[cIdx];
    await sb.from('teams').update({[c.field]:c.new,updated_at:new Date().toISOString()}).eq('id',d.teamId);
    await logActivity(d.teamId,d.teamName,'balance_flagged',`${c.field} updated via CSV`,String(c.old),String(c.new));
    const t=state.teams.find(t=>t.id===d.teamId); if(t) t[c.field]=c.new;
    toast('Applied','success'); renderTable();
  }

  async function confirmDiffPayment(dIdx) {
    const d=pendingDiffs[dIdx];
    const t=state.teams.find(t=>t.id===d.teamId);
    const amount=Number(d.paymentDetected||0);
    const billed=t?Number(t.entry_fee_eur||0)-Number(t.discount_eur||0):0;
    const update={
      payment_status:'paid',
      amount_paid_eur:amount>0?amount:billed,
      balance_eur:Math.max(0,Math.round((billed-(amount>0?amount:billed))*100)/100),
      paid_at:new Date().toISOString(),
      updated_at:new Date().toISOString()
    };
    await sb.from('teams').update(update).eq('id',d.teamId);
    await logActivity(d.teamId,d.teamName,'payment_received',
      `Payment of €${fmtMoney(update.amount_paid_eur)} confirmed via CSV import`,
      t?`${t.payment_status} / €${fmtMoney(t.amount_paid_eur||0)}`:'',
      `paid / €${fmtMoney(update.amount_paid_eur)}`);
    if(t) Object.assign(t,update);
    toast('Marked as paid','success'); renderTable(); renderKPIs(); updateBadges();
    if(document.getElementById('pageDashboard')?.classList.contains('active')) renderDashboard();
  }

  function closeDiffModal() { document.getElementById('diffModal').classList.remove('open'); pendingDiffs=[]; }

  // ── ADD TEAM ──
  function openAddTeamModal() { if(!state.currentEventId){toast('Create an event first','error');return;} document.getElementById('addTeamModal').classList.add('open'); }
  function closeAddTeamModal() { document.getElementById('addTeamModal').classList.remove('open'); }

  async function saveNewTeam() {
    const name=document.getElementById('addTeamName').value.trim();
    const country=document.getElementById('addTeamCountry').value.trim().toUpperCase();
    if(!name||!country){toast('Name and country required','error');return;}
    const {data,error}=await sb.from('teams').insert([{
      event_id:state.currentEventId,team_name:name,team_abbreviation:document.getElementById('addTeamAbbr').value.trim(),
      country,sportdata_id:document.getElementById('addTeamSdId').value.trim(),
      contact_email:document.getElementById('addTeamEmail').value.trim(),contact_name:document.getElementById('addTeamContact').value.trim(),
      athlete_count:parseInt(document.getElementById('addTeamAthletes').value)||0,
      balance_eur:parseFloat(document.getElementById('addTeamBalance').value)||0,
      payment_status:'unpaid',reminder_stage:'none',reminder_dates:{},updated_at:new Date().toISOString()
    }]).select();
    if(error){toast('Error: '+error.message,'error');return;}
    state.teams.unshift(data[0]);
    await logActivity(data[0].id,name,'status_changed','Team added manually','','');
    closeAddTeamModal(); renderTable(); renderKPIs(); renderCountryFilter(); updateBadges();
    toast('Team added','success');
  }

  // ── STRIPE ──
  // Al het Stripe-verkeer loopt via de edge function 'stripe-proxy'. De secret
  // key staat op de server en komt nooit meer in de browser. De functie kent
  // alleen de handelingen die hieronder worden aangeroepen.
  let stripeConfigured = false;

  async function stripeCall(action, payload = {}) {
    const { data, error } = await sb.functions.invoke('stripe-proxy', {
      body: { action, ...payload }
    });
    if (error) {
      // De foutmelding van de functie zelf is bruikbaarder dan 'non-2xx status'.
      let melding = error.message || 'Stripe-aanroep mislukt';
      try {
        const body = await error.context?.json?.();
        if (body?.error) melding = body.error;
      } catch { /* body niet leesbaar, gebruik de generieke melding */ }
      throw new Error(melding);
    }
    if (data?.error) throw new Error(data.error);
    return data;
  }

  async function refreshStripeStatus() {
    try {
      const res = await stripeCall('status');
      stripeConfigured = !!res?.configured;
      return res;
    } catch {
      stripeConfigured = false;
      return null;
    }
  }

  // ── STRIPE CONNECT ──
  async function connectCall(action, payload = {}) {
    const { data, error } = await sb.functions.invoke('stripe-connect', { body: { action, ...payload } });
    if (error) {
      let melding = error.message || 'Aanroep mislukt';
      try {
        const body = await error.context?.json?.();
        if (body?.error) melding = body.error;
      } catch { /* niet leesbaar */ }
      throw new Error(melding);
    }
    if (data?.error) throw new Error(data.error);
    return data;
  }

  async function connectStripe() {
    try {
      const res = await connectCall('start', {
        redirect_uri: `${location.origin}${location.pathname}?stripe=return`
      });
      location.href = res.url;
    } catch (e) {
      toast(e.message, 'error');
    }
  }

  async function disconnectStripe() {
    showConfirm('Koppeling verbreken',
      'Nieuwe betaallinks zijn daarna niet meer te maken. Bestaande links blijven werken, die staan op jullie eigen Stripe-account.',
      async () => {
        try {
          await connectCall('disconnect');
          toast('Koppeling verbroken','success');
          await renderSettingsStripe();
        } catch (e) {
          toast(e.message,'error');
        }
      });
  }

  // Terugkeer van Stripe: de code uit de adresbalk inwisselen voor een
  // account-id. Dat gebeurt op de server, de browser ziet geen sleutels.
  async function verwerkStripeTerugkeer() {
    const params = new URLSearchParams(location.search);
    if (params.get('stripe') !== 'return') return;
    const code = params.get('code');
    const state = params.get('state');
    // Schoonmaken voordat we iets doen, anders blijft de code in de adresbalk
    // staan en in de geschiedenis van de browser.
    const schoon = location.pathname;
    if (!code || !state) {
      history.replaceState({}, '', schoon);
      toast('Koppeling afgebroken', 'warning');
      return;
    }
    try {
      const res = await connectCall('callback', { code, state });
      history.replaceState({}, '', schoon);
      toast(res.livemode ? 'Stripe gekoppeld' : 'Stripe gekoppeld, testmodus', 'success');
      nav('settings');
    } catch (e) {
      history.replaceState({}, '', schoon);
      toast('Koppelen mislukt: ' + e.message, 'error');
    }
  }

  function openStripeModal(teamId) {
    const team=state.teams.find(t=>t.id===teamId); if(!team) return;
    stripeTeamRef=team;
    document.getElementById('stripeTeamName').value=team.team_name;
    document.getElementById('stripeAmount').value=team.balance_eur||0;
    const ev=state.events.find(e=>e.id===state.currentEventId);
    document.getElementById('stripeDescription').value=`${ev?.name || state.currentOrg?.name || 'Toernooi'} — ${team.team_name}`;
    document.getElementById('stripeError').style.display='none';
    document.getElementById('stripeModal').classList.add('open');
  }
  function closeStripeModal() { document.getElementById('stripeModal').classList.remove('open'); stripeTeamRef=null; }

  async function generateStripeLink() {
    const amount=parseFloat(document.getElementById('stripeAmount').value);
    if(!amount||amount<=0){showStripeError('Amount must be greater than 0.');return;}
    const description=document.getElementById('stripeDescription').value.trim();
    const btn=document.getElementById('btnGenerateStripe');
    btn.disabled=true; btn.innerHTML='<div class="spinner"></div> Generating…';
    document.getElementById('stripeError').style.display='none';
    try {
      const linkData=await stripeCall('create_payment_link',{
        amount,
        description,
        team_name:stripeTeamRef.team_name,
        sportdata_id:stripeTeamRef.sportdata_id||'',
        event_id:state.currentEventId
      });
      await sb.from('teams').update({stripe_payment_link:linkData.url,stripe_payment_link_id:linkData.id,stripe_link_amount:amount,updated_at:new Date().toISOString()}).eq('id',stripeTeamRef.id);
      const t=state.teams.find(t=>t.id===stripeTeamRef.id);
      if(t){t.stripe_payment_link=linkData.url;t.stripe_payment_link_id=linkData.id;t.stripe_link_amount=amount;}
      await logActivity(stripeTeamRef.id,stripeTeamRef.team_name,'stripe_link_generated',`Stripe link generated — €${amount} — ${linkData.url}`,'',linkData.url);

      // Show success state inside modal before closing
      btn.disabled=false;
      btn.style.background='var(--green)';
      btn.style.borderColor='var(--green)';
      btn.innerHTML='<i class="ti ti-check"></i> Link generated!';
      const footer=btn.parentElement;
      const copyBtn=document.createElement('button');
      copyBtn.className='btn btn-default btn-sm';
      copyBtn.innerHTML='<i class="ti ti-copy"></i> Copy link';
      copyBtn.onclick=()=>{navigator.clipboard.writeText(linkData.url);App.toast('Link copied','success');};
      footer.insertBefore(copyBtn, btn);

      renderTable();
      if(currentTeamId===stripeTeamRef.id) await renderPanelBody(state.teams.find(t=>t.id===currentTeamId));
      toast('Stripe link generated — click Copy link or close','success');
      // Auto-close after 2.5s
      setTimeout(()=>{ if(document.getElementById('stripeModal').classList.contains('open')) closeStripeModal(); },2500);
    } catch(e){
      showStripeError(e.message);
      btn.disabled=false; btn.innerHTML='<i class="ti ti-bolt"></i> Generate link';
    }
    // Only reset button on error (success path handled above)
  }

  function showStripeError(msg) { document.getElementById('stripeErrorMsg').textContent=msg; document.getElementById('stripeError').style.display='flex'; }

  // ── STRIPE LINK REGENERATION ──
  // A Payment Link keeps its original amount forever and stays payable until
  // it is deactivated. When entries change, the old link therefore charges the
  // wrong amount, so it must be replaced and closed rather than left alongside.

  function stripeLinkIsStale(t) {
    if (!t.stripe_payment_link_id) return false;
    // A written off balance must never trigger a new payment link.
    if (t.payment_status === 'paid' || isWrittenOff(t)) return false;
    const bal = Number(t.balance_eur || 0);
    if (bal <= 0) return false;
    const linked = t.stripe_link_amount;
    if (linked === null || linked === undefined) return false; // pre-dates tracking
    return Math.abs(Number(linked) - bal) > 0.01;
  }

  async function createStripeLink(team, amount) {
    const ev = state.events.find(e => e.id === state.currentEventId);
    return await stripeCall('create_payment_link', {
      amount,
      description: `${ev?.name || state.currentOrg?.name || 'Toernooi'} \u2014 ${team.team_name}`,
      team_name: team.team_name,
      sportdata_id: team.sportdata_id || '',
      event_id: state.currentEventId
    });
  }

  async function deactivateStripeLink(linkId) {
    if (!linkId) return;
    try {
      await stripeCall('deactivate_payment_link', { payment_link_id: linkId });
    } catch (e) {
      console.warn('Could not deactivate old payment link', linkId, e);
    }
  }

  async function regenerateStripeLink(teamId, opts = {}) {
    const team = state.teams.find(t => t.id === teamId);
    if (!team) return 'error';
    if (isWrittenOff(team)) {
      if (!opts.silent) toast('This balance was written off — undo the write-off first','warning');
      return 'none';
    }
    const amount = Number(team.balance_eur || 0);
    if (amount <= 0) { if(!opts.silent) toast('Nothing outstanding for this team','warning'); return 'none'; }
    const oldId = team.stripe_payment_link_id;
    const oldAmount = team.stripe_link_amount;
    try {
      const link = await createStripeLink(team, amount);
      await deactivateStripeLink(oldId);
      const upd = {
        stripe_payment_link: link.url,
        stripe_payment_link_id: link.id,
        stripe_link_amount: amount,
        updated_at: new Date().toISOString()
      };
      const { error } = await sb.from('teams').update(upd).eq('id', teamId);
      if (error) throw new Error(error.message);
      Object.assign(team, upd);
      await logActivity(teamId, team.team_name, 'stripe_link_generated',
        `Payment link regenerated for \u20ac${fmtMoney(amount)} after import`
        + (oldAmount != null ? ` (was \u20ac${fmtMoney(oldAmount)})` : '')
        + ' \u00b7 previous link deactivated \u00b7 send the club the new link',
        oldAmount != null ? `\u20ac${fmtMoney(oldAmount)}` : '', `\u20ac${fmtMoney(amount)}`);
      if (!opts.silent) {
        renderTable();
        if (currentTeamId === teamId) await renderPanelBody(team);
        toast(`New link for \u20ac${fmtMoney(amount)}`,'success');
      }
      return 'ok';
    } catch (e) {
      if (!opts.silent) toast('Regeneration failed: ' + e.message, 'error');
      return 'error';
    }
  }

  async function regenerateStaleLinks(ids) {
    toast(`Regenerating ${ids.length} link${ids.length>1?'s':''}\u2026`);
    let ok = 0, failed = 0;
    for (const id of ids) {
      const res = await regenerateStripeLink(id, { silent:true });
      if (res === 'ok') ok++; else if (res === 'error') failed++;
    }
    payReviewStaleLinks.forEach(s => { if (ids.includes(s.teamId)) s.done = true; });
    renderPayReview(); renderTable();
    if (currentTeamId) { const t = state.teams.find(x=>x.id===currentTeamId); if (t) await renderPanelBody(t); }
    await logActivity(null,'System','stripe_link_generated',
      `${ok} payment link${ok===1?'':'s'} regenerated after import${failed?`, ${failed} failed`:''}`,'','');
    toast(ok ? `${ok} link${ok>1?'s':''} regenerated${failed?` \u00b7 ${failed} failed`:''}` : 'No links regenerated',
      ok ? 'success' : 'error');
  }

  // Queries the Checkout Sessions belonging to a Payment Link.
  // Payment Intents cannot be filtered on custom metadata, and a Payment Link
  // does not pass its metadata down to the Payment Intent, so the old approach
  // could never find a match.
  async function fetchStripePayment(team) {
    const { sessions = [] } = await stripeCall('list_paid_sessions', {
      payment_link_id: team.stripe_payment_link_id
    });
    if (!sessions.length) return null;
    const totalCents = sessions.reduce((sum, s) => sum + (s.amount_total || 0), 0);
    const latest = sessions.reduce((a, b) => (a.created > b.created ? a : b));
    return {
      amount: totalCents / 100,
      paidAt: new Date(latest.created * 1000).toISOString(),
      sessionCount: sessions.length
    };
  }

  // Returns 'paid' | 'none' | 'error' so the bulk runner can summarise results.
  async function checkSingleStripeStatus(teamId, opts = {}) {
    const team = state.teams.find(t => t.id === teamId);
    if (!team?.stripe_payment_link_id) return 'none';
    if (!opts.silent) toast('Checking Stripe…');
    try {
      const payment = await fetchStripePayment(team);
      if (!payment) {
        if (!opts.silent) toast('No completed payment found for this link','warning');
        return 'none';
      }
      const prevStatus = team.payment_status;
      const prevPaid = Number(team.amount_paid_eur || 0);
      if (prevStatus === 'paid' && Math.abs(prevPaid - payment.amount) < 0.01) {
        if (!opts.silent) toast('Already recorded as paid');
        return 'paid';
      }
      const billed = Number(team.entry_fee_eur || 0) - Number(team.discount_eur || 0);
      const balance = Math.max(0, Math.round((billed - payment.amount) * 100) / 100);
      const update = {
        payment_status: 'paid',
        amount_paid_eur: payment.amount,
        balance_eur: balance,
        paid_at: payment.paidAt,
        updated_at: new Date().toISOString()
      };
      const { error } = await sb.from('teams').update(update).eq('id', team.id);
      if (error) throw new Error(error.message);
      Object.assign(team, update);
      await logActivity(team.id, team.team_name, 'payment_received',
        `Stripe payment of €${fmtMoney(payment.amount)} confirmed`
        + `${payment.sessionCount > 1 ? ` across ${payment.sessionCount} checkout sessions` : ''}`
        + ` · balance now €${fmtMoney(balance)}`,
        `${prevStatus} / €${fmtMoney(prevPaid)}`,
        `paid / €${fmtMoney(payment.amount)}`);
      if (!opts.silent) {
        renderTable(); renderKPIs(); updateBadges();
        if (currentTeamId === team.id) await renderPanelBody(team);
        if (document.getElementById('pageDashboard')?.classList.contains('active')) renderDashboard();
        toast(`€${fmtMoney(payment.amount)} recorded for ${team.team_name}`, 'success');
      }
      return 'paid';
    } catch (e) {
      if (!opts.silent) toast('Check failed: ' + e.message, 'error');
      return 'error';
    }
  }

  async function checkAllStripeStatuses() {
    const list = state.teams.filter(t => t.stripe_payment_link_id && !t.archived_at && !isSettled(t));
    if (!list.length) { toast('No open registrations with a Stripe link','warning'); return; }
    toast(`Checking ${list.length} Stripe link${list.length>1?'s':''}…`);
    let paid = 0, errors = 0;
    for (const t of list) {
      const result = await checkSingleStripeStatus(t.id, { silent: true });
      if (result === 'paid') paid++;
      if (result === 'error') errors++;
    }
    renderTable(); renderKPIs(); updateBadges();
    if (currentTeamId) { const t = state.teams.find(x => x.id === currentTeamId); if (t) await renderPanelBody(t); }
    if (document.getElementById('pageDashboard')?.classList.contains('active')) renderDashboard();
    await logActivity(null, 'System', 'stripe_bulk_check',
      `Bulk Stripe check over ${list.length} registrations — ${paid} newly paid${errors?`, ${errors} errors`:''}`, '', '');
    toast(paid > 0
      ? `${paid} payment${paid>1?'s':''} recorded${errors?` · ${errors} failed`:''}`
      : `No new payments found${errors?` · ${errors} failed`:''}`,
      paid > 0 ? 'success' : 'warning');
  }

  // ── EMAIL TEMPLATES ──
  async function openEmailModal() {
    if(!currentTeamId) return;
    await loadDbTemplates();
    const team=state.teams.find(t=>t.id===currentTeamId);
    const ev=state.events.find(e=>e.id===state.currentEventId);
    const tpls=getTemplates(team,ev);
    document.getElementById('templateTabs').innerHTML=tpls.map((t,i)=>`<button class="template-tab ${i===0?'active':''}" onclick="App.showTemplate(${i})">${t.stage}</button>`).join('');
    showTemplate(0,tpls,team);
    document.getElementById('emailModal').classList.add('open');
  }

  function closeEmailModal() {
    document.getElementById('emailModal').classList.remove('open');
  }

  function showTemplate(idx,tpls,tm) {
    const team=tm||(currentTeamId?state.teams.find(t=>t.id===currentTeamId):null);
    const ev=state.events.find(e=>e.id===state.currentEventId);
    const templates=tpls||getTemplates(team,ev);
    const t=templates[idx];
    document.querySelectorAll('.template-tab').forEach((el,i)=>el.classList.toggle('active',i===idx));
    const hasLink=!!team?.stripe_payment_link;
    const toEmail=team?.contact_email||team?.user_email||team?.club_email||'';
    const stageKeys=['email_1','reminder_1','reminder_2','final_warning'];
    const lastSent=(team?.reminder_dates||{})[stageKeys[idx]];
    const bodyHtml=esc(t.body).replace(/\n\n/g,'</p><p style="margin:0 0 10px">').replace(/\n/g,'<br>');
    const sourceBadge=t.hasOverride
      ?'<span class="tpl-override-badge"><i class="ti ti-pencil" style="font-size:10px"></i> Custom override for this team</span>'
      :t.hasDbTemplate?'<span style="font-size:10px;color:var(--text-3);margin-left:4px">Using central template</span>':'';
    document.getElementById('templateContent').innerHTML=`
      <div style="background:var(--surface-2);border:1px solid var(--border);border-radius:var(--radius);padding:12px 14px;margin-bottom:12px">
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:4px">
          <span style="font-size:10px;font-weight:600;text-transform:uppercase;letter-spacing:.07em;color:var(--text-3)">Subject</span>
          ${sourceBadge}
        </div>
        <div id="tplSubjectView" style="font-size:13px;font-weight:500;color:var(--text-1)">${esc(t.subject)}</div>
        <input id="tplSubjectEdit" style="display:none;width:100%;margin-top:6px;padding:6px 8px;font-size:13px;border:1px solid var(--border);border-radius:var(--radius);background:var(--bg);color:var(--text-1);box-sizing:border-box" value="${esc(t.subject)}">
      </div>
      <div id="tplBodyView" style="background:var(--surface);border:1px solid var(--border);border-radius:var(--radius);padding:14px;font-size:13px;line-height:1.7;color:var(--text-1);max-height:240px;overflow-y:auto;margin-bottom:6px">
        <p style="margin:0 0 10px">${bodyHtml}</p>
      </div>
      <textarea id="tplBodyEdit" style="display:none;width:100%;min-height:200px;padding:10px;font-size:12px;line-height:1.6;border:1px solid var(--border);border-radius:var(--radius);background:var(--bg);color:var(--text-1);font-family:var(--mono);resize:vertical;margin-bottom:6px;box-sizing:border-box">${esc(t.body)}</textarea>
      ${!hasLink?'<div class="template-warning"><i class="ti ti-alert-triangle"></i> No Stripe link yet.</div>':''}
      ${!toEmail?'<div class="template-warning" style="margin-top:4px"><i class="ti ti-alert-triangle"></i> No email address on file.</div>':''}
      <div style="border-top:1px solid var(--border);padding-top:12px;display:flex;flex-direction:column;gap:8px">
        <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap">
          ${true /* verzenden loopt via de server */?`
            <button class="btn btn-primary" id="btnSendMC" onclick="App.sendViaMailchimp(${idx})" ${!hasLink||!toEmail?'disabled':''}>
              <i class="ti ti-send"></i> Send "${esc(t.stage)}"
            </button>
            <span style="font-size:12px;color:var(--text-2)">&rarr; <strong>${esc(toEmail||'&mdash;')}</strong></span>
          `:'<div style="padding:8px 12px;background:var(--surface-2);border:1px solid var(--border);border-radius:var(--radius);font-size:12px;color:var(--text-2)"><i class="ti ti-mail-forward" style="color:var(--text-3)"></i> Add Brevo API key in Settings</div>'}
          <button class="btn btn-default btn-sm" onclick="App.copyTemplate(${idx})" ${!hasLink?'disabled':''}><i class="ti ti-copy"></i> Copy</button>
          <button class="btn btn-default btn-sm" id="btnEditOverride" onclick="App.toggleTeamOverrideEdit(${idx},'${t.seq||'regular'}')"><i class="ti ti-pencil"></i> Edit for this team</button>
          ${t.hasOverride?`<button class="btn btn-ghost btn-sm" onclick="App.clearTeamOverride('${team?.id}','${t.seq||'regular'}',${idx})"><i class="ti ti-refresh"></i> Remove override</button>`:''}
        </div>
        <div style="font-size:12px;color:var(--text-3)"><i class="ti ti-clock" style="font-size:13px"></i> ${lastSent?`Last sent: <strong style="color:var(--text-2)">${fmtDatetime(lastSent)}</strong>`:'Not sent yet'}</div>
      </div>`;
  }

  async function copyTemplate(idx) {
    const team=state.teams.find(t=>t.id===currentTeamId);
    const ev=state.events.find(e=>e.id===state.currentEventId);
    const tpls=getTemplates(team,ev);
    const t=tpls[idx];
    await navigator.clipboard.writeText(`Subject: ${t.subject}\n\n${t.body}`);
    const stageMap=['email_1','email_1','reminder_1','reminder_2','final_warning'];
    const stage=stageMap[idx]||'email_1';
    const now=new Date().toISOString();
    const rd=team.reminder_dates||{}; rd[stage]=now;
    await sb.from('teams').update({reminder_stage:stage,reminder_dates:rd,updated_at:now}).eq('id',team.id);
    const ti=state.teams.findIndex(t=>t.id===team.id);
    if(ti>=0){state.teams[ti].reminder_stage=stage;state.teams[ti].reminder_dates=rd;}
    await logActivity(team.id,team.team_name,'email_sent',`${t.stage} copied for ${team.team_name}`,'',stage);
    renderTable();
    await renderPanelBody(state.teams.find(t=>t.id===currentTeamId));
    toast('Copied — stage updated','success');
  }

  // ── EMAIL TEMPLATE SYSTEM ──
  // Priority: per-team override → central DB template → hardcoded default
  let dbTemplates = {}; // keyed `${sequence}_${stageIndex}`
  let currentTplSeq = 'regular';

  // Raw hardcoded defaults (no placeholder fill)
  const HARDCODED = {
    early_bird: [
      {
        stage: 'Email 1 — Early bird',
        subject: 'Your {{event_name}} registration — early bird payment link',
        body: `Hi {{contact_name}},

Thank you for registering {{team_name}} for {{event_name}}! We look forward to welcoming your team on {{tournament_date}} in {{city}}.

You qualify for our early bird rate. To lock in your discounted entry fee of €{{balance_eur}}, please complete payment before {{early_bird_deadline}}.

Pay securely here:
{{stripe_link}}

Important: after {{early_bird_deadline}}, the regular (higher) entry fees apply. Payment must be received — not just initiated — before the deadline.

{{organisation_name}}
{{from_email}} | {{website}}`
      },
      {
        stage: 'Reminder 1 — Early bird',
        subject: 'Early bird deadline approaching — {{event_name}}',
        body: `Hi {{contact_name}},

A quick reminder that your early bird payment deadline for {{event_name}} is {{early_bird_deadline}}.

Your current balance is €{{balance_eur}}. If payment is not received by {{early_bird_deadline}}, your registration will be moved to the regular pricing tier.

Please pay here before the deadline:
{{stripe_link}}

{{organisation_name}}
{{from_email}} | {{website}}`
      },
      {
        stage: 'Reminder 2 — Early bird',
        subject: 'Last chance: early bird rate expires {{early_bird_deadline}}',
        body: `Hi {{contact_name}},

This is your final reminder before the early bird deadline of {{early_bird_deadline}}.

After this date, {{team_name}}'s registration switches to regular pricing — higher than your current balance of €{{balance_eur}}.

Pay now to keep your early bird rate:
{{stripe_link}}

{{organisation_name}}
{{from_email}} | {{website}}`
      },
      {
        stage: 'Final warning',
        subject: 'Final notice — {{event_name}} registration at risk',
        body: `Hi {{contact_name}},

The early bird deadline of {{early_bird_deadline}} has passed without payment. Your registration has been moved to regular pricing. Please contact us to confirm your updated balance and complete payment before {{payment_deadline}}.

{{stripe_link}}

{{organisation_name}}
{{from_email}} | {{website}}`
      }
    ],
    regular: [
      {
        stage: 'Email 1 — Regular',
        subject: 'Your {{event_name}} registration — payment link inside',
        body: `Hi {{contact_name}},

Thank you for registering {{team_name}} for {{event_name}}. We look forward to welcoming your team on {{tournament_date}} in {{city}}.

To confirm your spot, please complete your payment of €{{balance_eur}} using the secure link below:

{{stripe_link}}

Please ensure payment is received by {{payment_deadline}}.

{{organisation_name}}
{{from_email}} | {{website}}`
      },
      {
        stage: 'Reminder 1 — Regular',
        subject: 'Friendly reminder — {{event_name}} payment',
        body: `Hi {{contact_name}},

Just a quick reminder that we haven't received payment yet for {{team_name}}'s registration at {{event_name}}.

Your payment of €{{balance_eur}} is due by {{payment_deadline}}. Pay securely here:

{{stripe_link}}

{{organisation_name}}
{{from_email}} | {{website}}`
      },
      {
        stage: 'Reminder 2 — Regular',
        subject: 'Action required — {{event_name}} payment deadline approaching',
        body: `Hi {{contact_name}},

Payment of €{{balance_eur}} for {{team_name}} is required by {{payment_deadline}}. Registrations without confirmed payment cannot be guaranteed after this date.

{{stripe_link}}

{{organisation_name}}
{{from_email}} | {{website}}`
      },
      {
        stage: 'Final warning',
        subject: 'Final notice — {{event_name}} registration at risk',
        body: `Hi {{contact_name}},

This is a final notice for {{team_name}}. We have not received payment of €{{balance_eur}}. Without payment before {{payment_deadline}}, your registration will be cancelled.

{{stripe_link}}

{{organisation_name}}
{{from_email}} | {{website}}`
      }
    ]
  };

  async function loadDbTemplates() {
    if (!state.currentEventId) return;
    const { data } = await sb.from('email_templates').select('*').eq('event_id', state.currentEventId);
    dbTemplates = {};
    (data||[]).forEach(t => { dbTemplates[`${t.sequence}_${t.stage_index}`] = t; });
  }

  function getSequenceForTeam(team, event) {
    return isEarlyBird(team, event) ? 'early_bird' : 'regular';
  }

  function getTemplates(team, event) {
    const seq = getSequenceForTeam(team, event);
    const fill = s => s
      .replace(/\{\{team_name\}\}/g, team?.team_name||'[Team]')
      .replace(/\{\{contact_name\}\}/g, team?.contact_name||'Team Leader')
      .replace(/\{\{stripe_link\}\}/g, team?.stripe_payment_link||'[STRIPE LINK]')
      .replace(/\{\{balance_eur\}\}/g, fmtMoney(team?.balance_eur||0))
      .replace(/\{\{payment_deadline\}\}/g, fmtDate(event?.payment_deadline)||'[DEADLINE]')
      .replace(/\{\{early_bird_deadline\}\}/g, fmtDate(event?.early_bird_deadline)||'[EB DEADLINE]')
      .replace(/\{\{entry_fee\}\}/g, fmtMoney(team?.entry_fee_eur||0))
      .replace(/\{\{discount\}\}/g, fmtMoney(team?.discount_eur||0))
      .replace(/\{\{amount_paid\}\}/g, fmtMoney(team?.amount_paid_eur||0))
      .replace(/\{\{entries\}\}/g, String(team?.athlete_count||0))
      .replace(/\{\{tournament_date\}\}/g, fmtDate(event?.tournament_date)||'[DATUM]')
      .replace(/\{\{event_name\}\}/g, event?.name || '[EVENEMENT]')
      .replace(/\{\{organisation_name\}\}/g, state.currentOrg?.name || '[ORGANISATIE]')
      .replace(/\{\{city\}\}/g, state.currentOrg?.city || '[PLAATS]')
      .replace(/\{\{website\}\}/g, state.currentOrg?.website || '')
      .replace(/\{\{from_email\}\}/g, state.currentOrg?.email_from_email || '');

    return [0,1,2,3].map(idx => {
      const dbKey = `${seq}_${idx}`;
      const clubOverride = team?.email_overrides?.[dbKey];
      const dbT = dbTemplates[dbKey];
      const hc = HARDCODED[seq][idx];
      const subject = clubOverride?.subject ?? dbT?.subject ?? hc.subject;
      const body    = clubOverride?.body    ?? dbT?.body    ?? hc.body;
      return {
        stage: hc.stage,
        subject: fill(subject),
        body: fill(body),
        seq,
        idx,
        hasOverride: !!clubOverride,
        hasDbTemplate: !!dbT
      };
    });
  }

  // ── CENTRAL TEMPLATE MANAGEMENT PAGE ──
  async function renderTemplatesPage() {
    const el = document.getElementById('templatesContent');
    if (!el) return;
    if (!state.currentEventId) {
      el.innerHTML = `<div style="text-align:center;padding:60px 20px;color:var(--text-3)">
        <i class="ti ti-mail-cog" style="font-size:40px;display:block;margin-bottom:12px"></i>
        <strong style="font-size:15px;color:var(--text-2)">No event selected</strong>
        <p style="font-size:13px;margin-top:6px">Select an event to manage its email templates.</p>
      </div>`;
      return;
    }
    await loadDbTemplates();
    const ev = state.events.find(e => e.id === state.currentEventId);

    const renderSeq = seq => [0,1,2,3].map(idx => {
      const dbKey = `${seq}_${idx}`;
      const saved = dbTemplates[dbKey];
      const hc = HARDCODED[seq][idx];
      const subject = saved?.subject ?? hc.subject;
      const body    = saved?.body    ?? hc.body;
      const meta = saved
        ? `Last saved ${fmtDatetime(saved.updated_at)} by ${esc(saved.updated_by||'admin')}`
        : '<span style="color:var(--text-3)">Using default</span>';
      return `<div class="tpl-stage-card">
        <div class="tpl-stage-header">
          <span class="tpl-stage-label">${esc(hc.stage)}</span>
          <span class="tpl-stage-meta">${meta}</span>
        </div>
        <div class="tpl-field">
          <label>Subject</label>
          <input id="tpl_subj_${seq}_${idx}" value="${esc(subject)}">
        </div>
        <div class="tpl-field">
          <label>Body</label>
          <textarea id="tpl_body_${seq}_${idx}">${esc(body)}</textarea>
        </div>
        <div class="tpl-placeholder-hint">
          Placeholders: <code>{{team_name}}</code> <code>{{contact_name}}</code>
          <code>{{stripe_link}}</code> <code>{{balance_eur}}</code>
          <code>{{entry_fee}}</code> <code>{{discount}}</code>
          <code>{{amount_paid}}</code> <code>{{entries}}</code>
          <code>{{payment_deadline}}</code> <code>{{early_bird_deadline}}</code>
          <code>{{tournament_date}}</code>
          <br><span style="color:var(--text-3)">Amounts come from the Sportdata import and always reflect what the club actually owes.</span>
        </div>
        <div style="display:flex;gap:8px;margin-top:12px;align-items:center">
          <button class="btn btn-primary btn-sm" onclick="App.saveCentralTemplate('${seq}',${idx},'${esc(hc.stage)}')">
            <i class="ti ti-device-floppy"></i> Save
          </button>
          ${saved ? `<button class="btn btn-ghost btn-sm" onclick="App.resetCentralTemplate('${seq}',${idx})">
            <i class="ti ti-refresh"></i> Reset to default
          </button>` : ''}
        </div>
      </div>`;
    }).join('');

    el.innerHTML = `
      <div style="margin-bottom:4px;font-size:13px;color:var(--text-2)">
        Templates for <strong>${esc(ev?.name||'')}</strong>. Changes apply to all new emails from this event.
        Per-team overrides in the email modal take priority over these central templates.
      </div>
      <div class="tpl-seq-tabs" style="margin-top:16px">
        <button class="tpl-seq-tab ${currentTplSeq==='regular'?'active':''}" onclick="App.switchTplSeq('regular')">Regular sequence</button>
        <button class="tpl-seq-tab ${currentTplSeq==='early_bird'?'active':''}" onclick="App.switchTplSeq('early_bird')">Early bird sequence</button>
      </div>
      <div>${renderSeq(currentTplSeq)}</div>`;
  }

  function switchTplSeq(seq) { currentTplSeq = seq; renderTemplatesPage(); }

  async function saveCentralTemplate(seq, idx, stageLabel) {
    const subject = document.getElementById(`tpl_subj_${seq}_${idx}`)?.value.trim();
    const body    = document.getElementById(`tpl_body_${seq}_${idx}`)?.value.trim();
    if (!subject||!body) { toast('Subject and body are required','error'); return; }
    const { data: { user } } = await sb.auth.getUser();
    const payload = {
      event_id: state.currentEventId, sequence: seq, stage_index: idx,
      stage_label: stageLabel, subject, body,
      updated_by: user?.email||'admin', updated_at: new Date().toISOString()
    };
    const { error } = await sb.from('email_templates')
      .upsert([payload], { onConflict: 'event_id,sequence,stage_index' });
    if (error) { toast('Save failed: '+error.message,'error'); return; }
    dbTemplates[`${seq}_${idx}`] = payload;
    renderTemplatesPage();
    toast('Template saved','success');
  }

  async function resetCentralTemplate(seq, idx) {
    showConfirm('Reset template', 'This removes the saved version and restores the default.', async () => {
      const { error } = await sb.from('email_templates')
        .delete().eq('event_id',state.currentEventId).eq('sequence',seq).eq('stage_index',idx);
      if (error) { toast('Error: '+error.message,'error'); return; }
      delete dbTemplates[`${seq}_${idx}`];
      renderTemplatesPage();
      toast('Reset to default','success');
    });
  }

  // ── PER-TEAM TEMPLATE OVERRIDE ──
  async function saveTeamOverride(teamId, seq, idx, subject, body) {
    const team = state.teams.find(t=>t.id===teamId);
    if (!team) return;
    const overrides = team.email_overrides || {};
    overrides[`${seq}_${idx}`] = { subject, body, savedAt: new Date().toISOString() };
    const { error } = await sb.from('teams')
      .update({ email_overrides: overrides, updated_at: new Date().toISOString() }).eq('id', teamId);
    if (error) { toast('Save failed: '+error.message,'error'); return; }
    team.email_overrides = overrides;
    toast('Override saved for this registration','success');
    showTemplate(idx);
  }

  async function clearTeamOverride(teamId, seq, idx) {
    const team = state.teams.find(t=>t.id===teamId);
    if (!team) return;
    const overrides = team.email_overrides || {};
    delete overrides[`${seq}_${idx}`];
    await sb.from('teams')
      .update({ email_overrides: overrides, updated_at: new Date().toISOString() }).eq('id', teamId);
    team.email_overrides = overrides;
    toast('Override removed — using central template','success');
    showTemplate(idx);
  }

  function toggleTeamOverrideEdit(idx, seq) {
    const sv = document.getElementById('tplSubjectView');
    const se = document.getElementById('tplSubjectEdit');
    const bv = document.getElementById('tplBodyView');
    const be = document.getElementById('tplBodyEdit');
    const btn = document.getElementById('btnEditOverride');
    const isEditing = be?.style.display !== 'none';
    if (isEditing) {
      const newSubj = se?.value.trim();
      const newBody = be?.value.trim();
      if (newSubj && newBody) saveTeamOverride(currentTeamId, seq, idx, newSubj, newBody);
      if (sv) { sv.textContent = newSubj||sv.textContent; sv.style.display=''; }
      if (se) se.style.display = 'none';
      if (bv) { bv.innerHTML = '<p>'+esc(newBody||'').replace(/\n\n/g,'</p><p>').replace(/\n/g,'<br>')+'</p>'; bv.style.display=''; }
      if (be) be.style.display = 'none';
      if (btn) btn.innerHTML = '<i class="ti ti-pencil"></i> Edit for this team';
    } else {
      if (sv) sv.style.display = 'none';
      if (se) se.style.display = '';
      if (bv) bv.style.display = 'none';
      if (be) be.style.display = '';
      if (btn) btn.innerHTML = '<i class="ti ti-device-floppy"></i> Save override';
    }
  }



  // ── MAILCHIMP (MANDRILL) ──
  async function emailCall(action, payload = {}) {
    const { data, error } = await sb.functions.invoke('email-send', { body: { action, ...payload } });
    if (error) {
      let melding = error.message || 'Aanroep mislukt';
      try {
        const body = await error.context?.json?.();
        if (body?.error) melding = body.error;
      } catch { /* niet leesbaar */ }
      throw new Error(melding);
    }
    if (data?.error) throw new Error(data.error);
    return data;
  }

  // Versturen gaat via de server. De sleutel van Brevo staat daar, en de
  // dagteller ook: die leest het logboek dat de functie zelf schrijft, zodat
  // de limiet niet te omzeilen is door die logregel over te slaan.
  async function sendViaMailchimp(idx) {
    const team = state.teams.find(t => t.id === currentTeamId);
    const ev = state.events.find(e => e.id === state.currentEventId);
    const t = getTemplates(team, ev)[idx];
    const toEmail = team?.contact_email || team?.user_email || team?.club_email || '';
    if (!toEmail) { toast('Geen ontvangeradres bij dit team', 'error'); return; }

    const btn = document.getElementById('btnSendMC');
    if (btn) { btn.disabled = true; btn.innerHTML = '<div class="spinner"></div> Versturen\u2026'; }

    const htmlBody = '<div style="font-family:Arial,sans-serif;font-size:14px;line-height:1.7;color:#18181b;max-width:560px"><p style="margin:0 0 12px">'
      + esc(t.body).replace(/\n\n/g, '</p><p style="margin:0 0 12px">').replace(/\n/g, '<br>')
      + '</p></div>';

    try {
      const res = await emailCall('send', {
        to: toEmail, subject: t.subject, text: t.body, html: htmlBody,
        team_id: team.id, team_name: team.team_name,
        event_id: state.currentEventId, stage: t.stage
      });

      // De stand van de herinneringen blijft van de applicatie: de functie weet
      // niet welke stap dit in de reeks is.
      const stages = ['email_1', 'reminder_1', 'reminder_2', 'final_warning'];
      const stage = stages[idx];
      const now = new Date().toISOString();
      const rd = { ...(team.reminder_dates || {}), [stage]: now };
      await sb.from('teams').update({ reminder_stage: stage, reminder_dates: rd, updated_at: now }).eq('id', team.id);
      const ti = state.teams.findIndex(x => x.id === team.id);
      if (ti >= 0) { state.teams[ti].reminder_stage = stage; state.teams[ti].reminder_dates = rd; }

      renderTable();
      if (currentTeamId === team.id) await renderPanelBody(state.teams[ti] || team);
      const rest = res.daily_limit - res.sent_today;
      toast(`Verstuurd naar ${toEmail}${rest <= 20 ? ` \u00b7 nog ${rest} vandaag` : ''}`, 'success');
      closeEmailModal();
    } catch (e) {
      toast(e.message, 'error');
    } finally {
      if (btn) { btn.disabled = false; btn.innerHTML = '<i class="ti ti-send"></i> Versturen'; }
    }
  }
  async function renderActivityPage() {
    if(!state.currentEventId) return;
    const {data}=await sb.from('activity_log').select('*').eq('event_id',state.currentEventId).order('created_at',{ascending:false}).limit(200);
    // "money" is a group filter across every action type that moves a euro,
    // so the financial trail can be read in one list.
    const logs=(data||[]).filter(l =>
      activityFilter==='all' ? true :
      activityFilter==='money' ? FEE_ACTIONS.has(l.action_type) :
      l.action_type===activityFilter);
    const el=document.getElementById('globalActivityList');
    if(!logs.length){el.innerHTML='<div class="empty-state"><i class="ti ti-list-details"></i><strong>No activity yet</strong></div>';return;}
    el.innerHTML=logs.map(l=>`
      <div class="global-activity-entry">
        <div class="ga-icon">${actionIcon(l.action_type)}</div>
        <div class="ga-body">
          ${l.team_id?`<div class="ga-team" onclick="App.openPanel('${l.team_id}')">${esc(l.team_name||'—')}</div>`:''}
          <div class="ga-desc">${esc(l.description)}</div>
          ${l.old_value?`<div style="font-size:11px;color:var(--text-3)">${esc(l.old_value)} → ${esc(l.new_value||'')}</div>`:''}
          <div class="ga-time">${fmtDatetime(l.created_at)}${l.actor_email?` · ${esc(l.actor_email)}`:''}</div>
        </div>
      </div>`).join('');
  }

  function filterActivity(type) {
    activityFilter=type;
    document.querySelectorAll('.filter-chip').forEach(c=>c.classList.toggle('active',c.dataset.type===type));
    renderActivityPage();
  }

  // ── MANUAL BALANCE CORRECTION & WRITE OFF ─────────────────────────────────
  // Both actions do the same thing to the data: they move
  // manual_adjustment_eur, which lowers the billed amount. They differ only in
  // intent, and that intent is what `settlement` records. Keeping one
  // mechanism means the balance can never be corrected twice by two routes.
  let adjustTeamId = null;

  function openAdjustModal(teamId) {
    const t = state.teams.find(x=>x.id===teamId); if(!t) return;
    adjustTeamId = teamId;
    const cur = balanceOf(t);
    document.getElementById('adjustTitle').textContent =
      (t.settlement && t.settlement!=='none') ? 'Change correction' : 'Adjust balance';
    document.getElementById('adjustIntro').innerHTML =
      `<strong>${esc(t.team_name)}</strong> is billed €${fmtMoney(round2((Number(t.entry_fee_eur)||0)-(Number(t.discount_eur)||0)))} by Sportdata`
      + ` and has paid €${fmtMoney(t.amount_paid_eur)}.`
      + ` Enter what should still be outstanding. The correction is calculated for you and is kept when Sportdata is re-imported.`;
    document.getElementById('adjustNewBalance').value = cur.toFixed(2);
    document.getElementById('adjustReason').value = t.adjustment_reason || '';
    closeWriteOffModal();
    document.getElementById('adjustModal').classList.add('open');
    previewAdjust();
  }
  function closeAdjustModal(){ document.getElementById('adjustModal').classList.remove('open'); }

  // Turns "what should still be outstanding" into an absolute correction on
  // the Sportdata fee, so repeated edits never stack on top of each other.
  function computeAdjustment(t, newBalance) {
    const sdBilled = round2((Number(t.entry_fee_eur)||0) - (Number(t.discount_eur)||0));
    const paid     = round2(t.amount_paid_eur);
    return round2(sdBilled - paid - newBalance);
  }

  function previewAdjust() {
    const t = state.teams.find(x=>x.id===adjustTeamId); if(!t) return;
    const raw = document.getElementById('adjustNewBalance').value;
    const reason = document.getElementById('adjustReason').value.trim();
    const el = document.getElementById('adjustPreview');
    const btn = document.getElementById('adjustSaveBtn');
    const hint = document.getElementById('adjustHint');
    const newBal = parseFloat(raw);

    if (raw === '' || isNaN(newBal) || newBal < 0) {
      el.innerHTML = '<div style="font-size:12px;color:var(--text-3)">Enter the new outstanding balance to see the effect.</div>';
      hint.textContent = ''; btn.disabled = true; return;
    }
    const sdBilled = round2((Number(t.entry_fee_eur)||0) - (Number(t.discount_eur)||0));
    const paid     = round2(t.amount_paid_eur);
    const adj      = computeAdjustment(t, newBal);
    const newStatus = deriveStatus(t.entry_fee_eur, t.discount_eur, t.amount_paid_eur, adj);

    hint.textContent = adj === 0 ? 'No correction: this matches the Sportdata amount.'
      : adj > 0 ? `Lowers the billed amount by €${fmtMoney(adj)}.`
                : `Raises the billed amount by €${fmtMoney(-adj)}.`;

    if (newBal > round2(sdBilled - paid)) {
      el.innerHTML = `<div class="banner banner-amber" style="margin:0"><i class="ti ti-alert-triangle"></i>
        <span>This is <strong>more</strong> than Sportdata bills. Only do this for a genuine surcharge, otherwise correct the fee in Sportdata instead.</span></div>`;
    } else {
      el.innerHTML = `
        <div class="adj-row"><span>Billed by Sportdata</span><span>€${fmtMoney(sdBilled)}</span></div>
        <div class="adj-row"><span>Manual correction</span><span style="color:${adj>0?'#7c3aed':'var(--amber)'}">${adj>0?'−':adj<0?'+':''}€${fmtMoney(Math.abs(adj))}</span></div>
        <div class="adj-row"><span>Already paid</span><span>€${fmtMoney(paid)}</span></div>
        <div class="adj-row total"><span>Outstanding after correction</span><span>€${fmtMoney(newBal)}</span></div>
        <div class="adj-row"><span>Status becomes</span><span>${statusBadge(newStatus)}</span></div>`;
    }
    btn.disabled = !reason;
  }

  async function saveAdjustment() {
    const t = state.teams.find(x=>x.id===adjustTeamId); if(!t) return;
    const newBal = parseFloat(document.getElementById('adjustNewBalance').value);
    const reason = document.getElementById('adjustReason').value.trim();
    if (isNaN(newBal) || newBal < 0 || !reason) return;
    // Via de dispatcher: de database rekent en logt, en legt het voorstel
    // eventueel eerst aan een tweede bevoegde voor (stap H).
    const ok = await viaDispatcher('finance.adjust', t, { team_id: t.id, nieuw_saldo: round2(newBal) }, reason);
    if (ok) closeAdjustModal();
  }

  function openWriteOffModal(teamId) {
    const t = state.teams.find(x=>x.id===teamId); if(!t) return;
    adjustTeamId = teamId;
    document.getElementById('writeOffIntro').innerHTML =
      `<strong>${esc(t.team_name)}</strong> has €${fmtMoney(balanceOf(t))} outstanding.`
      + ` Writing it off records that this amount will not be collected. The registration stays in the system for reference and the club drops out of reminders, Stripe checks and the deadline banner.`;
    document.getElementById('writeOffReason').value = '';
    closeAdjustModal();
    document.getElementById('writeOffModal').classList.add('open');
    previewWriteOff();
  }
  function closeWriteOffModal(){ document.getElementById('writeOffModal').classList.remove('open'); }

  function previewWriteOff() {
    const t = state.teams.find(x=>x.id===adjustTeamId); if(!t) return;
    const reason = document.getElementById('writeOffReason').value.trim();
    const open = balanceOf(t);
    document.getElementById('writeOffPreview').innerHTML = `
      <div class="adj-row"><span>Billed after any earlier correction</span><span>€${fmtMoney(billedOf(t))}</span></div>
      <div class="adj-row"><span>Already paid (stays as income)</span><span>€${fmtMoney(t.amount_paid_eur)}</span></div>
      <div class="adj-row total"><span>Written off</span><span style="color:var(--red)">€${fmtMoney(open)}</span></div>
      <div class="adj-row"><span>Balance becomes</span><span>€0.00</span></div>`;
    document.getElementById('writeOffSaveBtn').disabled = !reason;
  }

  async function saveWriteOff() {
    const t = state.teams.find(x=>x.id===adjustTeamId); if(!t) return;
    const reason = document.getElementById('writeOffReason').value.trim();
    if (!reason) return;
    const ok = await viaDispatcher('finance.writeoff', t, { team_id: t.id }, reason);
    if (ok) closeWriteOffModal();
  }

  function confirmReverseSettlement(teamId) {
    const t = state.teams.find(x=>x.id===teamId); if(!t) return;
    const label = isWrittenOff(t) ? 'write-off' : 'correction';
    const adj = round2(t.manual_adjustment_eur);
    showConfirm(`Undo ${label}`,
      `This removes the manual correction of €${fmtMoney(Math.abs(adj))} for ${t.team_name}. `
      + `The balance returns to the Sportdata amount and the club is collectable again. `
      + `Both the original entry and this reversal stay in the fee history.`,
      () => reverseSettlement(teamId));
  }

  async function reverseSettlement(teamId) {
    const t = state.teams.find(x=>x.id===teamId); if(!t) return;
    const reden = isWrittenOff(t) ? 'Write-off undone' : 'Correction undone';
    await viaDispatcher('finance.reverse', t, { team_id: teamId }, reden);
  }

  // Eén route voor handelingen die onder vierogen kunnen vallen. Geeft true als
  // de handeling is aangenomen (uitgevoerd of als voorstel ingediend).
  async function viaDispatcher(handeling, t, gegevens, reden) {
    const { data, error } = await sb.rpc('dispatch', { p_handeling: handeling, p_gegevens: gegevens, p_reden: reden });
    if (error) { toast(dispatchFout(error), 'error'); return false; }
    if (data?.status === 'wacht') {
      toast(t_('vierogen.ingediend'), 'success');
      await goedkeuringen.vernieuwTeller();
      if (currentTeamId === t.id) await renderPanelBody(t);
      return true;
    }
    // Uitgevoerd: de database heeft gerekend, dus de rij opnieuw ophalen.
    const { data: vers } = await sb.from('teams').select('*').eq('id', t.id).maybeSingle();
    if (vers) Object.assign(t, vers);
    await refreshAfterSettlement(t);
    toast(t_(handeling === 'finance.reverse' ? 'vierogen.teruggedraaid' : 'vierogen.uitgevoerd'), 'success');
    // Vierogen stond aan, maar er is geen tweede bevoegde: dan zeggen we dat.
    if (String(data?.toelichting || '').startsWith('vierogen niet mogelijk')) toast(t_('vierogen.alleen'), 'warning');
    return true;
  }

  async function refreshAfterSettlement(team) {
    // checkDeadlines too: a settled team must disappear from the overdue
    // banner immediately, not only after the next page load.
    renderTable(); renderKPIs(); updateBadges(); renderFlaggedPage(); checkDeadlines();
    if (document.getElementById('pageDashboard')?.classList.contains('active')) renderDashboard();
    if (document.getElementById('pageActivity')?.classList.contains('active')) renderActivityPage();
    if (currentTeamId === team.id && document.getElementById('panelOverlay').classList.contains('open')) {
      await renderPanelBody(team);
    }
  }

  // ── FLAGGED PAGE ──
  function renderFlaggedPage() {
    const flagged=state.teams.filter(t=>t.payment_status==='flagged'&&!isWrittenOff(t));
    const el=document.getElementById('flaggedContent');
    if(!flagged.length){el.innerHTML='<div class="empty-state" style="background:var(--surface);border:1px solid var(--border);border-radius:var(--radius-lg);padding:60px 20px;text-align:center"><i class="ti ti-flag" style="font-size:40px;color:var(--text-3);display:block;margin-bottom:12px"></i><strong style="color:var(--text-2);font-size:15px">No flagged teams</strong></div>';return;}
    el.innerHTML=`<div class="table-wrap"><table><thead><tr><th>Team</th><th>Country</th><th>Balance</th><th>Flag reason</th><th>Actions</th></tr></thead><tbody>${flagged.map(t=>`
      <tr onclick="App.openPanel('${t.id}')">
        <td><div class="td-name">${esc(t.team_name)}</div></td>
        <td>${esc(t.country||'—')}</td>
        <td class="td-balance amber">€${fmtMoney(t.balance_eur)}</td>
        <td>${t.balance_eur===0?'<span class="badge badge-blue">Zero balance</span>':t.amount_paid_eur>0?'<span class="badge badge-amber">Payment detected</span>':'<span class="badge badge-grey">Review needed</span>'}</td>
        <td onclick="event.stopPropagation()" style="display:flex;gap:6px;padding:11px 14px">
          <button class="btn btn-sm btn-default" onclick="App.resolveFlag('${t.id}','paid')"><i class="ti ti-check"></i> Mark paid</button>
          <button class="btn btn-sm btn-ghost" onclick="App.resolveFlag('${t.id}','unpaid')">Unpaid</button>
        </td>
      </tr>`).join('')}</tbody></table></div>`;
  }

  async function resolveFlag(teamId, status) {
    const team=state.teams.find(t=>t.id===teamId); if(!team) return;
    await sb.from('teams').update({payment_status:status,updated_at:new Date().toISOString()}).eq('id',teamId);
    await logActivity(teamId,team.team_name,'status_changed',`Flag resolved — ${status}`,'flagged',status);
    team.payment_status=status;
    renderFlaggedPage();renderTable();renderKPIs();updateBadges();
    toast('Updated','success');
  }

  // ── ADMINS PAGE ──
  // Gebruikersbeheer werkt op organisation_members. De oude tabel admins is
  // na de multi-tenant migratie alleen nog historie. Rollen: owner, admin,
  // editor, viewer. Wat een rol mag, staat in de RLS-policies, niet hier.
  const ROL_LABELS = { owner:'Owner', admin:'Admin', editor:'Editor', viewer:'Viewer' };

  async function renderAdminsPage() {
    const el = document.getElementById('adminsList');
    if (!el) return;
    const { data, error } = await metEigenOrg(sb.from('organisation_members')
      .select('*')).order('created_at', { ascending: true });
    if (error) {
      el.innerHTML = `<p style="font-size:13px;color:var(--red)">Kon gebruikers niet laden: ${esc(error.message)}</p>`;
      return;
    }
    if (!data?.length) { el.innerHTML='<p style="font-size:13px;color:var(--text-3)">No users found.</p>'; return; }
    beheerLeden = data;
    const ts = await sb.rpc('tweestap_status', { p_org: state.currentOrgId });
    const tweestapStatus = Object.fromEntries((ts.error ? [] : ts.data || []).map(r => [r.email, r.tweede_factor]));
    el.innerHTML = data.map(a => {
      const role = a.role || 'viewer';
      const isPrimary = a.email === PRIMARY_ADMIN;
      const isSelf = a.email === state.currentUserEmail.toLowerCase();
      // Owner maken of een owner aanpassen kan alleen een owner. De database
      // weigert het anders toch (trigger zz_bewaak_owner).
      const ownerBeheer = router.heeft('org.manage');
      const roleOpts = (ownerBeheer ? ['owner','admin','editor','viewer'] : ['admin','editor','viewer'])
        .map(r => `<option value="${r}" ${role===r?'selected':''}>${ROL_LABELS[r]}</option>`)
        .join('');
      const aanpasbaar = !isPrimary && !isSelf && (role !== 'owner' || ownerBeheer);
      const inactief = a.status && a.status !== 'active'
        ? `<span style="font-size:11px;color:var(--text-3)">${esc(a.status)}</span>` : '';
      return `<div class="admin-row">
        <div class="admin-avatar">${a.email.charAt(0).toUpperCase()}</div>
        <div style="flex:1;min-width:0">
          <div style="font-size:13px;font-weight:500;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${esc(a.email)}</div>
        </div>
        ${inactief}
        ${tweestapStatus[a.email?.toLowerCase()] === false && (role === 'owner' || role === 'admin') ? `<span class="badge badge-amber" title="${esc(t_('beheer.geentweestap.uitleg'))}"><i class="ti ti-shield-off" style="font-size:10px"></i> ${esc(t_('beheer.geentweestap'))}</span>` : ''}
        <span class="role-badge ${role}">${ROL_LABELS[role]||role}</span>
        ${aanpasbaar ? `
          <select class="form-input" style="width:105px;padding:5px 8px;font-size:12px" onchange="App.changeAdminRole('${esc(a.id)}',this.value)">${roleOpts}</select>
          <button class="btn btn-ghost btn-sm" onclick="App.removeAdmin('${esc(a.id)}')" title="${esc(t_('beheer.verwijderen'))}"><i class="ti ti-trash" style="color:var(--red)"></i></button>
        ` : `<span style="font-size:11px;color:var(--text-3);padding:0 4px">${isPrimary ? 'Primary' : isSelf ? 'Jij' : ''}</span>`}
      </div>`;
    }).join('');
    await renderInvites();
    await renderConsentList('orgConsentList', false);
    await renderOrgPlatformToegang();
    await renderOrgAuditLog();
    await tweestap.renderKaart(sb, state.currentOrgId);
    // Eigenaarschap overdragen: alleen voor de owner.
    const kaart = document.getElementById('eigendomKaart');
    if (kaart) { kaart.hidden = !router.heeft('org.manage'); if (!kaart.hidden) await eigendom.renderOverdracht(kaart); }
  }

  // Beperkt een query op organisation_members tot de organisatie waarin je nu
  // werkt. Zonder dit raakte een wijziging of verwijdering elke organisatie
  // waar de RLS je bij liet, en voor platformbeheer is dat elke organisatie.
  function metEigenOrg(query) {
    return state.currentOrgId ? query.eq('organisation_id', state.currentOrgId) : query;
  }

  // Rol wijzigen en verwijderen lopen via de dispatcher: met reden, en bij
  // vierogen pas na goedkeuring door een tweede bevoegde.
  let beheerLeden = [];
  async function beheerVoorstel(handeling, gegevens, reden) {
    const { data, error } = await sb.rpc('dispatch', { p_handeling: handeling, p_gegevens: gegevens, p_reden: reden });
    if (error) { toast(dispatchFout(error), 'error'); return; }
    toast(t_(data?.status === 'wacht' ? 'vierogen.ingediend' : 'vierogen.uitgevoerd'), 'success');
    await goedkeuringen.vernieuwTeller();
  }

  async function changeAdminRole(id, role) {
    const lid = beheerLeden.find(m => m.id === id);
    if (!lid || lid.email === PRIMARY_ADMIN) { toast(t_('beheer.primair'), 'error'); await renderAdminsPage(); return; }
    const r = await vraagReden({ titel: t_('beheer.rol.titel'),
      uitleg: t_('beheer.rol.uitleg', { email: lid.email, van: ROL_LABELS[lid.role] || lid.role, naar: ROL_LABELS[role] || role }),
      voorbeeld: t_('reden.voorbeeld.gebruiker') });
    if (r) await beheerVoorstel('user.role', { member_id: id, rol: role }, r.reden);
    await renderAdminsPage();
  }

  // Iemand toevoegen gaat via een uitnodiging, niet door direct een lid aan te
  // maken. De uitgenodigde aanvaardt eerst de voorwaarden, en die aanvaarding
  // wordt vastgelegd. Dat is het hele punt van deze route.
  async function inviteCall(action, payload = {}) {
    const { data, error } = await sb.functions.invoke('invitations', { body: { action, ...payload } });
    if (error) {
      let melding = error.message || 'Aanroep mislukt';
      try {
        const body = await error.context?.json?.();
        if (body?.error) melding = body.error;
      } catch { /* niet leesbaar */ }
      throw new Error(melding);
    }
    if (data?.error) throw new Error(data.error);
    return data;
  }

  async function addAdmin() {
    const email = document.getElementById('newAdminEmail')?.value.trim().toLowerCase();
    const role = document.getElementById('newAdminRole')?.value || 'editor';
    if (!email || !email.includes('@')) { toast('Vul een geldig e-mailadres in','error'); return; }
    try {
      const res = await inviteCall('create', { email, role });
      const link = `${location.origin}${location.pathname}?invite=${encodeURIComponent(res.token)}`;
      document.getElementById('newAdminEmail').value = '';
      document.getElementById('inviteResult').innerHTML = `
        <div class="banner banner-green" style="margin-top:12px">
          <i class="ti ti-check"></i>
          <span>Uitnodiging aangemaakt voor ${esc(email)}. Stuur deze link zelf naar die persoon, hij is 14 dagen geldig en werkt één keer.</span>
        </div>
        <div class="invite-link">
          <span style="flex:1" id="inviteLinkText">${esc(link)}</span>
          <button class="btn btn-default btn-sm" onclick="App.copyInviteLink()"><i class="ti ti-copy"></i> Kopieer</button>
        </div>`;
      await renderInvites();
    } catch (e) {
      toast(e.message, 'error');
    }
  }

  function copyInviteLink() {
    const el = document.getElementById('inviteLinkText');
    if (!el) return;
    navigator.clipboard.writeText(el.textContent);
    toast('Link gekopieerd','success');
  }

  async function renderInvites() {
    const el = document.getElementById('invitesList');
    if (!el) return;
    const { data } = await sb.from('invitations')
      .select('*').is('accepted_at', null).is('revoked_at', null)
      .order('created_at', { ascending: false });
    if (!data?.length) {
      el.innerHTML = '<p style="font-size:12px;color:var(--text-3)">Geen openstaande uitnodigingen.</p>';
      return;
    }
    const nu = Date.now();
    el.innerHTML = data.map(i => {
      const verlopen = new Date(i.expires_at).getTime() < nu;
      return `<div class="admin-row">
        <div class="admin-avatar">${i.email.charAt(0).toUpperCase()}</div>
        <div style="flex:1;min-width:0">
          <div style="font-size:13px;font-weight:500">${esc(i.email)}</div>
          <div style="font-size:11px;color:var(--text-3)">
            ${verlopen ? 'verlopen op' : 'geldig tot'} ${new Date(i.expires_at).toLocaleDateString('nl-NL')}
          </div>
        </div>
        <span class="role-badge ${i.role}">${ROL_LABELS[i.role]||i.role}</span>
        <button class="btn btn-ghost btn-sm" onclick="App.revokeInvite('${i.id}')" title="Intrekken">
          <i class="ti ti-x" style="color:var(--red)"></i>
        </button>
      </div>`;
    }).join('');
  }

  async function revokeInvite(id) {
    try {
      await inviteCall('revoke', { invitation_id: id });
      toast('Uitnodiging ingetrokken','success');
      await renderInvites();
    } catch (e) {
      toast(e.message,'error');
    }
  }

  async function removeAdmin(id) {
    const lid = beheerLeden.find(m => m.id === id);
    if (!lid || lid.email === PRIMARY_ADMIN) { toast(t_('beheer.primair'), 'error'); return; }
    const r = await vraagReden({ titel: t_('beheer.verwijderen'), uitleg: t_('beheer.verwijderen.uitleg', { email: lid.email }),
      knop: t_('beheer.verwijderen'), gevaar: true, voorbeeld: t_('reden.voorbeeld.gebruiker') });
    if (r) await beheerVoorstel('user.remove', { member_id: id }, r.reden);
    await renderAdminsPage();
  }

  // ── CONFIRM EXPIRED ──
  function confirmMarkExpired(teamId) {
    const team=state.teams.find(t=>t.id===teamId);
    showConfirm('Mark as expired',`Mark ${team?.team_name}'s registration as expired? This cannot be undone.`,async()=>{
      await sb.from('teams').update({payment_status:'expired',updated_at:new Date().toISOString()}).eq('id',teamId);
      await logActivity(teamId,team.team_name,'status_changed','Registration expired — deadline passed','unpaid','expired');
      team.payment_status='expired';
      renderTable();renderKPIs();updateBadges();
      if(currentTeamId===teamId) await renderPanelBody(state.teams.find(t=>t.id===currentTeamId));
      toast('Marked as expired','success');
    });
  }

  // ── ACTIONS DROPDOWN ──
  function showActions(teamId, btn) {
    closeDropdown();
    const team = state.teams.find(t=>t.id===teamId);
    const settled = team && team.settlement && team.settlement !== 'none';
    const rect=btn.getBoundingClientRect();
    const dd=document.createElement('div');
    dd.className='dropdown'; dd.id='activeDropdown';
    dd.style.cssText=`top:${rect.bottom+4}px;right:${window.innerWidth-rect.right}px`;
    dd.innerHTML=`
      <button class="dropdown-item" onclick="App.openPanel('${teamId}');App.closeDropdown()"><i class="ti ti-eye"></i> View details</button>
      <button class="dropdown-item" onclick="App.openStripeModal('${teamId}');App.closeDropdown()"><i class="ti ti-bolt"></i> Generate Stripe link</button>
      <button class="dropdown-item" onclick="App.openPanel('${teamId}');setTimeout(()=>App.openEmailModal(),150);App.closeDropdown()"><i class="ti ti-mail"></i> Email templates</button>
      <div class="dropdown-divider"></div>
      <button class="dropdown-item" onclick="App.quickStatus('${teamId}','paid');App.closeDropdown()"><i class="ti ti-check"></i> Mark as paid</button>
      <button class="dropdown-item" onclick="App.openAdjustModal('${teamId}');App.closeDropdown()"><i class="ti ti-adjustments-dollar"></i> Adjust balance</button>
      ${settled
        ? `<button class="dropdown-item" onclick="App.confirmReverseSettlement('${teamId}');App.closeDropdown()"><i class="ti ti-arrow-back-up"></i> Undo correction</button>`
        : `<button class="dropdown-item" onclick="App.openWriteOffModal('${teamId}');App.closeDropdown()"><i class="ti ti-receipt-off"></i> No show · write off</button>`}
      <button class="dropdown-item" onclick="App.archiveSingle('${teamId}');App.closeDropdown()"><i class="ti ti-archive"></i> Archive</button>
      <button class="dropdown-item danger" onclick="App.confirmMarkExpired('${teamId}');App.closeDropdown()"><i class="ti ti-user-x"></i> Mark as expired</button>`;
    document.getElementById('dropdownContainer').appendChild(dd);
    setTimeout(()=>document.addEventListener('click',closeDropdown,{once:true}),10);
  }

  function closeDropdown() { const d=document.getElementById('activeDropdown'); if(d) d.remove(); }

  async function quickStatus(teamId, status) {
    const team=state.teams.find(t=>t.id===teamId); if(!team) return;
    const prev=team.payment_status;
    const update={payment_status:status,updated_at:new Date().toISOString()};
    let extra='';
    if(status==='paid'){
      // The corrected amount is what the club actually owes, so that is what
      // gets recorded as received, not the raw Sportdata fee.
      const billed=billedOf(team);
      if(Number(team.amount_paid_eur||0)<=0){ update.amount_paid_eur=billed; extra=` · recorded €${fmtMoney(billed)}`; }
      update.balance_eur=0;
      update.paid_at=new Date().toISOString();
    }
    await sb.from('teams').update(update).eq('id',teamId);
    await logActivity(teamId,team.team_name,'status_changed',`Status set to ${status}${extra}`,prev,status);
    Object.assign(team,update);
    renderTable();renderKPIs();updateBadges();
    if(document.getElementById('pageDashboard')?.classList.contains('active')) renderDashboard();
    toast('Updated','success');
  }

  // ── SETTINGS ──
  async function renderSettingsStripe() {
    const el = document.getElementById('stripeStatus');
    const acties = document.getElementById('stripeActions');
    if (!el) return;
    el.innerHTML = '<div class="spinner"></div>';
    if (acties) acties.innerHTML = '';

    let st = null;
    try { st = await connectCall('status'); } catch (e) {
      el.innerHTML = `<div class="banner banner-red"><i class="ti ti-alert-circle"></i>
        <span>${esc(e.message)}</span></div>`;
      return;
    }
    stripeConfigured = !!st?.connected;

    if (!st.platform_ready) {
      el.innerHTML = `<div class="banner banner-red"><i class="ti ti-alert-circle"></i>
        <span>Het platform heeft Connect nog niet ingesteld. Neem contact op met de beheerder.</span></div>`;
      return;
    }

    const tarief = st.fee || { fee_bps: 0, fee_fixed_cents: 0 };
    const tariefTekst = (tarief.fee_bps === 0 && tarief.fee_fixed_cents === 0)
      ? 'Er wordt niets ingehouden op jullie betalingen.'
      : `Platformvergoeding: ${(tarief.fee_bps / 100).toFixed(2).replace('.', ',')} procent`
        + (tarief.fee_fixed_cents ? ` plus \u20ac${(tarief.fee_fixed_cents / 100).toFixed(2).replace('.', ',')}` : '')
        + ' per betaling.';

    if (st.connected) {
      el.innerHTML = `<div class="banner banner-green"><i class="ti ti-circle-check"></i>
        <span>Gekoppeld aan Stripe-account <code>${esc(st.account_id)}</code>.
        Betalingen komen rechtstreeks op jullie rekening. ${tariefTekst}</span></div>`;
      if (acties && st.can_manage) {
        acties.innerHTML = `<button class="btn btn-default btn-sm" onclick="App.disconnectStripe()">
          <i class="ti ti-unlink"></i> Koppeling verbreken</button>`;
      }
    } else {
      el.innerHTML = `<div class="banner banner-amber"><i class="ti ti-alert-triangle"></i>
        <span>Nog geen Stripe-account gekoppeld. Zonder koppeling kun je geen betaallinks maken. ${tariefTekst}</span></div>`;
      if (acties && st.can_manage) {
        acties.innerHTML = `<button class="btn btn-primary btn-sm" onclick="App.connectStripe()">
          <i class="ti ti-brand-stripe"></i> Verbind met Stripe</button>`;
      }
    }
  }

  // ── PRICING & EVENT SETTINGS ──

  function currentEvent() { return state.events.find(e => e.id === state.currentEventId) || null; }

  // Single source of truth for the early bird decision.
  // A team qualifies only if it was registered on or before the deadline
  // AND payment was received on or before that same deadline.
  function isEarlyBird(team, ev) {
    const evt = ev || currentEvent();
    if (!evt?.early_bird_deadline) return false;
    const deadline = new Date(evt.early_bird_deadline);
    deadline.setHours(23,59,59,999);
    const registered = new Date(team?.registration_date || team?.created_at || Date.now());
    if (registered > deadline) return false;
    if (team?.payment_status === 'paid') {
      const paidAt = new Date(team.paid_at || team.updated_at || Date.now());
      return paidAt <= deadline;
    }
    // Not paid yet: still eligible while the deadline has not passed
    return new Date() <= deadline;
  }

  function renderPricingSettings() {
    const el = document.getElementById('pricingSettings');
    if (!el) return;
    const ev = currentEvent();
    if (!ev) { el.innerHTML = '<p style="font-size:13px;color:var(--text-3)">Select an event first.</p>'; return; }
    const scoped = state.teams.filter(t => t.event_id === ev.id && !t.archived_at);
    const ebNow  = scoped.filter(t => isEarlyBird(t, ev)).length;
    const ebDate = ev.early_bird_deadline || '';
    const passed = ebDate && new Date(ebDate) < new Date();
    el.innerHTML = `
      <div style="font-size:12px;color:var(--text-3);margin-bottom:12px">Event: <strong style="color:var(--text-2)">${esc(ev.name)}</strong></div>
      <div class="form-group">
        <label class="form-label">Early bird deadline</label>
        <input class="form-input" id="setEbDeadline" type="date" value="${ebDate}" onchange="App.previewSettingsEbImpact()">
        <span class="form-hint">
          ${ebDate
            ? (passed
                ? 'This deadline has passed. All registrations now follow the regular email sequence.'
                : 'Registrations created on or before this date, and paid on or before it, follow the early bird sequence.')
            : 'Not set. Every registration follows the regular email sequence.'}
        </span>
      </div>
      <div style="font-size:12px;color:var(--text-2);padding:9px 12px;background:var(--surface-2);border:1px solid var(--border);border-radius:var(--radius);margin-bottom:10px">
        <i class="ti ti-info-circle" style="color:var(--text-3)"></i>
        Currently <strong>${ebNow}</strong> of ${scoped.length} registration${scoped.length===1?'':'s'} qualify for the early bird sequence.
      </div>
      <div id="setEbImpact" style="display:none"></div>
      <div style="display:flex;gap:8px;align-items:center;margin-top:4px">
        <button class="btn btn-primary btn-sm" onclick="App.savePricingSettings()"><i class="ti ti-check"></i> Save deadline</button>
        <span style="font-size:11px;color:var(--text-3)">Fees stay exactly as imported from Sportdata.</span>
      </div>`;
  }

  // Live impact preview for the Settings card.
  function previewSettingsEbImpact() {
    const ev = currentEvent();
    const box = document.getElementById('setEbImpact');
    if (!ev || !box) return;
    const newDate = document.getElementById('setEbDeadline').value || null;
    if ((ev.early_bird_deadline || null) === newDate) { box.style.display = 'none'; return; }
    const scoped = state.teams.filter(t => t.event_id === ev.id && !t.archived_at);
    const simulated = { ...ev, early_bird_deadline: newDate };
    const moved = scoped.filter(t => isEarlyBird(t, ev) !== isEarlyBird(t, simulated)).length;
    box.style.cssText = 'display:block;margin-bottom:10px;padding:11px 13px;background:#fffbeb;border:1px solid #fde68a;border-radius:var(--radius);font-size:12px;line-height:1.6;color:#92400e';
    box.innerHTML = moved > 0
      ? `<strong><i class="ti ti-alert-triangle"></i> ${moved} of ${scoped.length} registration${scoped.length===1?'':'s'} would switch email sequence.</strong><br>
         Fees and balances are untouched, but these teams will receive different wording from now on.`
      : `<i class="ti ti-info-circle"></i> No registrations switch sequence with this date.`;
  }

  async function savePricingSettings() {
    const ev = currentEvent();
    if (!ev) { toast('No event selected','error'); return; }
    const ebDate = document.getElementById('setEbDeadline').value || null;
    if ((ev.early_bird_deadline || null) === ebDate) { toast('No changes to save'); return; }

    const scoped = state.teams.filter(t => t.event_id === ev.id && !t.archived_at);
    const simulated = { ...ev, early_bird_deadline: ebDate };
    const moved = scoped.filter(t => isEarlyBird(t, ev) !== isEarlyBird(t, simulated)).length;

    const proceed = async () => {
      const { error } = await sb.from('events').update({ early_bird_deadline: ebDate }).eq('id', ev.id);
      if (error) { toast('Save failed: ' + error.message, 'error'); return; }
      const oldVal = ev.early_bird_deadline || 'not set';
      ev.early_bird_deadline = ebDate;
      await logActivity(null, 'System', 'settings_changed',
        `Early bird deadline changed for ${ev.name}`
        + (moved ? ` — ${moved} registration${moved===1?'':'s'} switched email sequence` : ''),
        String(oldVal), String(ebDate || 'not set'));
      renderPricingSettings();
      renderEventsSettings();
      if (document.getElementById('pageDashboard')?.classList.contains('active')) renderDashboard();
      toast('Early bird deadline saved', 'success');
    };

    if (moved > 0) {
      showConfirm('Change early bird deadline',
        `${moved} of ${scoped.length} registrations will switch email sequence.\n\nFees and balances stay exactly as imported from Sportdata. Only the wording of future emails changes.\n\nContinue?`,
        proceed);
    } else {
      await proceed();
    }
  }

  // ── EDIT EVENT ──
  let editingEventId = null;

  function openEditEventModal(eventId) {
    const ev = state.events.find(e => e.id === eventId);
    if (!ev) return;
    editingEventId = eventId;
    document.getElementById('edEventName').value = ev.name || '';
    document.getElementById('edEventLocation').value = ev.location || '';
    document.getElementById('edEventDate').value = ev.tournament_date || '';
    document.getElementById('edEventDeadline').value = ev.payment_deadline || '';
    document.getElementById('edEventEarlyBird').value = ev.early_bird_deadline || '';
    document.getElementById('edImpactWarning').style.display = 'none';
    document.getElementById('editEventModal').classList.add('open');
  }

  function closeEditEventModal() {
    document.getElementById('editEventModal').classList.remove('open');
    editingEventId = null;
  }

  // Shows how many registrations would change category under a new deadline.
  function previewEarlyBirdImpact() {
    const ev = state.events.find(e => e.id === editingEventId);
    const box = document.getElementById('edImpactWarning');
    if (!ev || !box) return;
    const newDate = document.getElementById('edEventEarlyBird').value || null;
    if ((ev.early_bird_deadline || null) === newDate) { box.style.display = 'none'; return; }

    const scoped = state.teams.filter(t => t.event_id === ev.id && !t.archived_at);
    const simulated = { ...ev, early_bird_deadline: newDate };
    let moved = 0;
    scoped.forEach(t => { if (isEarlyBird(t, ev) !== isEarlyBird(t, simulated)) moved++; });

    box.style.display = 'block';
    box.style.cssText = 'display:block;margin-top:10px;padding:11px 13px;background:#fffbeb;border:1px solid #fde68a;border-radius:var(--radius);font-size:12px;line-height:1.6;color:#92400e';
    box.innerHTML = moved > 0
      ? `<strong><i class="ti ti-alert-triangle"></i> ${moved} of ${scoped.length} registration${scoped.length===1?'':'s'} would fall into a different pricing category.</strong><br>
         Existing fees and balances stay untouched, but these teams will receive a different email sequence from now on.`
      : `<i class="ti ti-info-circle"></i> No registrations change category with this date. Existing fees stay untouched.`;
  }

  async function saveEditedEvent() {
    const ev = state.events.find(e => e.id === editingEventId);
    if (!ev) return;
    const next = {
      name: document.getElementById('edEventName').value.trim(),
      location: document.getElementById('edEventLocation').value.trim(),
      tournament_date: document.getElementById('edEventDate').value || null,
      payment_deadline: document.getElementById('edEventDeadline').value || null,
      early_bird_deadline: document.getElementById('edEventEarlyBird').value || null
    };
    if (!next.name) { toast('Event name is required','error'); return; }

    const labels = {
      name:'Event name', location:'Location', tournament_date:'Tournament date',
      payment_deadline:'Payment deadline', early_bird_deadline:'Early bird deadline'
    };
    const diffs = Object.keys(next).filter(k => (ev[k]||null) !== (next[k]||null));
    if (!diffs.length) { closeEditEventModal(); toast('No changes to save'); return; }

    const summary = diffs.map(k => `${labels[k]}: ${ev[k]||'empty'} to ${next[k]||'empty'}`).join('\n');
    showConfirm('Save event changes',
      `The following will change:\n\n${summary}\n\nExisting registrations keep their current fees and balances. Every change is written to the activity log.`,
      async () => {
        const { error } = await sb.from('events').update(next).eq('id', ev.id);
        if (error) { toast('Save failed: ' + error.message,'error'); return; }
        for (const k of diffs) {
          await logActivity(null, 'System', 'settings_changed',
            `${labels[k]} changed for ${ev.name}`, String(ev[k]||'empty'), String(next[k]||'empty'));
        }
        Object.assign(ev, next);
        closeEditEventModal();
        renderEventSelect();
        renderEventsSettings();
        renderPricingSettings();
        checkDeadlines();
        if (document.getElementById('pageDashboard')?.classList.contains('active')) renderDashboard();
        toast(`Event updated (${diffs.length} change${diffs.length>1?'s':''} logged)`, 'success');
      });
  }

  function renderEventsSettings() {
    const el=document.getElementById('eventsSettingsList');
    if(!state.events.length){el.innerHTML='<p style="font-size:13px;color:var(--text-3)">No events.</p>';return;}
    el.innerHTML=state.events.map(e=>{
      return `
      <div style="display:flex;align-items:center;gap:10px;padding:10px 0;border-bottom:1px solid var(--border)">
        <div style="flex:1;min-width:0">
          <div style="font-size:13px;font-weight:500">${esc(e.name)}${e.id===state.currentEventId?' <span class="badge badge-blue" style="margin-left:4px">Current</span>':''}</div>
          <div style="font-size:11px;color:var(--text-3)">${esc(e.location||'')}${e.location?' · ':''}${fmtDate(e.tournament_date)||'no date'} · Payment deadline ${fmtDate(e.payment_deadline)||'not set'}</div>
          <div style="font-size:11px;margin-top:2px;${e.early_bird_deadline?'color:var(--text-3)':'color:var(--amber)'}">${e.early_bird_deadline?`Early bird until ${fmtDate(e.early_bird_deadline)}`:'No early bird deadline set'}</div>
        </div>
        <button class="btn btn-default btn-sm" onclick="App.openEditEventModal('${e.id}')"><i class="ti ti-edit"></i> Edit</button>
      </div>`;}).join('');
  }

  function exportJSON() {
    const blob=new Blob([JSON.stringify({events: state.events,teams: state.teams,exported_at:new Date().toISOString()},null,2)],{type:'application/json'});
    const a=document.createElement('a');
    a.href=URL.createObjectURL(blob);
    a.download=`tornema_export_${new Date().toISOString().split('T')[0]}.json`;
    a.click();
  }

  // ── NAV ──
  // ── DASHBOARD ──
  function renderDashboard() {
    const el = document.getElementById('dashboardContent');
    if (!el) return;
    const ev = state.events.find(e => e.id === state.currentEventId);
    if (!state.currentEventId || !ev) {
      el.innerHTML = `<div style="text-align:center;padding:60px 20px;color:var(--text-3)">
        <i class="ti ti-layout-dashboard" style="font-size:40px;display:block;margin-bottom:12px"></i>
        <strong style="font-size:15px;color:var(--text-2)">No event selected</strong>
        <p style="font-size:13px;margin-top:6px">Create or select an event to see your dashboard.</p>
      </div>`;
      return;
    }
    const active = state.teams.filter(t => !t.archived_at);
    // Billed is what Sportdata invoices, before any correction of ours.
    const totalBilled  = active.reduce((s,t) => s+(t.entry_fee_eur||0)-(t.discount_eur||0), 0);

    // Money actually received. A team marked as paid but with an empty
    // amount_paid_eur (legacy data, manual status change) still counts for its
    // billed amount, but NEVER for a written off team: nothing came in there,
    // and counting it would report a no-show as income.
    const paidAmountOf = t => {
      const recorded = Number(t.amount_paid_eur || 0);
      if (recorded > 0) return recorded;
      if (isWrittenOff(t)) return 0;
      if (t.payment_status === 'paid') return billedOf(t);
      return 0;
    };
    // Everything we decided not to collect: write-offs and downward
    // corrections. Reported separately so the derivation stays visible instead
    // of quietly disappearing from the billed total.
    const writtenOffOf = t => Math.max(0, round2(t.manual_adjustment_eur));
    const totalPaid     = active.reduce((s,t) => s + paidAmountOf(t), 0);
    const totalWritten  = active.reduce((s,t) => s + writtenOffOf(t), 0);
    // A written off balance is not outstanding: it will never be collected.
    const totalOpen    = active.reduce((s,t) =>
      s + ((t.payment_status === 'paid' || isWrittenOff(t)) ? 0 : Math.max(0, Number(t.balance_eur||0))), 0);
    const totalAthletes = active.reduce((s,t) => s+(t.athlete_count||0), 0);
    const paidCount    = active.filter(t=>matchStatusFilter(t,'paid')).length;
    // Counts every team contributing to totalWritten, not just the no-shows,
    // otherwise the card can read "0 written off · €120".
    const writtenCount = active.filter(t => writtenOffOf(t) > 0).length;
    const noShowCount  = active.filter(isWrittenOff).length;
    const unpaidCount  = active.filter(t=>matchStatusFilter(t,'unpaid')||matchStatusFilter(t,'flagged')).length;
    const countries    = [...new Set(active.map(t=>t.country).filter(Boolean))];
    const paidPct      = totalBilled > 0 ? Math.round((totalPaid/totalBilled)*100) : 0;
    const writtenPct   = totalBilled > 0 ? Math.round((totalWritten/totalBilled)*100) : 0;
    const today        = new Date();

    // Countdown helpers
    const daysTo = (d) => d ? Math.ceil((new Date(d)-today)/(864e5)) : null;
    const daysToEB    = daysTo(ev.early_bird_deadline);
    const daysToPay   = daysTo(ev.payment_deadline);
    const daysToEvent = daysTo(ev.tournament_date);
    const countItem = (days, label, warn) => {
      const past = days !== null && days < 0;
      const urgent = days !== null && days >= 0 && days < 14 && warn;
      const color = past ? 'var(--text-3)' : urgent ? 'var(--amber)' : 'var(--text-1)';
      const bg = urgent ? 'background:#fffbeb;border-color:#fde68a' : '';
      return `<div class="dash-count-item" style="${bg}">
        <div class="dash-count-num" style="color:${color}">${past?'Past':days}</div>
        <div class="dash-count-lbl">${label}</div>
      </div>`;
    };

    // Growth chart data
    const sorted = [...active].sort((a,b)=>new Date(a.created_at)-new Date(b.created_at));
    const chartData = buildGrowthData(sorted);

    el.innerHTML = `
      <!-- Event header + countdowns -->
      <div style="display:flex;align-items:flex-start;justify-content:space-between;flex-wrap:wrap;gap:12px;margin-bottom:20px">
        <div>
          <div style="font-size:18px;font-weight:600;color:var(--text-1)">${esc(ev.name)}</div>
          <div style="font-size:12px;color:var(--text-3);margin-top:3px">${esc(ev.location||'')}${ev.location&&ev.tournament_date?' · ':''}${fmtDate(ev.tournament_date)||''}</div>
        </div>
        <div class="dash-countdown">
          ${daysToEB!==null?countItem(daysToEB,'Days to EB',true):''}
          ${daysToPay!==null?countItem(daysToPay,'Payment deadline',true):''}
          ${daysToEvent!==null?countItem(daysToEvent,'Days to event',false):''}
        </div>
      </div>

      <!-- KPI cards -->
      <div class="dash-kpi-grid">
        <div class="dash-kpi green">
          <div class="dash-kpi-label">Total paid</div>
          <div class="dash-kpi-value">€${fmtMoney(totalPaid)}</div>
          <div class="dash-kpi-sub">${paidCount} team${paidCount!==1?'s':''} confirmed</div>
        </div>
        <div class="dash-kpi amber">
          <div class="dash-kpi-label">Outstanding</div>
          <div class="dash-kpi-value">€${fmtMoney(totalOpen)}</div>
          <div class="dash-kpi-sub">${unpaidCount} team${unpaidCount!==1?'s':''} unpaid</div>
        </div>
        <div class="dash-kpi ${totalWritten>0?'red':''}">
          <div class="dash-kpi-label">Written off</div>
          <div class="dash-kpi-value">€${fmtMoney(totalWritten)}</div>
          <div class="dash-kpi-sub">${writtenCount} team${writtenCount!==1?'s':''}${noShowCount?` · ${noShowCount} no-show${noShowCount!==1?'s':''}`:''} · ${writtenPct}% of billed</div>
        </div>
        <div class="dash-kpi">
          <div class="dash-kpi-label">Total billed</div>
          <div class="dash-kpi-value">€${fmtMoney(totalBilled)}</div>
          <div class="dash-kpi-sub">${paidPct}% received</div>
        </div>
        <div class="dash-kpi blue">
          <div class="dash-kpi-label">Teams</div>
          <div class="dash-kpi-value">${active.length}</div>
          <div class="dash-kpi-sub">${countries.length} countr${countries.length!==1?'ies':'y'}</div>
        </div>
        <div class="dash-kpi">
          <div class="dash-kpi-label">Entries</div>
          <div class="dash-kpi-value">${totalAthletes}</div>
          <div class="dash-kpi-sub">across all registered teams</div>
        </div>
      </div>

      <!-- Payment progress -->
      <div class="dash-section">
        <div class="dash-section-title"><i class="ti ti-chart-bar" style="color:var(--accent)"></i> Payment progress</div>
        <div style="margin-bottom:16px">
          <div style="display:flex;justify-content:space-between;font-size:12px;color:var(--text-3);margin-bottom:6px">
            <span>€${fmtMoney(totalPaid)} received</span><span>${paidPct}% of €${fmtMoney(totalBilled)}</span>
          </div>
          <div style="height:10px;background:var(--surface-2);border-radius:5px;overflow:hidden;display:flex">
            <div style="height:100%;width:${paidPct}%;background:var(--green);transition:width .5s"></div>
            <div style="height:100%;width:${writtenPct}%;background:repeating-linear-gradient(45deg,var(--text-3),var(--text-3) 3px,transparent 3px,transparent 6px);transition:width .5s" title="Written off: €${fmtMoney(totalWritten)}"></div>
          </div>
          ${totalWritten>0?`<div style="font-size:11px;color:var(--text-3);margin-top:5px;display:flex;align-items:center;gap:5px">
            <span style="display:inline-block;width:10px;height:10px;border-radius:2px;background:repeating-linear-gradient(45deg,var(--text-3),var(--text-3) 3px,transparent 3px,transparent 6px)"></span>
            €${fmtMoney(totalWritten)} written off or corrected away and will not be collected
          </div>`:''}
        </div>
        ${['paid','partial','unpaid','flagged','expired','written_off'].map(status=>{
          // matchStatusFilter, not payment_status, so a written off team is
          // counted once under its own marking instead of under "paid".
          const cnt=active.filter(t=>matchStatusFilter(t,status)).length;
          if(!cnt) return '';
          const pct=active.length>0?Math.round((cnt/active.length)*100):0;
          const colors={paid:'var(--green)',partial:'#0e7490',unpaid:'var(--amber)',flagged:'var(--blue)',expired:'var(--red)',written_off:'var(--text-3)'};
          const labels={written_off:'written off'};
          return `<div class="dash-progress-row">
            <div class="dash-progress-label">${labels[status]||status}</div>
            <div class="dash-progress-bar"><div class="dash-progress-fill" style="width:${pct}%;background:${colors[status]}"></div></div>
            <div class="dash-progress-val">${cnt} team${cnt!==1?'s':''}</div>
          </div>`;
        }).join('')}
      </div>

      <!-- Growth chart -->
      <div class="dash-section">
        <div class="dash-section-title"><i class="ti ti-trending-up" style="color:var(--accent)"></i> Revenue growth — registrations over time</div>
        ${chartData.length>1 ? renderGrowthChart(chartData) : '<p style="font-size:13px;color:var(--text-3);text-align:center;padding:20px 0">Not enough data yet to show the growth chart.</p>'}
      </div>

      <!-- Countries -->
      ${countries.length ? `
      <div class="dash-section">
        <div class="dash-section-title"><i class="ti ti-world" style="color:var(--accent)"></i> Countries (${countries.length})</div>
        <div style="display:flex;flex-wrap:wrap;gap:8px">
          ${countries.sort().map(c=>{
            const {flag,iso}=getCountryFlag(c);
            const cnt=active.filter(t=>t.country===c).length;
            const ath=active.filter(t=>t.country===c).reduce((s,t)=>s+(t.athlete_count||0),0);
            return `<div style="display:flex;align-items:center;gap:6px;padding:5px 11px;background:var(--surface-2);border:1px solid var(--border);border-radius:99px;font-size:12px">
              <span style="font-size:15px">${flag}</span>
              <span style="font-weight:500">${esc(iso)}</span>
              <span style="color:var(--text-3)">${cnt}t · ${ath}a</span>
            </div>`;
          }).join('')}
        </div>
        <div style="font-size:11px;color:var(--text-3);margin-top:8px">t = teams · a = entries</div>
      </div>` : ''}
    `;
  }

  function buildGrowthData(sortedTeams) {
    if (!sortedTeams.length) return [];
    const weeks = {};
    sortedTeams.forEach(t => {
      const d = new Date(t.created_at);
      const day = d.getDay();
      const mon = new Date(d);
      mon.setDate(d.getDate()-(day===0?6:day-1));
      mon.setHours(0,0,0,0);
      const key = mon.toISOString().slice(0,10);
      if (!weeks[key]) weeks[key]={date:key,revenue:0,teams:0,athletes:0};
      weeks[key].revenue += (t.entry_fee_eur||0)-(t.discount_eur||0);
      weeks[key].teams++;
      weeks[key].athletes += t.athlete_count||0;
    });
    let cum={revenue:0,teams:0,athletes:0};
    return Object.values(weeks).sort((a,b)=>a.date.localeCompare(b.date)).map(w=>{
      cum.revenue+=w.revenue; cum.teams+=w.teams; cum.athletes+=w.athletes;
      return {date:w.date,revenue:cum.revenue,teams:cum.teams,athletes:cum.athletes,weekRevenue:w.revenue};
    });
  }

  function renderGrowthChart(data) {
    const W=560,H=200,PL=58,PR=16,PT=16,PB=36,cW=W-PL-PR,cH=H-PT-PB;
    const maxRev=Math.max(...data.map(d=>d.revenue),1);
    const xPos=i=>PL+(i/(data.length-1))*cW;
    const yRev=v=>PT+cH-(v/maxRev)*cH;
    const linePath=`M ${data.map((d,i)=>`${xPos(i)},${yRev(d.revenue)}`).join(' L ')}`;
    const areaPath=`M${xPos(0)},${PT+cH} L${data.map((d,i)=>`${xPos(i)},${yRev(d.revenue)}`).join(' L')} L${xPos(data.length-1)},${PT+cH} Z`;
    const yLabels=[0,.25,.5,.75,1].map(f=>({val:Math.round(maxRev*f),y:PT+cH-f*cH}));
    const step=Math.max(1,Math.floor(data.length/5));
    const xLabels=data.filter((_,i)=>i===0||i===data.length-1||i%step===0);
    const todayStr=new Date().toISOString().slice(0,10);
    const todayIdx=data.findIndex(d=>d.date>=todayStr);
    return `<svg viewBox="0 0 ${W} ${H}" xmlns="http://www.w3.org/2000/svg" style="width:100%;max-width:${W}px;display:block;overflow:visible">
      <defs><linearGradient id="rg" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stop-color="#0f6674" stop-opacity=".15"/>
        <stop offset="100%" stop-color="#0f6674" stop-opacity=".01"/>
      </linearGradient></defs>
      ${yLabels.map(l=>`
        <line x1="${PL}" y1="${l.y}" x2="${W-PR}" y2="${l.y}" stroke="#e4e4e0" stroke-width="1"/>
        <text x="${PL-6}" y="${l.y+4}" text-anchor="end" font-size="10" fill="#a1a1aa" font-family="monospace">€${l.val>=1000?Math.round(l.val/100)/10+'k':l.val}</text>
      `).join('')}
      <path d="${areaPath}" fill="url(#rg)"/>
      <path d="${linePath}" fill="none" stroke="#0f6674" stroke-width="2" stroke-linejoin="round" stroke-linecap="round"/>
      ${data.map((d,i)=>`<circle cx="${xPos(i)}" cy="${yRev(d.revenue)}" r="3.5" fill="#0f6674" stroke="#fff" stroke-width="1.5"><title>Week ${d.date}: €${fmtMoney(d.revenue)} · ${d.teams} teams · ${d.athletes} entries</title></circle>`).join('')}
      ${todayIdx>=0?`<line x1="${xPos(todayIdx)}" y1="${PT}" x2="${xPos(todayIdx)}" y2="${PT+cH}" stroke="#3b82f6" stroke-width="1.5" stroke-dasharray="4 3"/><text x="${xPos(todayIdx)}" y="${PT-4}" text-anchor="middle" font-size="9" fill="#3b82f6">today</text>`:''}
      ${xLabels.map(d=>`<text x="${xPos(data.indexOf(d))}" y="${H-6}" text-anchor="middle" font-size="10" fill="#a1a1aa" font-family="monospace">${d.date.slice(5).replace('-','/')}</text>`).join('')}
      <line x1="${PL}" y1="${PT}" x2="${PL}" y2="${PT+cH}" stroke="#e4e4e0" stroke-width="1"/>
      <line x1="${PL}" y1="${PT+cH}" x2="${W-PR}" y2="${PT+cH}" stroke="#e4e4e0" stroke-width="1"/>
    </svg>
    <div style="display:flex;gap:16px;margin-top:8px;font-size:11px;color:var(--text-3)">
      <span><span style="display:inline-block;width:12px;height:2px;background:#0f6674;border-radius:2px;vertical-align:middle;margin-right:4px"></span>Cumulative revenue</span>
      <span><span style="display:inline-block;width:12px;height:0;border-top:2px dashed #3b82f6;vertical-align:middle;margin-right:4px"></span>Today</span>
    </div>`;
  }

  function nav(page, opties = {}) {
    // Een pagina die deze rol niet mag openen, toont alleen lege tabellen of
    // foutmeldingen. Dan liever het dashboard.
    if (!router.magNaar(page)) page = router.eerstePagina();
    // Een pagina uit een andere module: eerst naar die module, met zijn eigen menu.
    if (router.moduleVan(page) !== router.module()) { wisselModule(router.moduleVan(page), page, opties); return; }
    router.onthoud(page, { vervang: !!opties.vervang, staat: { ctx: context.gekozen() } });
    huidigePagina = page;
    document.querySelectorAll('.nav-item').forEach(n=>n.classList.toggle('active',n.dataset.page===page));
    document.querySelectorAll('.page').forEach(p=>p.classList.remove('active'));
    document.getElementById('topbarTitle').textContent=router.titel(page);
    document.getElementById('btnAddTeam').style.display=page==='teams'?'flex':'none';
    document.getElementById('btnCheckAll').style.display=page==='teams'&&state.teams.some(t=>t.stripe_payment_link_id)?'flex':'none';
    document.getElementById(`page${page.charAt(0).toUpperCase()+page.slice(1)}`).classList.add('active');
    if(page==='dashboard') renderDashboard();
    if(page==='activity') renderActivityPage();
    if(page==='flagged') renderFlaggedPage();
    if(page==='admins') renderAdminsPage();
    if(page==='archived') { archiveSelectedIds=new Set(); renderArchivedPage(); }
    if(page==='clubs') { state.allClubs=[]; clubSelectedIds=new Set(); renderClubsPage(); loadClubsBadge(); }
    if(page==='templates') renderTemplatesPage();
    if(page==='leden') renderLeden();
    if(page==='activiteiten') activiteiten.render();
    if(page==='deelnemers') deelnemersMod.renderDeelnemers();
    if(page==='sporters') deelnemersMod.renderSporters();
    if(page==='toernooien') toernooienMod.render({
      openEditie: async id => { document.getElementById('eventSelect').value = id; await switchEvent(id); nav('dashboard'); },
      nieuweEditie: tid => openEventModal(tid),
      naAanmaken: loadEvents,
      kiesToernooi,
    });
    if(page==='clubregister') clubregister.render();
    if(page==='aansluitingen') aansluitingen.render();
    if(page==='verzoeken') verzoeken.render();
    if(page==='goedkeuringen') goedkeuringen.render();
    if(page==='settings'){renderSettingsStripe();renderSettingsMailchimp();renderEmailVerbruik();exportMod.renderKaart();organisatieMod.renderKaart(laadOrganisatie);}
    if(page==='toernooiinstellingen'){renderPricingSettings();renderEventsSettings();}
    tekenMenu();
  }

  // ── PLATFORMBEHEER ──
  // Dit is de kant van de leverancier. Of iemand hier binnenkomt bepaalt de
  // database: op platform_admins mag alleen een platformbeheerder lezen, dus
  // een lege uitkomst betekent geen toegang. Het menu-item verbergen is alleen
  // netjes, geen beveiliging.
  let isPlatformAdmin = false;

  async function bepaalPlatformrol() {
    const { data } = await sb.from('platform_admins').select('email').limit(1);
    isPlatformAdmin = !!data?.length;
    document.body.classList.toggle('platform-admin', isPlatformAdmin);
  }

  async function platformCall(action, payload = {}) {
    const { data, error } = await sb.functions.invoke('platform', { body: { action, ...payload } });
    if (error) {
      let melding = error.message || 'Aanroep mislukt';
      try {
        const body = await error.context?.json?.();
        if (body?.error) melding = body.error;
      } catch { /* niet leesbaar */ }
      throw new Error(melding);
    }
    if (data?.error) throw new Error(data.error);
    return data;
  }

  // Platformbeheer is een eigen omgeving, geen pagina tussen de evenementen
  // door. De zijbalk wisselt, de eventkiezer verdwijnt, en je komt binnen op
  // een overzicht in plaats van op een verzameling formulieren.
  // Alleen aan te roepen bij het opstarten in de context platformbeheer. Vanuit
  // een organisatie gaat het via naarPlatform: opnieuw laden in die context.
  async function openPlatform(pagina = 'dash') {
    if (!isPlatformAdmin) { toast('Geen platformbeheer', 'error'); return; }
    document.body.classList.add('platform-modus');
    const kiezer = document.getElementById('moduleKiezerWrap');
    if (kiezer) kiezer.hidden = true;
    document.getElementById('navOrg').style.display = 'none';
    document.getElementById('navPlatform').style.display = 'block';
    document.getElementById('orgOnly1').style.display = 'none';
    document.getElementById('btnAddTeam').style.display = 'none';
    document.getElementById('btnCheckAll').style.display = 'none';
    const banner = document.getElementById('bannerContainer');
    if (banner) banner.innerHTML = '';
    await zetPlatformMerk(true);
    navPlatform(router.platformRoute(pagina) ? pagina : 'dash', { vervang: true });
  }

  // Wisselen tussen een organisatie en platformbeheer is altijd een nieuwe
  // context: opnieuw laden, zodat er niets van de vorige blijft staan.
  function naarPlatform() { context.kies({ soort: 'platform' }, 'platform-dash'); }

  function closePlatform() {
    // Zonder eigen organisatie is er niets om naar terug te gaan: dan het startscherm.
    if (!state.currentOrgId) { context.naarStart(); return; }
    context.kies({ soort: 'org', org: state.currentOrgId });
  }

  // In platformbeheer sta je niet bij een klant maar bij de leverancier. De
  // organisatienaam hoort daar dus niet in de zijbalk; die zou suggereren dat
  // je nog binnen een klant aan het werk bent.
  async function zetPlatformMerk(aan) {
    const naam = document.querySelector('.brand-name');
    const sub = document.getElementById('brandSub');
    if (!naam || !sub) return;
    if (!aan) {
      naam.textContent = state.currentOrg?.name || 'Organisatie';
      sub.textContent = window.__merknaam || 'CRM';
      document.title = state.currentOrg?.name
        ? `${state.currentOrg.name} — ${window.__merknaam || 'CRM'}` : (window.__merknaam || 'CRM');
      return;
    }
    if (!window.__korteNaam) {
      const { data } = await sb.from('platform_settings')
        .select('platform_short_name, platform_name').eq('id', true).maybeSingle();
      window.__korteNaam = data?.platform_short_name || data?.platform_name || window.__merknaam || 'Platform';
    }
    naam.textContent = window.__korteNaam;
    sub.textContent = 'Platform management';
    document.title = `Platform — ${window.__korteNaam}`;
  }

  function navPlatform(page, opties = {}) {
    if (!isPlatformAdmin) return;
    router.onthoud('platform-' + page, { vervang: !!opties.vervang, staat: { ctx: context.gekozen() } });
    document.querySelectorAll('#navPlatform .nav-item')
      .forEach(n => n.classList.toggle('active', n.dataset.ppage === page));
    document.querySelectorAll('.page').forEach(p => p.classList.remove('active'));
    document.getElementById('topbarTitle').textContent = router.titel(page);
    const id = 'pagePlatform' + page.charAt(0).toUpperCase() + page.slice(1);
    document.getElementById(id)?.classList.add('active');

    if (page === 'dash') renderPlatformDash();
    if (page === 'orgs') { renderPlatformOrgs(); renderPlatformToegang(); }
    if (page === 'terms') renderTermsList();
    if (page === 'consent') renderConsentList('consentList', true);
    if (page === 'email') renderPlatformEmail();
    if (page === 'stripe') renderPlatformStripe();
    if (page === 'instellingen') renderPlatformInstellingen();
  }

  async function renderPlatformInstellingen() {
    const { data } = await sb.from('platform_settings').select('*').eq('id', true).maybeSingle();
    const zet = (id, waarde) => { const el = document.getElementById(id); if (el) el.value = waarde || ''; };
    zet('platformName', data?.platform_name);
    zet('platformShort', data?.platform_short_name);
    zet('platformSupport', data?.support_email);
    zet('platformOrigins', (data?.allowed_origins || []).join('\n'));
  }

  async function saveOrigins() {
    const regels = document.getElementById('platformOrigins').value
      .split('\n').map(r => r.trim()).filter(Boolean);
    const fout = regels.find(r => !/^https?:\/\/[^/]+$/.test(r));
    if (fout) { toast(`Ongeldig adres: ${fout}. Alleen https en zonder pad.`, 'error'); return; }
    if (!regels.length) { toast('Er moet minstens één adres overblijven', 'error'); return; }
    const { error } = await sb.from('platform_settings')
      .update({ allowed_origins: regels, updated_by: state.currentUserEmail, updated_at: new Date().toISOString() })
      .eq('id', true);
    if (error) { toast('Opslaan mislukt: ' + error.message, 'error'); return; }
    toast('Adressen opgeslagen, binnen een minuut actief', 'success');
    await renderPlatformInstellingen();
  }

  // Alles wat het overzicht nodig heeft, in één keer opgehaald.
  async function platformGegevens() {
    const [orgs, limieten, docs, aanvaardingen, uitnodigingen] = await Promise.all([
      sb.from('organisation_overview').select('*').order('name'),
      sb.from('platform_billing').select('*'),
      sb.from('terms_documents').select('*').eq('is_current', true),
      sb.from('terms_acceptance_overview').select('*').order('accepted_at', { ascending: false }),
      sb.from('invitations').select('*').is('accepted_at', null).is('revoked_at', null)
    ]);
    const perOrg = Object.fromEntries((limieten.data || []).map(l => [l.organisation_id, l]));
    (orgs.data || []).forEach(o => {
      o.email_daily_limit = perOrg[o.id]?.email_daily_limit ?? 200;
      o.fee_bps = perOrg[o.id]?.fee_bps ?? o.fee_bps ?? 0;
      o.fee_fixed_cents = perOrg[o.id]?.fee_fixed_cents ?? o.fee_fixed_cents ?? 0;
    });
    return {
      orgs: orgs.data || [],
      geldendeVoorwaarden: (docs.data || []).find(d => d.kind === 'platform_terms') || null,
      aanvaardingen: aanvaardingen.data || [],
      uitnodigingen: uitnodigingen.data || []
    };
  }

  async function renderPlatformDash() {
    const kpi = document.getElementById('platformKpis');
    const aandacht = document.getElementById('platformAttention');
    if (!kpi) return;
    kpi.innerHTML = '<div class="spinner"></div>';

    const g = await platformGegevens();
    const gekoppeld = g.orgs.filter(o => o.stripe_status === 'connected').length;
    const leden = g.orgs.reduce((n, o) => n + Number(o.leden || 0), 0);
    const inschrijvingen = g.orgs.reduce((n, o) => n + Number(o.inschrijvingen || 0), 0);

    // Wie heeft de geldende versie aanvaard? Dat is de vraag die telt zodra er
    // een nieuwe versie uitgaat: een oude aanvaarding dekt die niet.
    const versie = g.geldendeVoorwaarden?.version || null;
    const akkoordOrgs = new Set(
      g.aanvaardingen.filter(a => versie && a.document_version === versie).map(a => a.organisation_id)
    );
    const nietAkkoord = versie ? g.orgs.filter(o => !akkoordOrgs.has(o.id)) : g.orgs;

    const tegel = (label, waarde, toelichting, kleur) => `
      <div class="kpi-card">
        <div style="font-size:10px;font-weight:600;text-transform:uppercase;letter-spacing:.06em;color:var(--text-3)">${label}</div>
        <div style="font-size:22px;font-weight:600;margin-top:4px;${kleur ? `color:${kleur}` : ''}">${waarde}</div>
        <div style="font-size:11px;color:var(--text-3);margin-top:2px">${toelichting}</div>
      </div>`;

    kpi.innerHTML =
      tegel('Organisaties', g.orgs.length, `${g.orgs.filter(o => o.status === 'active').length} actief`) +
      tegel('Gebruikers', leden, 'over alle organisaties') +
      tegel('Stripe', `${gekoppeld}/${g.orgs.length}`, 'gekoppeld', gekoppeld < g.orgs.length ? 'var(--amber)' : 'var(--green)') +
      tegel('Voorwaarden', versie ? `${akkoordOrgs.size}/${g.orgs.length}` : 'geen', versie ? `akkoord op ${versie}` : 'nog niets gepubliceerd', nietAkkoord.length ? 'var(--amber)' : 'var(--green)') +
      tegel('Inschrijvingen', inschrijvingen, 'over alle organisaties') +
      tegel('Uitnodigingen', g.uitnodigingen.length, 'nog openstaand');

    const punten = [];
    if (!versie) {
      punten.push(['red', 'Er geldt geen versie van de voorwaarden. Niemand kan een uitnodiging aanvaarden.', 'terms']);
    }
    for (const o of g.orgs) {
      if (versie && !akkoordOrgs.has(o.id)) {
        punten.push(['amber', `${o.name} heeft versie ${versie} niet aanvaard.`, 'consent']);
      }
      if (o.stripe_status !== 'connected') {
        punten.push(['amber', `${o.name} heeft geen Stripe-account gekoppeld en kan geen betaallinks maken.`, 'orgs']);
      }
    }
    const { data: zonderAfzender } = await sb.from('organisations')
      .select('name').is('email_from_email', null);
    (zonderAfzender || []).forEach(o =>
      punten.push(['amber', `${o.name} heeft geen antwoordadres: antwoorden op e-mail van deze organisatie komen niet bij haar aan.`, 'email']));

    const oud = g.uitnodigingen.filter(i => new Date(i.expires_at) < new Date());
    if (oud.length) punten.push(['amber', `${oud.length} uitnodiging${oud.length === 1 ? '' : 'en'} verlopen zonder te zijn gebruikt.`, 'orgs']);

    aandacht.innerHTML = punten.length
      ? punten.map(([kleur, tekst, pagina]) => `
          <div class="org-row">
            <i class="ti ti-${kleur === 'red' ? 'alert-circle' : 'alert-triangle'}"
               style="color:var(--${kleur});font-size:16px"></i>
            <div style="flex:1;font-size:13px">${esc(tekst)}</div>
            <button class="btn btn-ghost btn-sm" onclick="App.navPlatform('${pagina}')">Bekijken</button>
          </div>`).join('')
      : '<div class="banner banner-green"><i class="ti ti-circle-check"></i><span>Niets dat aandacht vraagt.</span></div>';

    const recent = document.getElementById('platformRecentConsent');
    if (recent) {
      recent.innerHTML = g.aanvaardingen.length
        ? g.aanvaardingen.slice(0, 5).map(a => consentRij(a, true)).join('')
        : '<p style="font-size:12px;color:var(--text-3)">Nog geen aanvaardingen.</p>';
    }

    await tweestapOverzicht.render(sb, document.getElementById('platformTweestap'));
    const badge = document.getElementById('badgePlatformOrgs');
    if (badge) badge.textContent = g.orgs.length;
  }

  // Een regel in het aanvaardingsoverzicht op het platformdashboard. Viel weg
  // bij de standaardtabel in fase 9, terwijl het dashboard hem nog aanriep:
  // met minstens één aanvaarding brak de pagina op een ReferenceError.
  function consentRij(a, toonOrg) {
    return `<div class="org-row">
      <div style="flex:1;min-width:200px">
        <div style="font-size:13px;font-weight:600">${esc(a.accepted_by_name || a.accepted_by_email)}</div>
        <div style="font-size:11px;color:var(--text-3)">
          ${esc(a.accepted_by_job_title || '')}${a.accepted_by_job_title ? ' &middot; ' : ''}${esc(a.accepted_by_email)}
          ${toonOrg ? ' &middot; ' + esc(a.organisation) : ''}
        </div>
      </div>
      <div style="font-size:11px;color:var(--text-3);min-width:150px">
        ${esc(SOORT_LABELS[a.document_kind] || a.document_kind)} ${esc(a.document_version)}<br>
        ${new Date(a.accepted_at).toLocaleString('nl-NL')}
      </div>
      <button class="btn btn-default btn-sm" onclick="App.showCert('${a.id}')">
        <i class="ti ti-certificate"></i> Certificaat</button>
    </div>`;
  }

  const ABO_LABELS = { trial: 'Proef', active: 'Actief', paused: 'Gepauzeerd', cancelled: 'Opgezegd' };

  async function renderPlatformOrgs() {
    const el = document.getElementById('platformOrgs');
    if (!el) return;
    el.innerHTML = '<div class="spinner"></div>';
    const { orgs } = await platformGegevens();
    tabelTeken('orgs', 'platformOrgs', [
      { veld: 'name', label: 'Organisatie',
        cel: o => `<strong>${esc(o.name)}</strong><div style="margin-top:2px">${refLabel(o.ref)}</div>`,
        tekst: o => `${o.name} ${o.ref || ''}` },
      { veld: 'status', label: 'Status', cel: o => `<span class="role-badge ${o.status === 'active' ? 'editor' : 'viewer'}">${esc(o.status)}</span>` },
      { veld: 'stripe_status', label: 'Stripe',
        tekst: o => o.stripe_status === 'connected' ? 'gekoppeld' : 'niet gekoppeld',
        cel: o => `<span class="role-badge ${o.stripe_status === 'connected' ? 'editor' : 'viewer'}">${o.stripe_status === 'connected' ? 'Gekoppeld' : 'Niet gekoppeld'}</span>` },
      { veld: 'subscription_status', label: 'Abonnement',
        tekst: o => ABO_LABELS[o.subscription_status] || o.subscription_status || '' },
      { veld: 'leden', label: 'Leden', uitlijn: 'right', mono: true, filter: false },
      { veld: 'inschrijvingen', label: 'Inschrijvingen', uitlijn: 'right', mono: true, filter: false },
      { veld: 'email_from_email', label: 'Antwoordadres',
        tekst: o => o.email_from_email || '',
        cel: o => o.email_from_email ? esc(o.email_from_email) : '<span style="color:var(--text-3)">—</span>' },
      { veld: 'created_at', label: 'Aangemaakt', filter: false,
        tekst: o => (o.created_at || '').slice(0, 10) },
    ], orgs, { zoekLabel: 'Zoek op naam, code of afzender', leeg: 'Nog geen organisaties.' });
  }

  // ── PLATFORMTOEGANG ──
  // Toegang tot de gegevens van een klant is per keer, met een reden en een
  // vervaldatum. De database bepaalt de tijden en de naam van de aanvrager;
  // wat hier wordt meegestuurd is alleen de gewenste duur.
  let toegangOrg = null;

  function resterend(tot) {
    const min = Math.max(0, Math.round((new Date(tot) - Date.now()) / 60000));
    return min >= 60 ? `${Math.floor(min / 60)}u ${min % 60}m` : `${min}m`;
  }

  async function renderPlatformToegang() {
    const el = document.getElementById('platformToegang');
    if (!el) return;
    el.innerHTML = '<div class="spinner"></div>';
    const [{ orgs }, grants] = await Promise.all([
      platformGegevens(),
      sb.from('platform_access_grants').select('*')
        .is('revoked_at', null).gt('expires_at', new Date().toISOString())
        .eq('email', state.currentUserEmail.toLowerCase())
    ]);
    if (grants.error) {
      el.innerHTML = `<p style="font-size:12px;color:var(--red)">${esc(grants.error.message)}</p>`;
      return;
    }
    const actief = Object.fromEntries((grants.data || []).map(g => [g.organisation_id, g]));
    el.innerHTML = orgs.map(o => {
      const g = actief[o.id];
      const eigen = o.id === state.currentOrgId;
      return `<div class="org-row">
        <div style="flex:1;min-width:160px">
          <div style="font-size:13px;font-weight:600">${esc(o.name)}</div>
          <div style="font-size:11px;color:var(--text-3)">${g ? `Toegang nog ${resterend(g.expires_at)}: ${esc(g.reason)}` : eigen ? 'Je bent lid van deze organisatie' : 'Geen toegang'}</div>
        </div>
        ${g ? `<button class="btn btn-default btn-sm" onclick="App.trekToegangIn('${g.id}')"><i class="ti ti-lock"></i> Intrekken</button>`
            : eigen ? '' : `<button class="btn btn-default btn-sm" onclick="App.openToegangModal('${o.id}')"><i class="ti ti-key"></i> Aanvragen</button>`}
      </div>`;
    }).join('') || '<p style="font-size:12px;color:var(--text-3)">Nog geen organisaties.</p>';
  }

  async function openToegangModal(orgId) {
    toegangOrg = orgId;
    const { orgs } = await platformGegevens();
    const naam = orgs.find(o => o.id === orgId)?.name || '';
    document.getElementById('toegangTitel').textContent = `Toegang tot ${naam}`;
    document.getElementById('toegangReden').value = '';
    document.getElementById('toegangDuur').value = '4';
    document.getElementById('toegangModal').classList.add('open');
  }
  function closeToegangModal() {
    toegangOrg = null;
    document.getElementById('toegangModal').classList.remove('open');
  }

  async function vraagToegangAan() {
    const reden = document.getElementById('toegangReden').value.trim();
    const uren = Number(document.getElementById('toegangDuur').value) || 4;
    if (reden.length < 10) { toast('Geef een reden van minstens tien tekens', 'error'); return; }
    if (!toegangOrg) return;
    const nu = new Date();
    const { error } = await sb.from('platform_access_grants').insert([{
      organisation_id: toegangOrg,
      email: state.currentUserEmail.toLowerCase(),
      reason: reden,
      granted_at: nu.toISOString(),
      expires_at: new Date(nu.getTime() + uren * 3600 * 1000).toISOString(),
    }]);
    if (error) { toast(error.message, 'error'); return; }
    closeToegangModal();
    toast(`Toegang verleend voor ${uren} uur`, 'success');
    await renderPlatformToegang();
  }

  async function trekToegangIn(id) {
    const { error } = await sb.from('platform_access_grants')
      .update({ revoked_at: new Date().toISOString() }).eq('id', id);
    if (error) { toast(error.message, 'error'); return; }
    toast('Toegang ingetrokken', 'success');
    await renderPlatformToegang();
    await renderOrgPlatformToegang();
  }

  // ── AUDITLOG ──
  // Het bewijsspoor uit audit_log, alleen lezen. De tijdlijn per inschrijving
  // blijft activity_log; dit is wat de database zelf vastlegt.
  const AUDIT_TABELLEN = {
    organisation_members: 'Gebruiker', invitations: 'Uitnodiging', roles: 'Rol',
    role_permissions: 'Rolrecht', platform_access_grants: 'Platformtoegang',
    organisations: 'Organisatie', platform_billing: 'Tarief', teams: 'Inschrijving',
  };
  const AUDIT_HANDELINGEN = { insert: 'aangemaakt', update: 'gewijzigd', delete: 'verwijderd', opruimen: 'opgeruimd' };

  function auditSamenvatting(r) {
    const n = r.nieuw || {}, o = r.oud || {};
    if (r.handeling === 'update') {
      return Object.keys(n).filter(k => k !== 'updated_at')
        .map(k => `${esc(k)}: ${esc(JSON.stringify(o[k]))} &rarr; ${esc(JSON.stringify(n[k]))}`).join(', ');
    }
    const bron = r.handeling === 'delete' ? o : n;
    return esc(bron.email || bron.name || bron.key || bron.reason || bron.team_name || '');
  }

  async function renderOrgAuditLog() {
    const el = document.getElementById('orgAuditLog');
    if (!el || !state.currentOrgId) return;
    const { data, error } = await sb.from('audit_log').select('*')
      .eq('organisation_id', state.currentOrgId)
      .order('id', { ascending: false }).limit(25);
    if (error) { el.innerHTML = '<p style="font-size:12px;color:var(--text-3)">Niet beschikbaar.</p>'; return; }
    if (!data?.length) { el.innerHTML = '<p style="font-size:12px;color:var(--text-3)">Nog niets vastgelegd.</p>'; return; }
    el.innerHTML = data.map(r => `<div class="org-row">
        <div style="flex:1;min-width:200px">
          <div style="font-size:13px">${esc(AUDIT_TABELLEN[r.tabel] || r.tabel)} ${esc(AUDIT_HANDELINGEN[r.handeling] || r.handeling)}
            <span style="color:var(--text-3)">${auditSamenvatting(r)}</span></div>
          <div style="font-size:11px;color:var(--text-3)">${esc(r.actor_email || 'systeem')}${r.actor_rol ? ' (' + esc(r.actor_rol) + ')' : ''} &middot; ${fmtDatetime(r.created_at)}</div>
        </div>
      </div>`).join('');
  }

  // Aan de kant van de organisatie: wie van de leverancier er bij kon.
  async function renderOrgPlatformToegang() {
    const el = document.getElementById('orgPlatformToegang');
    if (!el || !state.currentOrgId) return;
    const { data, error } = await sb.from('platform_access_grants').select('*')
      .eq('organisation_id', state.currentOrgId)
      .order('granted_at', { ascending: false }).limit(20);
    if (error) {
      el.innerHTML = `<p style="font-size:12px;color:var(--text-3)">Niet beschikbaar.</p>`;
      return;
    }
    if (!data?.length) {
      el.innerHTML = '<p style="font-size:12px;color:var(--text-3)">Platformbeheer heeft nog geen toegang gehad.</p>';
      return;
    }
    const nu = Date.now();
    el.innerHTML = data.map(g => {
      const loopt = !g.revoked_at && new Date(g.expires_at) > nu;
      const status = g.revoked_at
        ? `ingetrokken ${fmtDatetime(g.revoked_at)}${g.revoked_by ? ' door ' + esc(g.revoked_by) : ''}`
        : loopt ? `loopt nog ${resterend(g.expires_at)}` : `verlopen ${fmtDatetime(g.expires_at)}`;
      return `<div class="org-row">
        <div style="flex:1;min-width:180px">
          <div style="font-size:13px">${esc(g.reason)}</div>
          <div style="font-size:11px;color:var(--text-3)">${esc(g.email)} &middot; ${fmtDatetime(g.granted_at)} &middot; ${status}</div>
        </div>
        ${loopt ? `<button class="btn btn-default btn-sm" onclick="App.trekToegangIn('${g.id}')"><i class="ti ti-lock"></i> Intrekken</button>` : ''}
      </div>`;
    }).join('');
  }

  async function renderPlatformStripe() {
    const { data: inst } = await sb.from('platform_settings').select('*').eq('id', true).maybeSingle();
    const veld = document.getElementById('platformClientId');
    if (veld) veld.value = inst?.stripe_connect_client_id || '';
    const statusEl = document.getElementById('platformStripeStatus');
    if (!statusEl) return;
    let klaar = null;
    try { klaar = await connectCall('status'); } catch { /* geen lidmaatschap */ }
    const heeftClientId = !!inst?.stripe_connect_client_id;
    statusEl.innerHTML = klaar?.platform_ready
      ? `<div class="banner banner-green"><i class="ti ti-circle-check"></i>
           <span>Connect is ingesteld. Organisaties kunnen koppelen.</span></div>`
      : `<div class="banner banner-amber"><i class="ti ti-alert-triangle"></i>
           <span>Nog niet compleet. Nodig: een client-id hieronder${heeftClientId ? '' : ' (ontbreekt)'},
           en de secret key van het platform als servergeheim
           (<code>npx supabase secrets set STRIPE_SECRET_KEY=...</code>).</span></div>`;

    await renderAbonnementen();
  }

  // Abonnementen en tarieven per organisatie. Bewust één scherm: het gaat in
  // beide gevallen over wat een klant betaalt, en gescheiden houden leidt tot
  // twee plekken waar een tarief kan staan.
  async function renderAbonnementen() {
    const el = document.getElementById('platformAbonnementen');
    if (!el) return;
    el.innerHTML = '<div class="spinner"></div>';
    const { orgs } = await platformGegevens();
    if (!orgs.length) { el.innerHTML = '<p style="font-size:12px;color:var(--text-3)">Nog geen organisaties.</p>'; return; }

    el.innerHTML = orgs.map(o => `
      <div class="org-row" style="align-items:flex-end">
        <div style="flex:1;min-width:150px">
          <div style="font-size:13px;font-weight:600">${esc(o.name)}</div>
          <div style="font-size:11px;color:var(--text-3)">${o.inschrijvingen} inschrijvingen</div>
        </div>
        <div class="form-group" style="margin-bottom:0;width:110px">
          <label class="form-label">Bedrag (€)</label>
          <input class="form-input" style="padding:5px 8px;font-size:12px" id="abo_bedrag_${o.id}"
                 type="number" min="0" step="0.01" value="${((o.subscription_cents || 0) / 100).toFixed(2)}">
        </div>
        <div class="form-group" style="margin-bottom:0;width:100px">
          <label class="form-label">Per</label>
          <select class="form-input" style="padding:5px 8px;font-size:12px" id="abo_interval_${o.id}">
            <option value="year"${o.subscription_interval === 'year' ? ' selected' : ''}>jaar</option>
            <option value="month"${o.subscription_interval === 'month' ? ' selected' : ''}>maand</option>
          </select>
        </div>
        <div class="form-group" style="margin-bottom:0;width:120px">
          <label class="form-label">Status</label>
          <select class="form-input" style="padding:5px 8px;font-size:12px" id="abo_status_${o.id}">
            ${Object.entries(ABO_LABELS).map(([w, l]) =>
              `<option value="${w}"${o.subscription_status === w ? ' selected' : ''}>${l}</option>`).join('')}
          </select>
        </div>
        <div class="form-group" style="margin-bottom:0;width:86px">
          <label class="form-label" title="Basispunten, 100 = 1 procent">Fee (bp)</label>
          <input class="form-input" style="padding:5px 8px;font-size:12px" id="fee_bps_${o.id}"
                 type="number" min="0" max="1000" step="1" value="${o.fee_bps}">
        </div>
        <div class="form-group" style="margin-bottom:0;width:86px">
          <label class="form-label" title="Vast bedrag per transactie, in centen">Fee (ct)</label>
          <input class="form-input" style="padding:5px 8px;font-size:12px" id="fee_fix_${o.id}"
                 type="number" min="0" max="10000" step="1" value="${o.fee_fixed_cents}">
        </div>
        <button class="btn btn-default btn-sm" onclick="App.saveAbonnement('${o.id}')"><i class="ti ti-check"></i> Opslaan</button>
      </div>`).join('');
  }

  async function saveAbonnement(orgId) {
    const euro = parseFloat(document.getElementById(`abo_bedrag_${orgId}`).value);
    const interval = document.getElementById(`abo_interval_${orgId}`).value;
    const status = document.getElementById(`abo_status_${orgId}`).value;
    const bps = parseInt(document.getElementById(`fee_bps_${orgId}`).value, 10);
    const fix = parseInt(document.getElementById(`fee_fix_${orgId}`).value, 10);

    if (!Number.isFinite(euro) || euro < 0 || euro > 1000000) { toast('Bedrag tussen 0 en 1000000', 'error'); return; }
    if (!Number.isFinite(bps) || bps < 0 || bps > 1000) { toast('Basispunten tussen 0 en 1000', 'error'); return; }
    if (!Number.isFinite(fix) || fix < 0 || fix > 10000) { toast('Vast bedrag tussen 0 en 10000 cent', 'error'); return; }

    const { error } = await sb.from('platform_billing').update({
      subscription_cents: Math.round(euro * 100),
      subscription_interval: interval,
      subscription_status: status,
      fee_bps: bps, fee_fixed_cents: fix,
      updated_by: state.currentUserEmail, updated_at: new Date().toISOString(),
    }).eq('organisation_id', orgId);
    if (error) { toast('Opslaan mislukt: ' + error.message, 'error'); return; }
    toast('Opgeslagen', 'success');
  }

  // ── E-MAIL VAN HET PLATFORM ──
  async function renderPlatformEmail() {
    const statusEl = document.getElementById('platformEmailStatus');
    const lijst = document.getElementById('platformEmailOrgs');
    if (!statusEl) return;

    let geheim = null;
    try { geheim = await platformCall('secrets_status'); } catch { /* oude functie */ }

    if (geheim?.brevo_set) {
      const wie = geheim.brevo_updated_by ? ` door ${esc(geheim.brevo_updated_by)}` : '';
      const wanneer = geheim.brevo_updated_at
        ? ` op ${new Date(geheim.brevo_updated_at).toLocaleString('nl-NL')}` : '';
      statusEl.innerHTML = `<div class="banner banner-green"><i class="ti ti-circle-check"></i>
        <span>Sleutel ingesteld (${esc(geheim.brevo_hint || '')})${wanneer}${wie}. Verzenden werkt.</span></div>`;
    } else if (geheim?.brevo_env) {
      statusEl.innerHTML = `<div class="banner banner-amber"><i class="ti ti-alert-triangle"></i>
        <span>Er staat geen sleutel in de database. Verzenden werkt nu op de oude
        omgevingsvariabele van de functie. Vul hem hieronder in, dan is hij voortaan
        hier te wisselen zonder opnieuw uit te rollen.</span></div>`;
    } else {
      statusEl.innerHTML = `<div class="banner banner-red"><i class="ti ti-alert-circle"></i>
        <span>Geen sleutel. Er kan niets verstuurd worden, door geen enkele organisatie.</span></div>`;
    }

    if (!lijst) return;
    // Het platformadres waar alle e-mail vandaan komt (B1).
    const afzenderEl = document.getElementById('platformEmailAfzender');
    if (afzenderEl) {
      const { data: ps } = await sb.from('platform_settings').select('email_afzender_adres, email_afzender_merk').eq('id', true).maybeSingle();
      afzenderEl.innerHTML = ps?.email_afzender_adres
        ? `<p style="font-size:13px;margin-bottom:10px">Afzender: <strong>{Organisatie} via ${esc(ps.email_afzender_merk || 'Tornema')}</strong>
           &lt;${esc(ps.email_afzender_adres)}&gt;</p>`
        : `<div class="banner banner-amber"><i class="ti ti-alert-triangle"></i><span>De migratie voor het platformadres
           (20261107090000_afzender_tornema_app.sql) is nog niet gedraaid.</span></div>`;
    }
    const { data: rijen } = await sb.from('organisations')
      .select('id, name, email_from_name, email_from_email').order('name');
    const { data: limieten } = await sb.from('platform_billing').select('organisation_id, email_daily_limit');
    const perOrg = Object.fromEntries((limieten || []).map(l => [l.organisation_id, l]));
    lijst.innerHTML = (rijen || []).map(o => `
      <div class="org-row" style="align-items:flex-end">
        <div style="flex:1;min-width:140px">
          <div style="font-size:13px;font-weight:600">${esc(o.name)}</div>
          <div style="font-size:11px;color:var(--text-3)">${o.email_from_email ? 'antwoordadres ingesteld' : 'geen antwoordadres'}</div>
        </div>
        <div class="form-group" style="margin-bottom:0;flex:1;min-width:180px">
          <label class="form-label">Antwoordadres</label>
          <input class="form-input" style="padding:5px 8px;font-size:12px" id="mail_adres_${o.id}"
                 type="email" value="${esc(o.email_from_email || '')}" placeholder="contact@vereniging.nl">
        </div>
        <div class="form-group" style="margin-bottom:0;flex:1;min-width:150px">
          <label class="form-label">Naam voor "via"</label>
          <input class="form-input" style="padding:5px 8px;font-size:12px" id="mail_naam_${o.id}"
                 value="${esc(o.email_from_name || '')}" placeholder="Naam van de organisatie">
        </div>
        <div class="form-group" style="margin-bottom:0;width:90px">
          <label class="form-label">Per dag</label>
          <input class="form-input" style="padding:5px 8px;font-size:12px" id="mail_limiet_${o.id}"
                 type="number" min="0" max="5000" step="10" value="${perOrg[o.id]?.email_daily_limit ?? 200}">
        </div>
        <button class="btn btn-default btn-sm" onclick="App.saveMailLimit('${o.id}')"><i class="ti ti-check"></i> Opslaan</button>
      </div>`).join('') || '<p style="font-size:12px;color:var(--text-3)">Nog geen organisaties.</p>';

    await renderPlatformVerbruik();
  }

  // Slaat antwoordadres, naam en limiet samen op. Die staan op de organisatie,
  // de limiet op de facturatieregel; voor de beheerder is het één handeling.
  async function saveMailLimit(orgId) {
    const n = parseInt(document.getElementById(`mail_limiet_${orgId}`).value, 10);
    const adres = document.getElementById(`mail_adres_${orgId}`).value.trim();
    const naam = document.getElementById(`mail_naam_${orgId}`).value.trim();
    if (!Number.isFinite(n) || n < 0 || n > 5000) { toast('Limiet tussen 0 en 5000','error'); return; }
    if (adres && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(adres)) { toast('Ongeldig antwoordadres','error'); return; }

    const { error: orgFout } = await sb.from('organisations')
      .update({ email_from_email: adres || null, email_from_name: naam || null, updated_at: new Date().toISOString() })
      .eq('id', orgId);
    if (orgFout) { toast('Afzender opslaan mislukt: ' + orgFout.message,'error'); return; }

    const { error } = await sb.from('platform_billing')
      .update({ email_daily_limit: n, updated_by: state.currentUserEmail, updated_at: new Date().toISOString() })
      .eq('organisation_id', orgId);
    if (error) { toast('Limiet opslaan mislukt: ' + error.message,'error'); return; }

    toast(n === 0 ? 'Opgeslagen, verzenden staat uit voor deze organisatie' : 'Opgeslagen','success');
    if (orgId === state.currentOrgId && state.currentOrg) {
      state.currentOrg.email_from_email = adres || null;
      state.currentOrg.email_from_name = naam || null;
    }
    await renderPlatformEmail();
  }

  async function saveBrevoKey() {
    const veld = document.getElementById('brevoKey');
    const sleutel = veld.value.trim();
    if (!sleutel) { toast('Vul een sleutel in', 'error'); return; }
    try {
      await platformCall('set_brevo_key', { key: sleutel });
      veld.value = '';
      toast('Sleutel opgeslagen', 'success');
      await renderPlatformEmail();
    } catch (e) { toast(e.message, 'error'); }
  }

  async function clearBrevoKey() {
    if (!confirm('Sleutel wissen? Zolang er geen andere sleutel staat kan geen enkele organisatie nog e-mail versturen.')) return;
    try {
      await platformCall('clear_brevo_key');
      toast('Sleutel gewist', 'success');
      await renderPlatformEmail();
    } catch (e) { toast(e.message, 'error'); }
  }

  // ── E-MAILVERBRUIK ────────────────────────────────────────────────────────
  // Telt uit activity_log, dezelfde regels waar de daglimiet op telt. Eén bron,
  // dus de factuur en de limiet kunnen niet uit elkaar lopen.
  const MAAND_NAMEN = ['jan','feb','mrt','apr','mei','jun','jul','aug','sep','okt','nov','dec'];

  function maandLabel(datum) {
    const d = new Date(datum);
    return `${MAAND_NAMEN[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
  }

  function laatsteMaanden(aantal) {
    const nu = new Date();
    const rij = [];
    for (let i = aantal - 1; i >= 0; i--) {
      const d = new Date(Date.UTC(nu.getUTCFullYear(), nu.getUTCMonth() - i, 1));
      rij.push(d.toISOString().slice(0, 10));
    }
    return rij;
  }

  async function renderEmailVerbruik() {
    const el = document.getElementById('emailVerbruik');
    if (!el) return;
    const { data, error } = await sb.from('email_usage_monthly').select('*');
    if (error) {
      el.innerHTML = `<p style="font-size:12px;color:var(--text-3)">Verbruik nog niet beschikbaar (${esc(error.message)})</p>`;
      return;
    }
    const perMaand = Object.fromEntries((data || []).map(r => [r.maand, Number(r.aantal)]));
    const maanden = laatsteMaanden(12);
    const totaal = (data || []).reduce((s, r) => s + Number(r.aantal), 0);
    const dezeMaand = perMaand[maanden[maanden.length - 1]] || 0;

    el.innerHTML = `
      <div style="display:flex;gap:18px;flex-wrap:wrap;margin-bottom:10px">
        <div><div style="font-size:11px;color:var(--text-3)">Deze maand</div>
             <div style="font-size:18px;font-weight:600;font-family:var(--mono)">${dezeMaand}</div></div>
        <div><div style="font-size:11px;color:var(--text-3)">Totaal sinds de start</div>
             <div style="font-size:18px;font-weight:600;font-family:var(--mono)">${totaal}</div></div>
      </div>
      <div class="table-wrap"><table class="tabel-standaard">
        <thead><tr><th>Maand</th><th style="text-align:right">Verstuurd</th></tr></thead>
        <tbody>${maanden.slice().reverse().map(m => `
          <tr><td>${esc(maandLabel(m))}</td>
              <td style="text-align:right;font-family:var(--mono)">${perMaand[m] || 0}</td></tr>`).join('')}
        </tbody>
      </table></div>`;
  }

  async function renderPlatformVerbruik() {
    const el = document.getElementById('platformEmailVerbruik');
    if (!el) return;
    el.innerHTML = '<div class="spinner"></div>';
    const [{ data: verbruik, error }, { data: orgs }] = await Promise.all([
      sb.from('email_usage_monthly').select('*'),
      sb.from('organisations').select('id, name').order('name'),
    ]);
    if (error) {
      el.innerHTML = `<p style="font-size:12px;color:var(--text-3)">Verbruik nog niet beschikbaar (${esc(error.message)})</p>`;
      return;
    }
    const maanden = laatsteMaanden(6);
    const per = {};
    (verbruik || []).forEach(r => { per[`${r.organisation_id}|${r.maand}`] = Number(r.aantal); });

    el.innerHTML = `<div class="table-wrap"><table class="tabel-standaard">
      <thead><tr><th>Organisatie</th>
        ${maanden.slice().reverse().map(m => `<th style="text-align:right">${esc(maandLabel(m))}</th>`).join('')}
        <th style="text-align:right">Totaal</th></tr></thead>
      <tbody>${(orgs || []).map(o => {
        const totaal = (verbruik || []).filter(r => r.organisation_id === o.id)
          .reduce((s, r) => s + Number(r.aantal), 0);
        return `<tr><td><strong>${esc(o.name)}</strong></td>
          ${maanden.slice().reverse().map(m =>
            `<td style="text-align:right;font-family:var(--mono)">${per[`${o.id}|${m}`] || 0}</td>`).join('')}
          <td style="text-align:right;font-family:var(--mono);font-weight:600">${totaal}</td></tr>`;
      }).join('')}</tbody>
    </table></div>`;
  }

  // ── VOORWAARDEN ──
  const SOORT_LABELS = {
    user_terms:'Gebruiksvoorwaarden',
    platform_terms:'Overeenkomst met het platform',
    dpa:'Verwerkersovereenkomst',
    privacy_policy:'Privacyverklaring',
  };

  // Standaardreikwijdte per soort. Een overeenkomst bindt de organisatie en
  // hoort bij de owner; gebruiksvoorwaarden gelden voor iedereen die inlogt.
  const SOORT_ROLLEN = {
    user_terms:     ['owner','admin','editor','viewer'],
    privacy_policy: ['owner','admin','editor','viewer'],
    platform_terms: ['owner'],
    dpa:            ['owner'],
  };

  function termsSoortGewijzigd() {
    const soort = document.getElementById('termsKind').value;
    const standaard = SOORT_ROLLEN[soort] || ['owner','admin','editor','viewer'];
    document.querySelectorAll('.terms-rol').forEach(v => { v.checked = standaard.includes(v.value); });
  }

  async function renderTermsList() {
    const el = document.getElementById('termsList');
    if (!el) return;
    const { data, error } = await sb.from('terms_documents').select('*').order('published_at', { ascending: false });
    if (error) { el.innerHTML = `<p style="font-size:13px;color:var(--red)">${esc(error.message)}</p>`; return; }
    if (!data?.length) {
      el.innerHTML = '<p style="font-size:13px;color:var(--text-3)">Nog geen versie gepubliceerd. Zonder geldende versie kan niemand een uitnodiging aanvaarden.</p>';
      return;
    }
    const { data: tellingen } = await sb.from('terms_acceptance_overview').select('document_version, document_kind');
    const telPer = {};
    (tellingen || []).forEach(a => {
      const k = `${a.document_kind}|${a.document_version}`;
      telPer[k] = (telPer[k] || 0) + 1;
    });
    el.innerHTML = data.map(d => `
      <div class="org-row">
        <div style="flex:1;min-width:180px">
          <div style="font-size:13px;font-weight:600">
            ${esc(SOORT_LABELS[d.kind] || d.kind)} ${esc(d.version)}
            <span style="font-size:11px;color:var(--text-3)">(${esc(d.language)})</span>
          </div>
          <div style="font-size:11px;color:var(--text-3)">
            geldig vanaf ${d.effective_from} &middot;
            <a href="${esc(d.content_url)}" target="_blank" style="color:var(--accent)">document</a> &middot;
            ${d.content_text ? 'tekst aanwezig' : '<span style="color:var(--amber)">geen tekst</span>'} &middot;
            ${telPer[`${d.kind}|${d.version}`] || 0} aanvaard
          </div>
          <div style="font-size:11px;color:var(--text-3)">
            voorgelegd aan: ${esc((d.applies_to_roles || ['owner','admin','editor','viewer']).join(', '))}
          </div>
          <div style="font-size:10px;color:var(--text-3);font-family:var(--mono);word-break:break-all">${esc(d.content_sha256)}</div>
        </div>
        ${d.is_current
          ? '<span class="role-badge editor">Geldt nu</span>'
          : `<button class="btn btn-default btn-sm" onclick="App.makeTermsCurrent('${d.id}')">Laten gelden</button>`}
      </div>`).join('');
  }

  // Uploaden en de vingerafdruk berekenen in één handeling. De vingerafdruk
  // wordt lokaal uit de bytes berekend, vóór het versturen: zou de server hem
  // berekenen, dan bewijst hij alleen dat de server zichzelf vertrouwt.
  async function uploadTermsFile(input) {
    const bestand = input.files?.[0];
    if (!bestand) return;
    const status = document.getElementById('termsFileStatus');
    const zeg = (tekst, kleur) => { if (status) { status.textContent = tekst; status.style.color = kleur || 'var(--text-3)'; } };

    if (bestand.size > 20 * 1024 * 1024) { zeg('Bestand is groter dan 20 MB', 'var(--red)'); input.value = ''; return; }

    try {
      zeg('Vingerafdruk berekenen…');
      const buffer = await bestand.arrayBuffer();
      const digest = await crypto.subtle.digest('SHA-256', buffer);
      const hex = Array.from(new Uint8Array(digest)).map(b => b.toString(16).padStart(2, '0')).join('');
      document.getElementById('termsHash').value = hex;

      const soort = document.getElementById('termsKind').value;
      const versie = (document.getElementById('termsVersion').value.trim() || 'concept')
        .replace(/[^A-Za-z0-9._-]/g, '-');
      const taal = document.getElementById('termsLang').value;
      const ext = (bestand.name.split('.').pop() || 'pdf').toLowerCase().replace(/[^a-z0-9]/g, '');
      // Het pad bevat de vingerafdruk, dus een ander bestand kan nooit een
      // bestaand adres overschrijven. Dat is precies wat een gepubliceerde
      // versie onbruikbaar zou maken als bewijs.
      const pad = `${soort}/${versie}-${taal}-${hex.slice(0, 12)}.${ext}`;

      zeg('Uploaden…');
      const { error } = await sb.storage.from('voorwaarden')
        .upload(pad, bestand, { contentType: bestand.type || 'application/octet-stream', upsert: false });
      if (error && !/exists/i.test(error.message)) throw error;

      const { data } = sb.storage.from('voorwaarden').getPublicUrl(pad);
      document.getElementById('termsUrl').value = data.publicUrl;
      zeg(`${bestand.name} geüpload`, 'var(--green)');
      toast('Bestand geüpload, adres en vingerafdruk ingevuld', 'success');
    } catch (e) {
      zeg('Uploaden mislukt: ' + e.message, 'var(--red)');
      toast('Uploaden mislukt: ' + e.message, 'error');
    }
    input.value = '';
  }

  async function publishTerms() {
    const versie = document.getElementById('termsVersion').value.trim();
    const soort = document.getElementById('termsKind').value;
    const taal = document.getElementById('termsLang').value;
    const vanaf = document.getElementById('termsFrom').value || new Date().toISOString().slice(0, 10);
    const url = document.getElementById('termsUrl').value.trim();
    const hash = document.getElementById('termsHash').value.trim().toLowerCase();
    const tekst = document.getElementById('termsText').value.trim();
    const nu = document.getElementById('termsCurrent').checked;
    const rollen = Array.from(document.querySelectorAll('.terms-rol'))
      .filter(v => v.checked).map(v => v.value);

    if (!versie) { toast('Vul een versienummer in','error'); return; }
    if (!/^https:\/\//.test(url)) { toast('Het adres moet met https beginnen','error'); return; }
    if (!/^[a-f0-9]{64}$/.test(hash)) { toast('De vingerafdruk moet 64 tekens zijn. Upload het bestand.','error'); return; }
    if (!tekst) { toast('Plak de tekst erbij. Zonder tekst ziet de gebruiker bij het aanvaarden een leeg vak.','error'); return; }
    if (!rollen.length) { toast('Kies minstens één rol die dit document krijgt voorgelegd','error'); return; }

    // Er mag er per soort en taal maar één gelden. Eerst de oude uitzetten,
    // anders weigert de database de nieuwe regel.
    if (nu) {
      await sb.from('terms_documents').update({ is_current: false })
        .eq('kind', soort).eq('language', taal).eq('is_current', true);
    }
    const { error } = await sb.from('terms_documents').insert([{
      kind: soort, version: versie, language: taal, effective_from: vanaf,
      content_url: url, content_sha256: hash, content_text: tekst,
      applies_to_roles: rollen, is_current: nu
    }]);
    if (error) { toast('Publiceren mislukt: ' + error.message, 'error'); return; }
    ['termsVersion','termsUrl','termsHash','termsText'].forEach(id => document.getElementById(id).value = '');
    const st = document.getElementById('termsFileStatus'); if (st) st.textContent = '';
    toast(`Versie ${versie} gepubliceerd`, 'success');
    await renderTermsList();
  }

  async function makeTermsCurrent(id) {
    const { data: doc } = await sb.from('terms_documents').select('kind, language, version').eq('id', id).maybeSingle();
    if (!doc) return;
    await sb.from('terms_documents').update({ is_current: false })
      .eq('kind', doc.kind).eq('language', doc.language).eq('is_current', true);
    const { error } = await sb.from('terms_documents').update({ is_current: true }).eq('id', id);
    if (error) { toast(error.message, 'error'); return; }
    toast(`Versie ${doc.version} geldt nu`, 'success');
    await renderTermsList();
  }

  // ── AANVAARDINGEN ──
  async function renderConsentList(elementId, toonOrg) {
    const el = document.getElementById(elementId);
    if (!el) return;
    el.innerHTML = '<div class="spinner"></div>';
    const { data, error } = await sb.from('terms_acceptance_overview')
      .select('*').order('accepted_at', { ascending: false }).limit(500);
    if (error) { el.innerHTML = `<p style="font-size:13px;color:var(--red)">${esc(error.message)}</p>`; return; }

    const kolommen = [
      { veld: 'accepted_by_name', label: 'Naam',
        tekst: a => a.accepted_by_name || a.accepted_by_email || '',
        cel: a => `<strong>${esc(a.accepted_by_name || a.accepted_by_email)}</strong>
                   <div style="font-size:11px;color:var(--text-3)">${esc(a.accepted_by_email)}</div>` },
      { veld: 'accepted_by_job_title', label: 'Functie' },
    ];
    if (toonOrg) kolommen.push({ veld: 'organisation', label: 'Organisatie' });
    kolommen.push(
      { veld: 'document_kind', label: 'Document',
        tekst: a => SOORT_LABELS[a.document_kind] || a.document_kind },
      { veld: 'document_version', label: 'Versie', mono: true },
      { veld: 'document_language', label: 'Taal', mono: true },
      { veld: 'accepted_at', label: 'Aanvaard op', filter: false,
        tekst: a => new Date(a.accepted_at).toISOString().slice(0, 16).replace('T', ' '),
        cel: a => esc(new Date(a.accepted_at).toLocaleString('nl-NL')) },
      { veld: 'ip_address', label: 'IP-adres', mono: true,
        tekst: a => a.ip_address || '',
        cel: a => a.ip_address ? esc(a.ip_address) : '<span style="color:var(--text-3)">—</span>' },
      { veld: 'certificaat', label: '', filter: false, uitlijn: 'right',
        tekst: () => '',
        cel: a => `<button class="btn btn-default btn-sm" onclick="App.showCert('${a.id}')">
                     <i class="ti ti-certificate"></i> Certificaat</button>` },
    );

    tabelTeken(`consent_${elementId}`, elementId, kolommen, data || [], {
      zoekLabel: 'Zoek op naam, e-mail, versie of IP-adres',
      leeg: 'Nog geen aanvaardingen vastgelegd.',
    });
  }

  async function showCert(id) {
    const { data: a } = await sb.from('terms_acceptance_overview').select('*').eq('id', id).maybeSingle();
    if (!a) { toast('Niet gevonden', 'error'); return; }
    const regel = (label, waarde) => `<tr>
      <td style="padding:5px 12px 5px 0;color:var(--text-3);font-size:12px;white-space:nowrap;vertical-align:top">${label}</td>
      <td style="padding:5px 0;font-size:12px;word-break:break-all">${esc(waarde ?? '')}</td></tr>`;
    document.getElementById('certBody').innerHTML = `
      <div id="certContent">
        <div style="font-size:15px;font-weight:600;margin-bottom:2px">Certificaat van aanvaarding</div>
        <div style="font-size:12px;color:var(--text-3);margin-bottom:14px">${esc(window.__merknaam || 'CRM')}</div>
        <table style="width:100%;border-collapse:collapse">
          ${regel('Organisatie', a.organisation)}
          ${regel('Document', `${SOORT_LABELS[a.document_kind] || a.document_kind} versie ${a.document_version} (${a.document_language})`)}
          ${regel('Geldig vanaf', a.document_effective_from)}
          ${regel('Adres document', a.document_url)}
          ${regel('Vingerafdruk', a.document_sha256)}
          ${regel('Aanvaard door', `${a.accepted_by_name} (${a.accepted_by_email})`)}
          ${regel('Functie', a.accepted_by_job_title)}
          ${regel('Bevoegdheid bevestigd', a.authority_confirmed ? 'ja' : 'nee')}
          ${regel('Wijze', a.acceptance_method)}
          ${regel('Tijdstip', new Date(a.accepted_at).toLocaleString('nl-NL') + ' (' + a.accepted_at + ')')}
          ${regel('IP-adres', a.ip_address || 'niet vastgelegd')}
          ${regel('Land', a.geo_country || 'niet vastgelegd')}
          ${regel('Browser', a.user_agent)}
          ${regel('Kenmerk', a.id)}
        </table>
        <p style="font-size:11px;color:var(--text-3);line-height:1.6;margin-top:14px;border-top:1px solid var(--border);padding-top:10px">
          Dit certificaat is een weergave van een regel uit het aanvaardingsregister van
          ${esc(window.__merknaam || 'het platform')}. Die regel kan niet worden gewijzigd of verwijderd.
          Het certificaat is geen notariële akte en bewijst niet de identiteit van de ondertekenaar;
          het legt vast welke verklaring op welk moment vanaf welk adres is afgelegd, en welk
          document daarbij is getoond. De vingerafdruk maakt controleerbaar of het document op het
          genoemde adres nog hetzelfde is als destijds.
        </p>
      </div>`;
    document.getElementById('certModal').classList.add('open');
  }

  function closeCert() { document.getElementById('certModal').classList.remove('open'); }

  function printCert() {
    document.body.classList.add('bezig-met-afdrukken');
    window.print();
    setTimeout(() => document.body.classList.remove('bezig-met-afdrukken'), 500);
  }

  async function savePlatformSettings() {
    const velden = { updated_by: state.currentUserEmail, updated_at: new Date().toISOString() };
    const clientId = document.getElementById('platformClientId');
    const naam = document.getElementById('platformName');
    const kort = document.getElementById('platformShort');
    const support = document.getElementById('platformSupport');
    // Eén knop op twee pagina's: alleen opslaan wat op het zichtbare scherm staat.
    if (clientId && clientId.offsetParent !== null) velden.stripe_connect_client_id = clientId.value.trim() || null;
    if (naam && naam.offsetParent !== null) velden.platform_name = naam.value.trim() || 'CRM';
    if (kort && kort.offsetParent !== null) velden.platform_short_name = kort.value.trim() || null;
    if (support && support.offsetParent !== null) velden.support_email = support.value.trim() || null;

    const { error } = await sb.from('platform_settings').update(velden).eq('id', true);
    if (error) { toast('Opslaan mislukt: ' + error.message, 'error'); return; }
    toast('Opgeslagen','success');
    if (velden.platform_short_name !== undefined) {
      window.__korteNaam = velden.platform_short_name || velden.platform_name || window.__merknaam;
      if (document.body.classList.contains('platform-modus')) await zetPlatformMerk(true);
    }
    if (velden.platform_name) await toonMerknaamOpnieuw(velden.platform_name);
    if (velden.stripe_connect_client_id !== undefined) await renderPlatformStripe();
  }

  // De merknaam staat op drie plekken in beeld; na het opslaan meteen bijwerken.
  async function toonMerknaamOpnieuw(naam) {
    window.__merknaam = naam;
    const sub = document.getElementById('brandSub');
    if (sub) sub.textContent = naam;
    document.title = state.currentOrg?.name ? `${state.currentOrg.name} \u2014 ${naam}` : naam;
  }

  async function saveOrgFee(orgId) {
    const bps = parseInt(document.getElementById(`fee_bps_${orgId}`).value, 10);
    const fix = parseInt(document.getElementById(`fee_fix_${orgId}`).value, 10);
    if (!Number.isFinite(bps) || bps < 0 || bps > 1000) { toast('Basispunten tussen 0 en 1000','error'); return; }
    if (!Number.isFinite(fix) || fix < 0 || fix > 10000) { toast('Vast bedrag tussen 0 en 10000 cent','error'); return; }
    const { error } = await sb.from('platform_billing').update({
      fee_bps: bps, fee_fixed_cents: fix,
      updated_by: state.currentUserEmail, updated_at: new Date().toISOString()
    }).eq('organisation_id', orgId);
    if (error) { toast('Opslaan mislukt: ' + error.message, 'error'); return; }
    toast(bps === 0 && fix === 0 ? 'Geen inhouding meer' : 'Tarief opgeslagen','success');
  }

  async function createOrganisation() {
    const naam = document.getElementById('newOrgName').value.trim();
    const owner = document.getElementById('newOrgOwner').value.trim().toLowerCase();
    if (naam.length < 2) { toast('Vul een naam in','error'); return; }
    if (!owner.includes('@')) { toast('Vul een geldig e-mailadres in','error'); return; }
    try {
      const res = await platformCall('create_organisation', { name: naam, owner_email: owner });
      const link = `${location.origin}${location.pathname}?invite=${encodeURIComponent(res.invite_token)}`;
      document.getElementById('newOrgName').value = '';
      document.getElementById('newOrgOwner').value = '';
      document.getElementById('newOrgResult').innerHTML = `
        <div class="banner banner-green" style="margin-top:12px">
          <i class="ti ti-check"></i>
          <span>${esc(res.organisation.name)} aangemaakt. Stuur deze link naar ${esc(owner)};
          hij is 14 dagen geldig en werkt één keer.</span>
        </div>
        <div class="invite-link">
          <span style="flex:1" id="inviteLinkText">${esc(link)}</span>
          <button class="btn btn-default btn-sm" onclick="App.copyInviteLink()"><i class="ti ti-copy"></i> Kopieer</button>
        </div>`;
      await renderPlatformOrgs();
    } catch (e) {
      toast(e.message, 'error');
    }
  }

  // ── HELPERS ── (opmaak in lib/format.js)

  // ── MONEY MODEL ──────────────────────────────────────────────────────────
  // Rekenregels staan in lib/money.js. Badges hieronder zijn weergave.

  function statusBadge(s){const m={paid:'badge-green',partial:'badge-teal',unpaid:'badge-amber',flagged:'badge-blue',expired:'badge-red'};return`<span class="badge ${m[s]||'badge-grey'}">${s||'—'}</span>`}

  // The settlement marking replaces the derived status in the table for a
  // write-off, because showing "paid" next to a no-show is actively
  // misleading. A fee adjustment keeps the payment status visible and adds a
  // marker, since the club may still owe the corrected amount.
  function settlementBadge(t) {
    if (isWrittenOff(t)) return `<span class="badge badge-grey" title="No show / written off"><i class="ti ti-receipt-off" style="font-size:10px"></i> Written off</span>`;
    if (t?.settlement === 'fee_adjusted') return `<span class="badge badge-purple" title="Fee adjusted manually"><i class="ti ti-edit" style="font-size:10px"></i> Adjusted</span>`;
    return '';
  }
  function combinedStatusBadge(t) {
    if (isWrittenOff(t)) return settlementBadge(t);
    return statusBadge(t.payment_status) + (t?.settlement === 'fee_adjusted' ? ' ' + settlementBadge(t) : '');
  }
  function stageBadge(s){if(!s||s==='none')return`<span class="badge badge-grey">—</span>`;const l={email_1:'Email 1',reminder_1:'Reminder 1',reminder_2:'Reminder 2',final_warning:'Final'};return`<span class="badge badge-blue">${l[s]||s}</span>`}
  function actionIcon(t){const m={
    email_sent:'<i class="ti ti-mail"></i>',status_changed:'<i class="ti ti-refresh"></i>',
    stripe_link_generated:'<i class="ti ti-bolt"></i>',csv_imported:'<i class="ti ti-upload"></i>',
    note_added:'<i class="ti ti-notes"></i>',balance_flagged:'<i class="ti ti-flag"></i>',
    price_changed:'<i class="ti ti-tag"></i>',entries_changed:'<i class="ti ti-users"></i>',
    payment_received:'<i class="ti ti-coin-euro"></i>',
    fee_adjusted:'<i class="ti ti-adjustments-dollar" style="color:#7c3aed"></i>',
    written_off:'<i class="ti ti-receipt-off" style="color:var(--red)"></i>',
    settlement_reversed:'<i class="ti ti-arrow-back-up"></i>',
    adjustment_conflict:'<i class="ti ti-alert-triangle" style="color:var(--amber)"></i>'
  };return m[t]||'<i class="ti ti-point"></i>'}

  // Only these action types touch what a club owes or has paid. The fee
  // history filters on this set so it stays a financial record rather than a
  // second copy of the general activity feed.
  const FEE_ACTIONS = new Set([
    'price_changed','entries_changed','payment_received','balance_flagged',
    'fee_adjusted','written_off','settlement_reversed','adjustment_conflict'
  ]);
  const FEE_ACTION_LABELS = {
    price_changed:'Fee changed (Sportdata)', entries_changed:'Entries changed (Sportdata)',
    payment_received:'Payment', balance_flagged:'Flagged for review',
    fee_adjusted:'Manual correction', written_off:'Written off',
    settlement_reversed:'Correction undone', adjustment_conflict:'Needs review',
    status_changed:'Status changed', stripe_link_generated:'Stripe link',
    email_sent:'Email sent', csv_imported:'CSV import', note_added:'Note'
  };
  function feeActionLabel(t){ return FEE_ACTION_LABELS[t] || String(t||'').replace(/_/g,' '); }

  async function archiveSingle(teamId) {
    const team=state.teams.find(t=>t.id===teamId); if(!team) return;
    const now=new Date().toISOString();
    await sb.from('teams').update({archived_at:now,updated_at:now}).eq('id',teamId);
    await logActivity(teamId,team.team_name,'status_changed','Team archived','active','archived');
    state.teams=state.teams.filter(t=>t.id!==teamId);
    renderTable(); renderKPIs(); updateBadges();
    await loadArchivedBadge();
    if(currentTeamId===teamId) closePanelDirect();
    toast('Team archived','success');
  }

  async function renderSettingsMailchimp() {
    const el = document.getElementById('brevoStatus');
    if (!el) return;
    el.innerHTML = '<div class="spinner"></div>';
    let st = null;
    try { st = await emailCall('status'); } catch (e) {
      el.innerHTML = `<div class="banner banner-red"><i class="ti ti-alert-circle"></i><span>${esc(e.message)}</span></div>`;
      return;
    }
    // Het afzenderadres wordt op het platform ingesteld, niet hier: het moet
    // daar geverifieerd worden en een organisatie die het zelf zet krijgt een
    // adres dat de mailserver weigert.
    const afzender = st.from_email
      ? `Afzender: <strong>${esc(st.from_name || '')}</strong> &lt;${esc(st.from_email)}&gt;`
      : 'Er is nog geen afzenderadres ingesteld';

    if (!st.platform_ready) {
      el.innerHTML = `<div class="banner banner-red"><i class="ti ti-alert-circle"></i>
        <span>Het platform heeft e-mail nog niet ingesteld. Neem contact op met de beheerder.</span></div>`;
    } else if (!st.configured) {
      el.innerHTML = `<div class="banner banner-amber"><i class="ti ti-alert-triangle"></i>
        <span>${afzender}. Vraag de beheerder van het platform er een in te stellen; zonder afzender kun je niets versturen.</span></div>`;
    } else {
      el.innerHTML = `<div class="banner banner-green"><i class="ti ti-circle-check"></i>
        <span>${afzender}. Vandaag ${st.sent_today} van ${st.daily_limit} e-mails gebruikt.</span></div>`;
    }
  }

  return {
    init,nav,alleToernooien,switchEvent,openEventModal,closeEventModal,saveEvent,
    filterByKpi,renderTable,openPanel,closePanel,closePanelDirect,
    openColFilter,applyColFilter,clearColFilter,clearColumnFilters,previewColFilter,
    openColToggle,toggleColVisibility,resetColVisibility,
    updateTeamField,importPaymentsCSV,importClubDataCSV,importDeelnemersCSV,showImportMenu,
    openAddTeamModal,closeAddTeamModal,saveNewTeam,
    openStripeModal,closeStripeModal,generateStripeLink,
    checkSingleStripeStatus,checkAllStripeStatuses,
    openEmailModal,closeEmailModal,showTemplate,copyTemplate,
    filterActivity,confirmDiffChange,confirmDiffPayment,closeDiffModal,
    confirmClubDiffChange,confirmAllClubDiff,confirmApplyAllTeams,
    confirmMarkExpired,resolveFlag,showActions,closeDropdown,quickStatus,
    openAdjustModal,closeAdjustModal,previewAdjust,saveAdjustment,
    openWriteOffModal,closeWriteOffModal,previewWriteOff,saveWriteOff,
    confirmReverseSettlement,reverseSettlement,
    archiveSelected,archiveSingle,restoreSelected,restoreSingle,
    toggleRow,toggleSelectAll,clearSelection,
    toggleArchiveRow,toggleArchiveSelectAll,clearArchiveSelection,
    deleteSelectedModal,deleteArchivedModal,validateDeleteCount,closeDeleteModal,executeDelete,
    switchArchiveTab,
    exportJSON,toast,addAdmin,removeAdmin,changeAdminRole,renderDashboard,
    copyInviteLink,revokeInvite,connectStripe,disconnectStripe,
    openPlatform: naarPlatform,closePlatform,navPlatform,savePlatformSettings,saveOrgFee,createOrganisation,
    saveOrigins,
    saveMailLimit,publishTerms,makeTermsCurrent,uploadTermsFile,showCert,closeCert,printCert,
    saveAbonnement,saveBrevoKey,clearBrevoKey,termsSoortGewijzigd,
    tabelZoek,tabelSorteer,tabelFilter,tabelFilterToepassen,tabelFilterWissen,tabelWisAlles,tabelRijKlik,
    renderTemplatesPage,switchTplSeq,saveCentralTemplate,resetCentralTemplate,
    renderPricingSettings,savePricingSettings,previewSettingsEbImpact,closeConfirm,
    showPaymentReviewModal,closePayReviewModal,undoAppliedPayment,
    dismissPaymentReminder,applyCsvPaymentAnyway,
    regenerateStripeLink,regenerateStaleLinks,
    openEditEventModal,closeEditEventModal,saveEditedEvent,previewEarlyBirdImpact,
    saveTeamOverride,clearTeamOverride,toggleTeamOverrideEdit,
    sendViaMailchimp,
    renderClubsPage,loadClubsBadge,sortClubs,
    openClubColFilter,applyClubColFilter,clearClubColFilter,clearAllClubFilters,previewClubColFilter,
    openClubColToggle,toggleClubCol,resetClubCols,
    toggleClubRow,toggleClubSelectAll,clearClubSelection,archiveClubsSelected,deleteClubsSelectedModal,
    openToegangModal,closeToegangModal,vraagToegangAan,trekToegangIn
  };
})();

koppelApp(App.init);

// Vaste teksten vertalen en de taalkeuze koppelen, vóór het inloggen: het
// inlogscherm heeft ook een taalkeuze. Bij een wissel krijgt de bovenbalk de
// titel in de nieuwe taal; pagina's luisteren zelf naar 'taalgewijzigd'.
document.addEventListener('DOMContentLoaded', () => { vertaalDom(); koppelTaalkeuze(); });
document.addEventListener('taalgewijzigd', () => {
  const actief = document.querySelector('.nav-item.active[data-page], .nav-item.active[data-ppage]');
  const naam = actief?.dataset.page || actief?.dataset.ppage;
  if (naam) document.getElementById('topbarTitle').textContent = router.titel(naam);
});

document.addEventListener('DOMContentLoaded', () => {
  // Wait for Supabase CDN library to be available before initialising
  if (typeof supabase !== 'undefined') {
    Auth.init();
  } else {
    // CDN not yet loaded — wait for it
    const script = document.querySelector('script[src*="supabase"]');
    if (script) {
      script.addEventListener('load', Auth.init);
      // Het script staat synchroon in de kop: is de bibliotheek er nu nog
      // niet, dan is laden mislukt. Dan het inlogscherm, niet het laadscherm.
      document.body.classList.remove('laden');
      document.getElementById('authScreen')?.classList.add('active');
    } else {
      // Fallback: poll until available
      const interval = setInterval(() => {
        if (typeof supabase !== 'undefined') {
          clearInterval(interval);
          Auth.init();
        }
      }, 50);
    }
  }
});

// ── GLOBALE KOPPELING ──
// De HTML gebruikt inline handlers (onclick="App.x()"). In het originele
// bestand stond deze code direct in een <script>-blok, waar `const App` in de
// globale scope terechtkwam. Na het bundelen zit alles in een functie, dus de
// koppeling moet expliciet. Zie tests/handlers.mjs, die dit bewaakt.
window.App = App;
window.Auth = Auth;
